import type { ChangeSet, ChangeSetStatus, FieldDiff, Intent } from '@stitch/changesets';
import type {
  Account,
  AccountKind,
  BoardAgent,
  BoardQueue,
  CallDetail,
  CallPage,
  CallRecord,
  Directory,
  EmergencyLocation,
  Mailbox,
  VoicemailMessage,
  RecordedCall,
  DirectoryEntry,
  FieldDef,
  FieldOption,
  KindDef,
  OptionList,
  Person,
  Preset,
  RoutingAccount,
  Tenant,
  Wallboard
} from '@stitch/pbx-adapter';
import type { MyQueue, MySwitch, PhoneNumber, SharedPhoneKind } from '@stitch/pbx-adapter';
import { announceChange } from '../lib/changes';

export type {
  MyQueue,
  MySwitch,
  PhoneNumber,
  Account,
  AccountKind,
  BoardAgent,
  BoardQueue,
  CallPage,
  CallRecord,
  Directory,
  DirectoryEntry,
  EmergencyLocation,
  ChangeSet,
  ChangeSetStatus,
  FieldDef,
  FieldDiff,
  FieldOption,
  Intent,
  KindDef,
  OptionList,
  Person,
  Preset,
  RoutingAccount,
  Tenant,
  Wallboard
};

/** A person as the People page shows them: their details and feature set. */
export type PersonRow = Person & { featureSet: string };

/** A feature page's data: the kind's settings (as this PBX supports them) and the tenant's accounts. */
export interface AccountsView {
  tenant: string;
  kind: AccountKind;
  def: KindDef;
  writable: boolean;
  removable: boolean;
  /** Settings that can be changed here (live ones such as log-ins even when the rest can't). */
  writableFields: string[];
  /** Settings lists leave out; load one account to see them. */
  lazyFields: string[];
  notice?: string;
  /** Stitch staff only: which setting keeps the page view-only. */
  staffNote?: string;
  /** Call queues only: how many the company's plan includes (Growth: 2), or null for no limit (3157). */
  queueLimit?: QueueLimit | null;
  accounts: Account[];
}

/** A plan's call queue limit, and the plan with more (3157). */
export interface QueueLimit {
  max: number;
  plan: string | null;
  upgrade: string | null;
}
export type Step = ChangeSet['steps'][number];

/** Change sets as the API returns them; diagnostics only reach staff. */
export type ChangeSetView = Omit<ChangeSet, 'diagnostics'> & { diagnostics?: ChangeSet['diagnostics'] };
export type TenantSummary = Tenant & {
  writable: boolean;
  /** The company's time zone (its own, or Stitch's default). */
  timeZone?: string;
  /** The phone system's: call reports' days and hours are in it (3136). */
  callsTimeZone?: string;
  customer?: { id: string; name: string; status: CustomerStatus };
  /** Archive only (1.13.0, 2717): since when, and how many days Postgres keeps its calls after that. */
  archiveOnly?: { since: string | null; hotDays: number };
};

export interface Health {
  ok: boolean;
  demo?: boolean;
  pbx?: { reachable: boolean; version?: string; major?: number | null; supported?: boolean; code?: string };
  writes?: { tenants: string[]; systemWrites: boolean; readOnly: boolean };
  notices?: string[];
}

/** App calling: what Stitch last saw for an extension at the call gateway. */
export type CallingState = 'off' | 'registering' | 'registered' | 'auth_failing' | 'failing' | 'no-password' | 'no-extension' | 'error';

export type CallingView =
  | { configured: false }
  | {
      configured: true;
      gateway: string;
      /** How many customers have calls in the app on (Stitch Admin → Updates); null: every tenant (tests only). */
      companies: number | null;
      registrar: string;
      transport: string;
      lastSync: { at: string; ok: boolean; ms: number; error?: string } | null;
      /** SIP passwords the phone system doesn't show: Stitch sets its own (sealed), or can't. */
      passwords?: 'stitch' | 'off';
      counts: Record<CallingState, number>;
      people: { domain: string; extension: string; names: string[]; state: CallingState; code?: number; detail?: string; since?: string; bindings?: number }[];
      /** Calls in the app: whether Stitch hears the gateway's call events, and whether calls have sound. */
      /** 'portal': Stitch Admin in its own container, where the portal's container hears them. */
      calls?: { stream: 'connected' | 'connecting' | 'off' | 'portal'; since: string | null; media: 'livekit' | 'none' | 'unknown'; calls: number; error: string | null; livekit: boolean } | null;
      /** The gateway's own version (1.12.12+); "older" when it's from before 1.12.12 and needs updating. */
      version?: { version: string; sourceId: string; startedAt: string } | 'older' | 'unknown' | null;
    };

/** Something keeping features off for customers (Stitch Admin → Overview, 1.12.34). */
export interface Blocker {
  id: string;
  tone: 'crit' | 'warn' | 'neutral';
  title: string;
  why: string;
  /** How many customers (with a tenant, not suspended) it touches. */
  customers: number;
  to: string;
}

/** Stitch Admin → System → Deploy day (1.13.0, Teampace 3122): what a deploy still needs. */
export interface DeployChecklistLine {
  id: string;
  title: string;
  state: 'done' | 'todo' | 'problem';
  said: string;
  /** A Stitch Admin page, or a card on System (/admin/system#archive). */
  to: string;
  counts?: { done: number; of: number };
}
export interface DeployChecklist {
  version: string;
  lines: DeployChecklistLine[];
  done: number;
  of: number;
}

export interface SystemInfo {
  pbx: { reachable: boolean; version?: string; major?: number | null; supported?: boolean; code?: string; adapter: string };
  /** linked: how many customers' tenants Stitch may change (1.12.33); null on a server that predates it. */
  writes: { tenants: string[]; linked?: number | null; systemWrites: boolean; readOnly: boolean };
  /** Features and settings on trial, and where (null: wherever changes are allowed). */
  trial: { features: string[]; tenants: string[] | null };
  notices: string[];
  endpoints: { name: string; method: string; template: string; verified: boolean; verifiedOn?: string; notes: string }[];
  accountTypes: { kind: AccountKind; title: string; vodiaType: string; verified: boolean; verifiedOn?: string; writable: boolean; trial: boolean; notes: string; provisional: string[] }[];
  store: string;
  /** How Stitch sends invitations and resets; null when email isn't set up. */
  mail: { provider: 'smtp' | 'resend' | 'outbox'; from: string } | null;
  startedAt: string;
  /** Commit and date of the running build ("dev" from a checkout). */
  build: string;
  /** The version people see, e.g. 1.0.1. */
  version: string;
  demo: boolean;
  /**
   * Which side of Stitch this container serves (STITCH_SURFACE): both (all), Stitch Admin in its
   * own container, or the portal; the two addresses; and which background work runs here.
   */
  surface?: { name: Surface; adminUrl?: string; portalUrl?: string; jobs: { name: string; here: boolean; how: string }[] };
}

/** Which side of Stitch a container serves: both on one address (all), Stitch Admin, or the portal. */
export type Surface = 'all' | 'admin' | 'portal' | 'webhooks';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.message === 'string' ? body.message : `Request failed (${status})`);
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : 'ERROR';
    this.body = body;
  }
  get conflicts(): FieldDiff[] {
    return (this.body.conflicts as FieldDiff[] | undefined) ?? (this.body.diffs as FieldDiff[] | undefined) ?? [];
  }
}

/** Kept for call sites written before sign-in: who is asking now comes from the session cookie. */
export type Area = 'admin' | 'tenant';

/** Live help: while Stitch support has control of this tab, changes name them too. */
let assisting: string | null = null;
export function setAssisting(sessionId: string | null): void {
  assisting = sessionId;
}

/** Every request names the web app, so the API knows it isn't a page on another site. */
function headersFor(): Record<string, string> {
  return { Accept: 'application/json', 'x-stitch-request': '1', ...(assisting ? { 'x-stitch-assist': assisting } : {}) };
}
const staffOr = (): Area => 'tenant';

/**
 * Reads started while `withSignal` runs a page's load are cancelled with it (a newer load replaced
 * it, or the page went away), so the server and phone system don't do work nobody will see.
 */
let ambientSignal: AbortSignal | undefined;
export function withSignal<T>(signal: AbortSignal, start: () => T): T {
  const before = ambientSignal;
  ambientSignal = signal;
  try {
    return start();
  } finally {
    ambientSignal = before;
  }
}

export async function request<T>(_area: Area, method: string, path: string, body?: unknown, raw?: { data: Blob; type: string }, extra?: Record<string, string>): Promise<T> {
  // Only reads are ever cancelled: a change, once sent, runs to the end.
  const signal = method === 'GET' ? ambientSignal : undefined;
  const headers = { ...headersFor(), ...extra };
  if (raw) headers['Content-Type'] = raw.type;
  else if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(path, { method, headers, ...(signal ? { signal } : {}), ...(raw ? { body: raw.data } : body !== undefined ? { body: JSON.stringify(body) } : {}) });
  } catch {
    if (signal?.aborted) throw new ApiError(0, { code: 'CANCELLED', message: 'Replaced by a newer load.' });
    throw new ApiError(0, { code: 'NETWORK', message: 'Could not reach the Stitch API. Check your connection and try again.' });
  }
  const text = await res.text();
  let json: Record<string, unknown> = {};
  try {
    json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    // Not Stitch's answer (Stitch always answers in JSON): a page from the proxy in front of it.
    json = { code: 'NOT_STITCH', message: notStitch(res.status) };
  }
  // Signed out (the session ended elsewhere, or expired): the app goes back to sign-in.
  // (A TV's pass isn't a sign-in: a TV that's been turned off just says so.)
  if (res.status === 401 && !path.startsWith('/api/auth/') && !path.startsWith('/api/display/')) window.dispatchEvent(new Event('stitch:signed-out'));
  if (!res.ok) throw new ApiError(res.status, json);
  return json as T;
}

/** What to say when the web server in front of Stitch answered instead of Stitch (an error page). */
function notStitch(status: number): string {
  if (status === 502 || status === 503) return `Stitch’s server didn’t answer just then (${status}). Try again in a moment; if it keeps happening, tell Stitch support.`;
  if (status === 504) return 'Stitch’s server took too long to answer (504). Try again; if it keeps happening, tell Stitch support.';
  if (status === 413) return 'That’s too large to send (413).';
  return `Something between you and Stitch answered instead (${status || 'no answer'}). Try again, or tell Stitch support.`;
}

const enc = encodeURIComponent;

// --- signing in -----------------------------------------------------------------------------

export interface Membership {
  customerId: string;
  customerName: string;
  tenant: string;
  role: 'admin' | 'user';
  extension: string | null;
  /** The company's time zone; null: Stitch's default. */
  timeZone?: string | null;
  /** Ringing, the call dock, busy status and history run on live sessions here (1.13.0). */
  liveSessions?: boolean;
  /** The receptionist seat (1.13.0): the Console is theirs. */
  seat?: 'receptionist';
  /** The company is archive only (1.13.0, 2717): Call history, Storage and Billing; nothing on the phone system. */
  archiveOnly?: true;
  /** Billing shows to the company's own people (1.13.0): once Stitch staff turn "Customers see Billing" on. */
  billingVisible?: true;
  /** A custom role of the company's: what more they may see, and for which queues. */
  roleId?: string;
  roleName?: string;
  permissions?: RolePermissions;
}
export interface Me {
  /**
   * timeZone: how times are shown to them (1.12.13). null (or 'device', from before) is each
   * device's own zone (Automatic); 'company' the company's; else a zone they chose.
   * sharedZone: the zone coworkers see for them; zonePinned: one they chose for coworkers.
   * shareLocalTime, teamClock, teamClockStill: their choices (Your settings → Time).
   * staffAccount: Stitch staff in the portal of a split Stitch, where they're a member of their own
   * company like a customer (staff is false there); they get a link to Stitch Admin.
   */
  user: {
    id: string;
    email: string;
    name: string;
    staff: boolean;
    timeZone?: string | null;
    sharedZone?: string | null;
    zonePinned?: string | null;
    shareLocalTime?: boolean;
    teamClock?: boolean;
    teamClockStill?: boolean;
    /** Calls and audio (1.12.18): a Patch rings their desk phone when they're patched in. */
    patchDeskRing?: boolean;
    /** Team texts (1.12.33): the sound, spoken line and reminders for a text to their ring group or queue. Missing from an older server: the defaults. */
    teamTextSound?: TeamTextSoundSettings;
    staffAccount?: boolean;
  };
  memberships: Membership[];
}
/** Your settings → Team texts (1.12.33): kept per person, so they follow you to every device. */
export interface TeamTextSoundSettings {
  /** Its own sound (off: the message sound, as for any message). */
  sound: boolean;
  /** "New text for Billing", said after the sound. */
  speak: boolean;
  /** Again every 30 seconds while it waits for you, up to 5 times. */
  reminders: boolean;
  volume: 'low' | 'medium' | 'high';
}
export interface AuthState extends Partial<Me> {
  signedIn: boolean;
  demo: boolean;
  /** No Stitch staff account yet: the setup link from the server log creates one. */
  setup: boolean;
  /** Stitch can email (invitations, password resets). */
  mail: boolean;
  /** How to reach Stitch support. */
  support?: { email?: string; phone?: string };
  /** The time zone for a company that hasn't chosen one. */
  timeZone?: string;
  /** Which side of Stitch this is (all when Stitch Admin and the portal share an address). */
  surface?: Surface;
  /** The other side's address, for whoever may go there: the portal (from Stitch Admin), Stitch Admin (staff, from the portal). */
  links?: { admin?: string; portal?: string };
}
/** The company admin's home page (see apps/api/src/overview.ts). */
export interface FlowStep {
  kind: string;
  extension: string;
  name: string;
  detail: string;
  via?: string;
}
export interface Overview {
  generatedAt: string;
  timeZone: string;
  open: { open: boolean; text: string; rule: { extension: string; name: string } } | null;
  attention: { id: string; tone: 'warn' | 'crit'; title: string; detail: string; action: { label: string; to: string } }[];
  flow: { number: string; label: string; state: 'business' | 'afterHours' | 'always'; steps: FlowStep[]; other: string | null; nextHoliday: string | null } | null;
  counts: {
    people: number;
    mailboxes: number;
    phones: number | null;
    phonesOnline: number | null;
    numbers: number | null;
    numbersNowhere: number | null;
    queues: string[];
    agents: number;
    admins: number;
    endUsers: number;
    invited: number;
  };
  checklist: { show: boolean; done: number; steps: { id: 'signin' | 'flow' | 'holidays' | 'team' | 'phones'; title: string; detail: string; done: boolean; action?: { label: string; to: string } }[] } | null;
  missing: string[];
}
export interface CompanySettings {
  timeZone: string | null;
  defaultTimeZone: string;
  /** False for a tenant that isn't a Stitch customer yet. */
  editable: boolean;
  /**
   * Voicemail transcripts: whether Stitch has the service, whether this company has them on,
   * whether people may switch their own, and the mailboxes (people's extensions and shared
   * voicemail boxes) they're on for.
   */
  /**
   * Voicemail transcripts: the company's switch; every mailbox (new ones too) except `off`, or only
   * `mailboxes` (1.12.34). Use transcribed() to ask about one mailbox.
   */
  transcripts?: { available: boolean; on: boolean; selfService: boolean; every: boolean; mailboxes: string[]; off: string[] };
}
export interface LinkInfo {
  purpose: 'invite' | 'reset';
  email: string;
  name: string;
  place: string;
  role: 'staff' | 'admin' | 'user' | null;
  existing: boolean;
}
export interface InviteView {
  id: string;
  email: string;
  name: string;
  role: 'staff' | 'admin' | 'user' | null;
  extension: string | null;
  roleId?: string | null;
  /** Messaging only (no phone): an end user with no extension and no role. */
  messagingOnly?: boolean;
  createdAt: string;
  expiresAt: string;
}

/** What a company's own role may do; each for every call queue ('all') or some. */
export type RolePermission = 'calls' | 'recordings' | 'wallboard' | 'reports' | 'agents' | 'billing';
export type RolePermissions = Partial<Record<RolePermission, 'all' | string[]>>;
export interface RoleView {
  id: string;
  name: string;
  permissions: RolePermissions;
  updatedAt: string;
}
export interface MemberView {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  extension: string | null;
  roleId?: string | null;
  /** Messaging only (no phone): an end user with no extension and no role. */
  messagingOnly?: boolean;
  lastSignInAt: string | null;
  disabled: boolean;
}
/**
 * Messaging-only people (end users without an extension, Messages only): how many a company's plan
 * includes and how many are taken, people and open invitations alike. Admins and anyone with an
 * extension don't count (apps/api/src/plan.ts).
 */
export interface MessagingSeats {
  allowance: number;
  /** Following Stitch's default, rather than a number Stitch staff set for this company. */
  isDefault: boolean;
  defaultAllowance: number;
  used: number;
  members: number;
  invited: number;
}
/**
 * A company's request for more messaging-only people: waiting for Stitch, done (the plan was
 * raised: allowanceAfter), or declined (with Stitch's reason).
 */
export interface SeatRequestView {
  id: string;
  more: number;
  note: string;
  status: 'waiting' | 'done' | 'declined';
  askedAt: string;
  askedBy: string;
  allowanceThen: number;
  answeredAt: string | null;
  answeredBy: string | null;
  allowanceAfter: number | null;
  reason: string | null;
}
/** Feature control (1.13.0): one feature for a company, as staff or the company sees it. */
export type FeatureState = 'on' | 'off' | 'blocked';
export type FeatureControl = { type: 'switch'; on: boolean; label: string } | { type: 'button'; label: string } | { type: 'link'; label: string; to: string };
export interface FeatureView {
  id: string;
  name: string;
  what: string;
  state: FeatureState;
  /** Why it isn't on (null when it is). */
  reason: string | null;
  /** More about how it's on ("Up to 10 a month"). */
  detail: string | null;
  owner: 'stitch' | 'admin' | 'person';
  ownerWords: string;
  /** Stitch staff only. */
  control?: FeatureControl | null;
  history?: { at: string; by: string; what: string }[];
  running?: boolean;
}
/** A company's request for a feature (Ask Stitch to turn this on). */
export interface FeatureRequestView {
  id: string;
  feature: string;
  featureName: string;
  status: 'open' | 'done' | 'closed';
  askedByName: string;
  askedAt: string;
  answeredByName: string;
  answeredAt: string | null;
  reason: string;
}
export interface FeatureRequestListing extends FeatureRequestView {
  customerId: string;
  customerName: string;
  domain: string | null;
  /** Open ones: where the feature stands now. */
  now?: { state: FeatureState; reason: string | null; control: FeatureControl | null };
}
/** What's on, for a company: each feature, whether Stitch can be asked, and the request. */
export interface CompanyFeature extends FeatureView {
  askable: boolean;
  request: FeatureRequestView | null;
}

/** In Stitch Admin's list: with its company, and (while waiting) where the company stands. */
export interface SeatRequestListing extends SeatRequestView {
  customer: { id: string; name: string; tenant: string | null };
  seats?: MessagingSeats;
}
export interface InviteResult {
  invite: InviteView;
  /** The link, to share by hand: only when email isn't set up. With email, only the invitee ever sees it. */
  link: string | null;
  emailed: boolean;
}

// --- customers (staff) -------------------------------------------------------------------------

/** Archive only (1.13.0, 2717): the company's service ended; Call history, Storage and Billing stay. */
export type CustomerStatus = 'onboarding' | 'active' | 'suspended' | 'archive_only';

export interface CustomerSummary {
  id: string;
  name: string;
  tenant: string | null;
  status: CustomerStatus;
  contactName: string;
  contactEmail: string;
  createdAt: string;
  liveAt: string | null;
  /** Archive only: since when, and who made it so. */
  archiveOnlyAt?: string | null;
  archiveOnlyBy?: string | null;
}
export type StepId = 'company' | 'tenant' | 'numbers' | 'people' | 'phones' | 'admin' | 'live';
export interface ChecklistStep {
  step: StepId;
  title: string;
  state: 'done' | 'todo' | 'skipped' | 'waiting';
  detail: string;
}
/** A leg of a live session, for Stitch staff (3126). */
export interface StaffLeg {
  key: string;
  kind: 'app' | 'line' | 'desk' | 'outside' | 'coworker' | 'guest' | 'picture';
  /** "Stitch app", "Desk phone", "Outside number"… */
  device: string;
  via: '' | 'webrtc' | 'gateway';
  who: string;
  extension: string;
  number: string;
  state: 'ringing' | 'active' | 'held' | 'left' | 'missed' | 'declined';
  joinedAt: string | null;
  leftAt: string | null;
  cause: string;
  ended: string;
}
export interface StaffSession {
  id: string;
  kind: 'call' | 'patch' | 'meeting';
  origin: string;
  state: 'ringing' | 'live' | 'held' | 'ended';
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endCause: string;
  ended: string;
  seconds: number | null;
  legs: StaffLeg[];
}
export interface StaffSessions {
  customer: { id: string; name: string; tenant: string | null; liveSessions: boolean };
  sessions: StaffSession[];
  more: boolean;
  next: string | null;
}

export interface CustomerDetail {
  customer: CustomerSummary & { notes: string; onboarding: { steps?: Record<string, { at: string; by: string; note?: string }>; skipped?: StepId[] } };
  checklist: ChecklistStep[];
  writable: boolean;
  /** Whether Stitch may create the tenant itself (system writes on), with a suggested domain. */
  tenantCreation?: { on: boolean; suggested?: string };
  /** Registered for texting (10DLC or toll-free approved): Stitch staff switch it on. */
  texting?: CustomerTexting;
}

// --- phones ------------------------------------------------------------------------------------

export interface PhoneTemplate {
  id: string;
  tenant: string | null;
  shared: boolean;
  name: string;
  model: string;
  keys: { key: string; type: string; target?: string; label?: string }[];
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  uses?: number;
}
export interface TemplateUse {
  phone: string;
  templateId: string;
  appliedAt: string;
  current: boolean;
}
export interface MyView {
  tenant: string;
  extension: string;
  name: string;
  featureSet: string;
  phones: Account[];
  queues: MyQueue[] | null;
  switches: MySwitch[] | null;
  canSignIn: boolean;
  /** Do not disturb, when the phone system keeps it on the extension. */
  dnd?: boolean;
  canDnd: boolean;
}

/** A voicemail's transcript, made by Stitch: "pending" while it's being written. */
export interface TranscriptView {
  status: 'pending' | 'done' | 'failed';
  text?: string;
}
export type VoicemailView = VoicemailMessage & { transcript?: TranscriptView };
/** Which voicemail greeting plays: the standard one, Stitch's typed one, a recorded one, or one set on the phone system. */
export type GreetingPlaying = { kind: 'standard' } | { kind: 'typed'; slot: string; text: string } | { kind: 'recorded'; slot: string } | { kind: 'other' };

/** A person's voicemail greeting (My phone, their page, Voicemail boxes; 1.12.33). */
export interface GreetingView {
  tenant: string;
  extension: string;
  name: string;
  /** A voicemail box (no phone). */
  mailbox: boolean;
  playing: GreetingPlaying;
  /** Stitch's typed greeting, when there is one (playing or not). */
  typed: { slot: string; text: string } | null;
  /** Personal greetings that are recordings, by number. */
  recorded: string[];
  /** Where newly typed words go; null when every personal greeting holds a recording. */
  slotForTyped: string | null;
  maxLength: number;
  /** The language their voicemail speaks ("en"), for the preview. */
  lang: string;
  canChange: boolean;
}

/** Type a greeting (saved and played), or choose which plays. */
export type GreetingChange = { use: 'typed'; text?: string } | { use: 'recorded'; slot: string } | { use: 'standard' };

export type MailboxView = Omit<Mailbox, 'messages'> & {
  messages: VoicemailView[];
  tenant: string;
  extension: string;
  canChange: boolean;
  page?: number;
  more?: boolean;
  /**
   * Writing out this mailbox's voicemail (null: no service): the company's switch, this mailbox's,
   * whether the person looking may change it, and whether they may turn the company on first.
   */
  transcripts?: { company: boolean; on: boolean; canChange: boolean; canTurnOnCompany: boolean } | null;
};

/** What Leland answers with: words, a question with choices, or a plan to review. */
/** A plan for a while (applied now, put back at `at`) or later (applied at `at`): 1.12.15. */
export interface PlanWhen {
  kind: 'until' | 'at';
  at: string;
  /** The time in the person's own zone ("3:11 PM", "tomorrow 8:00 AM"). */
  text: string;
  /** What happens then ("Signs you back in to Scheduling and Insurance"). */
  summary: string;
}

/** A change waiting for its time, or done lately (Leland's changes later, or for a while). */
export interface ScheduledChangeView {
  id: string;
  tenant: string;
  userId: string;
  userName: string;
  action: 'apply' | 'undo';
  changeSetId: string;
  title: string;
  summary: string;
  runAt: string;
  status: 'waiting' | 'running' | 'done' | 'cancelled' | 'failed';
  outcome: string;
  createdAt: string;
  doneAt: string | null;
  doneByName: string | null;
}

export type LelandItem =
  | { type: 'text'; text: string }
  | { type: 'question'; question: string; choices: { label: string; detail?: string }[] }
  | { type: 'plan'; changeSet: ChangeSetView; when?: PlanWhen }
  | { type: 'scheduled'; scheduled: ScheduledChangeView[] }
  | { type: 'import'; import: ImportView }
  | { type: 'error'; text: string };

// --- people from a spreadsheet -------------------------------------------------------------------

export type ImportField = 'firstName' | 'lastName' | 'fullName' | 'extension' | 'email' | 'mobile' | 'featureSet';
export interface ImportRowView {
  row: number;
  firstName: string;
  lastName: string;
  extension: string;
  assigned: boolean;
  email: string;
  mobile: string;
  featureSet: string;
  status: 'new' | 'exists' | 'problem';
  note?: string;
  warnings: string[];
}
export interface ImportView {
  id: string;
  fileName: string;
  sheets: { name: string; rows: number }[];
  sheet: string;
  headerRow: number;
  columns: { index: number; letter: string; heading: string }[];
  mapping: Partial<Record<ImportField, number>>;
  options: { firstExtension: string; defaultFeatureSet: string; sendEmails: boolean };
  people: ImportRowView[];
  counts: { new: number; exists: number; problem: number; warnings: number };
  emailable: number;
  parts: { changeSetId: string; people: number; steps: number }[];
  truncated: { rows?: boolean; columns?: boolean; cells?: boolean } | null;
  expiresAt: string;
}
export interface UploadInfo {
  file: string;
  sheets: { name: string; rows: number }[];
  sheet: string;
  headerRow: number;
  rowsBelowHeadings: number;
}
export interface ImportPlanBody {
  sheet?: string;
  headerRow?: number;
  mapping?: Partial<Record<ImportField, number>>;
  firstExtension?: string;
  defaultFeatureSet?: string;
  sendEmails?: boolean;
}

/** A file as base64, for the upload endpoints. */
export async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** What one person is doing right now. */
export interface PersonPresence {
  state: 'idle' | 'ringing' | 'talking';
  dnd: boolean;
  phones: number;
  with?: { name: string; number: string };
  since?: string;
  external?: boolean;
}
export interface PresenceView {
  tenant: string;
  source: 'live' | 'list';
  people: Record<string, PersonPresence>;
  liveError?: string;
}

const json = (b: unknown) => b;

// --- activity and session recording ------------------------------------------------------------

/** One thing someone did in the portal (from the audit log, or a change they made). */
export interface ActivityEntry {
  id: string;
  at: string;
  who: string;
  whoId: string | null;
  action: string;
  detail: Record<string, unknown>;
  ip?: string;
}
export interface ActivityView {
  tenant: string;
  people: { id: string; name: string; email: string }[];
  entries: ActivityEntry[];
  more: boolean;
}
/** Where a session recording is kept: Stitch Block Storage, Stitch's database (testing), or the company's own bucket. */
export type RecordingPlace = 'stitch' | 'database' | 'theirs';
export interface SessionRecordingView {
  id: string;
  userId: string;
  userName: string;
  startedAt: string;
  lastAt: string;
  pages: string[];
  bytes: number;
  expiresAt: string;
  where: RecordingPlace[];
  bucket?: string;
  exportError?: string;
}
export interface OwnStorage {
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  accessKeyId: string;
  hasSecret: boolean;
}
export interface RecordingSettingsView {
  enabled: boolean;
  retentionDays: number;
  /** "admin", "user", or custom role ids. */
  roles: string[];
  export?: OwnStorage;
  /** Where Stitch keeps new recordings. */
  storage: 'stitch' | 'database';
  /** Whether a company's own storage can be added on this server. */
  canExport: boolean;
  maxDays: number;
}

/** What a wallboard TV can play a sound for (see lib/boardSounds). */
export type DisplaySound = 'caller' | 'busy' | 'urgent' | 'noAgents' | 'hungUp' | 'parked' | 'answered' | 'clear' | 'offline' | 'texts';
/** A TV's own choices, set from the Wallboard page. */
export interface DisplayOptions {
  /** Each agent's name and state (off: only how many are ready and logged in). */
  showAgents: boolean;
  /** When numbers turn amber (red at twice). Null: whatever that TV's browser has. */
  limits: { waiting: number; waitSeconds: number } | null;
  sounds: { on: boolean; volume: 'low' | 'medium' | 'high'; events: DisplaySound[] };
}
/** Any part of a TV's choices, to change. */
export type DisplayChoices = Partial<Omit<DisplayOptions, 'sounds'>> & { sounds?: Partial<DisplayOptions['sounds']> };

/** A TV showing a company's wallboard. */
export interface WallboardDisplay {
  id: string;
  name: string;
  queues: 'all' | string[];
  createdByName: string;
  createdAt: string;
  lastSeenAt: string | null;
  options: DisplayOptions;
}

// --- buying numbers, 911 addresses and moving numbers in --------------------------------------------

/** A 911 address: address2 is where in the building (floor, suite). */
export interface E911Address {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}
export interface SavedAddress extends E911Address {
  id: string;
  label: string;
  createdAt: string;
  updatedAt: string;
}
/** A saved address by id, or one typed in (saved for next time with saveAs). */
export type AddressChoice = { addressId: string } | (E911Address & { saveAs?: string });
export interface NumbersPolicy {
  canOrder: boolean;
  monthlyLimit: number;
}
/** Open inventory (1.13.0): numbers nobody uses yet wait in a hidden auto attendant that says the company's name. */
export interface OpenInventory {
  extension: string;
  /** Callers are offered the company's main menu (press 1). */
  pressOne: boolean;
}
export interface CarrierStatus {
  carrier: boolean;
  writes: boolean;
  routed: boolean;
  addsToPhoneSystem: boolean;
  policy: NumbersPolicy;
  /** New numbers stay on the bill at least this many days. */
  minHoldDays: number;
  usedThisMonth: number;
  remaining: number;
  canOrder: boolean;
  states: string[];
  /** New numbers can wait in open inventory (1.13.0). */
  inventory?: boolean;
}
export interface AvailableNumber {
  number: string;
  city: string;
  state: string;
  rateCenter: string;
  tollFree: boolean;
  /** Stitch staff only. */
  price?: { monthly?: number; setup?: number; perMinute?: number };
}
export interface RateCenter {
  rateCenter: string;
  city: string;
  state: string;
}
export interface OrderLine {
  number: string;
  bought: boolean;
  e911: 'pending' | 'set' | 'skipped' | 'failed';
  pbx: 'pending' | 'added' | 'waiting' | 'failed';
  error?: string;
}
export type OrderStatus = 'working' | 'done' | 'partial' | 'failed' | 'needs_staff' | 'porting' | 'cancelled';
export interface PortDetails {
  accountName: string;
  businessName?: string;
  contact: string;
  btn: string;
  accountLast4: string;
  wantedDate: string;
  address: E911Address;
  /** The confirmed move date (firm order commitment), once there is one. */
  focDate?: string | null;
  carrierStatus?: string;
  checkedAt?: string;
}
export interface NumberOrder {
  id: string;
  kind: 'order' | 'port';
  status: OrderStatus;
  lines: OrderLine[];
  destination: string;
  address: (E911Address & { label?: string }) | null;
  port: PortDetails | null;
  requestedByName: string;
  error: string | null;
  /** After the order errored or its answer was lost: what the carrier's own list showed (3125). */
  carrierCheck?: { at: string; outcome: 'nothing' | 'all' | 'some' | 'unknown'; bought: number; of: number; said: string } | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}
export interface NumberE911 {
  number: string;
  destination: string;
  atCarrier: boolean;
  inService: boolean;
  tollFree: boolean;
  address: E911Address | null;
  callerName: { name: string; at: string; by: string } | null;
}
export interface E911View {
  carrier: boolean;
  writes: boolean;
  numbers: NumberE911[];
  addresses: SavedAddress[];
}
export interface PortForm {
  numbers: string[];
  accountName: string;
  businessName?: string;
  firstName: string;
  lastName: string;
  serviceAddress: E911Address;
  btn: string;
  accountNumber: string;
  pin?: string;
  wantedDate: string;
  signature: string;
  bill?: { name: string; dataUrl: string };
  destination: string;
  address: AddressChoice | null;
  authorize: true;
  confirmAddress?: boolean;
}
export type NumberProblem = 'no_e911' | 'not_at_carrier' | 'no_tenant' | 'call_flow' | 'porting';
export interface InventoryRow {
  number: string;
  carrier: { number: string; callFlow: string; hasE911: boolean; notes: string; rateCenter: string; state: string; inService: boolean; account?: { id: string; name: string; callFlow: string | null } } | null;
  tenant: string | null;
  destination: string;
  problems: NumberProblem[];
  /** Bought less than 90 days ago: the carrier keeps it until then. */
  heldUntil: string | null;
}
export interface AdminNumbersView {
  carrier: { name: string; writes: boolean } | null;
  /** The routing new numbers get at the carrier, when it's set. */
  callFlow: string | null;
  /** The phone system's trunk new numbers come in on (PBX_NUMBER_TRUNK), when set. */
  trunk: string | null;
  callFlows: { id: string; name: string; routes: string[] }[];
  endpoints: { name: string; path: string; write: boolean; verified: boolean; notes?: string }[];
  inventory: InventoryRow[];
  inventoryError: string | null;
  orders: (NumberOrder & { tenant: string; company: string; carrierRef: string | null })[];
}

// --- texting check (Stitch staff) --------------------------------------------------------------

export interface TextView {
  id: string;
  tenant: string | null;
  stitchNumber: string;
  otherNumber: string;
  direction: 'in' | 'out';
  kind: 'sms' | 'mms';
  body: string;
  status: 'received' | 'sending' | 'sent' | 'delivered' | 'failed';
  carrierUuid: string | null;
  error: string | null;
  receipts: { at: string; sendStatus: string; status: string; error: string }[];
  mediaCount: number;
  media: { seq: number; contentType: string; bytes: number; stored: 'database' | 'b2' | 'none'; error: string | null }[];
  sentByName: string;
  createdAt: string;
}
export interface TextingView {
  carrier: { name: string; writes: boolean } | null;
  webhooks: { on: boolean; inboundUrl: string | null; receiptUrl: string | null; ips: string[]; seenIps: string[]; trustProxy: boolean };
  publicUrl: boolean;
  storage: 'b2' | 'database';
  numbers: string[];
  checklist: { smsOut: boolean; smsIn: boolean; mmsOut: boolean; mmsIn: boolean; receipts: boolean; secured: boolean; rejectsOthers: boolean };
  texts: TextView[];
  hooks: { id: string; at: string; kind: string; ip: string; accepted: boolean; reason: string; detail: Record<string, unknown> }[];
}

// --- test center (Stitch staff) -----------------------------------------------------------------

export interface TestCaseInfo {
  kind: 'auto' | 'manual';
  id: string;
  area: string;
  title: string;
  expect: string;
  needs?: 'tenant' | 'writes';
  steps?: string[];
  caution?: string;
}
export interface TraceCallView {
  service: string;
  method: string;
  path: string;
  status: number | null;
  ms: number;
  request?: unknown;
  response?: unknown;
  error?: string;
}
export interface TestResultView {
  id: string;
  area: string;
  title: string;
  kind: 'auto';
  expect: string;
  status: 'pass' | 'warn' | 'fail' | 'error' | 'skip';
  ms: number;
  detail: string;
  facts?: Record<string, unknown>;
  error?: { name: string; code?: string; message: string; stack?: string };
  calls?: TraceCallView[];
  moreCalls?: number;
}
export interface HandsOnMark {
  status: 'pass' | 'fail' | 'blocked';
  note: string;
  by: string;
  at: string;
}
export interface TestRunView {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  startedByName: string;
  options: { tenant?: string | null; writes?: boolean; version?: string; build?: string; container?: string };
  results: TestResultView[];
  manual: Record<string, HandsOnMark>;
}
export interface TestCenterView {
  cases: TestCaseInfo[];
  running: string | null;
  runs: Omit<TestRunView, 'results' | 'manual'>[];
  /** writable: inside the write fence; test: named in it (not only by `*`); customer: the Stitch company on it, if any. */
  tenants: { domain: string; name: string; writable: boolean; test: boolean; customer: string | null }[];
  defaultTenant: string | null;
}

// --- messages ---------------------------------------------------------------------------------------

export type ConversationKind = 'channel' | 'dm' | 'text';
export type TextStatus = 'received' | 'sending' | 'sent' | 'delivered' | 'failed';
/** Who a channel is for (1.12.19): everyone in the company, open to anyone to join, or private (by invitation). */
export type ChannelAccess = 'everyone' | 'open' | 'private';

export interface ConversationView {
  id: string;
  kind: ConversationKind;
  /** "front-desk", "Dana Ruiz, Marcus Lee", "(212) 555-0142". */
  title: string;
  topic: string;
  archived: boolean;
  joined: boolean;
  isDefault: boolean;
  people: { id: string; name: string }[];
  stitchNumber: string | null;
  otherNumber: string | null;
  lastSeq: number;
  lastReadSeq: number;
  unread: number;
  /** Of what they haven't read, the messages (and thread replies) that mention them. */
  mentions: number;
  lastMessageAt: string | null;
  /** The newest message, briefly. */
  preview: ConversationPreview | null;
  canManage: boolean;
  /** What they chose to be told about it; null: the default for its kind. */
  notify: NotifyLevel | null;
  notifyDefault: NotifyLevel;
  /** Texts (1.12.30): the name the company gave the other number ("Beth"); the title shows it. */
  contactName: string | null;
  /** Texts: archived by you, with no text from that number since. */
  archivedByMe: boolean;
  /** Texts to a ring group's or queue's number (1.12.32): the team, who has it, what you can do. Missing from an older server: none. */
  team?: TeamTextView | null;
  /** Channels (1.12.19): who it's for; null for direct messages and texts. Missing from an older server: open. */
  access?: ChannelAccess | null;
  /** Channels: they can leave it (not an Everyone channel). */
  canLeave?: boolean;
  /** Channels: they can add people from the company. */
  canAdd?: boolean;
  /** Channels: they can take people out (admins). */
  canRemove?: boolean;
}
/** A conversation's newest message, briefly: "You: see you at 3". author null: the outside number. */
export interface ConversationPreview {
  author: string | null;
  mine: boolean;
  body: string;
  attached: boolean;
  at: string;
}
/** A message that mentions you (the phone app's Activity). */
export interface MentionView {
  id: string;
  conversation: { id: string; kind: ConversationKind; title: string };
  author: string;
  body: string;
  at: string;
  threadRootId: string | null;
  alsoInChannel: boolean;
  read: boolean;
}
/** Unread messages: in total, in direct messages, and mentions of you. */
export interface UnreadParts {
  unread: number;
  dms: number;
  mentions: number;
}
export interface ChannelListing {
  id: string;
  title: string;
  topic: string;
  joined: boolean;
  archived: boolean;
  members: number;
  isDefault: boolean;
  access?: ChannelAccess;
}
export interface MessagesInbox {
  /** phone: false for someone with no extension (messaging only). */
  me: { id: string; name: string; role: 'admin' | 'user'; extension: string | null; phone?: boolean };
  conversations: ConversationView[];
  channels: ChannelListing[];
  unread: number;
  texting: {
    numbers: 'all' | string[];
    registered: boolean;
    /**
     * The company numbers you can start a text from (1.12.30); "mine": it rings your own phone. Each
     * with where it rings, yours first (1.12.31). Since 3147 also the ones that can't text yet, with
     * why (canText false), and the team (ring group or queue extension) whose texts they are.
     */
    from?: { number: string; label: string; mine: boolean; group?: 'mine' | 'team' | 'company'; where?: string; team?: string | null; canText?: boolean; why?: string }[];
    /** Whether people at the company may delete texts. */
    canDelete?: boolean;
  };
  /** The company lets its admins search every archived message (History, 2716): everyone is told. */
  archive?: { adminsSearch: boolean };
}
export interface MessageView {
  id: string;
  conversationId: string;
  seq: number;
  /** What someone wrote, or a note from Stitch in the conversation (a Patch started or ended). */
  kind: 'message' | 'system';
  /** For a note: what it's about ({ type: 'patch.started', patchId }). */
  meta: Record<string, unknown> | null;
  author:
    | { kind: 'person'; id: string | null; name: string }
    | { kind: 'outside'; number: string }
    /** An incoming webhook (1.12.19): its name, and its picture (the webhook's own version, an emoji, or picture n of the message). */
    | { kind: 'app'; id: string | null; name: string; picture: string | null; emoji: string | null; image: number | null };
  body: string;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  /** A deleted text (1.12.30): who deleted it, and until when anyone who sees it can bring it back. */
  deletedText?: { by: string; undoUntil: string | null };
  /** A deleted chat message (1.13.0): who deleted it, and until when they can bring it back (null: no longer). */
  deletedMessage?: { byId: string | null; undoUntil: string | null };
  text: { direction: 'in' | 'out'; kind: 'sms' | 'mms'; status: TextStatus; error: string | null; media: { seq: number; contentType: string; ready: boolean; failed: boolean }[] } | null;
  /** A reply in a thread: the message it answers; alsoInChannel: shown in the conversation too. */
  threadRootId: string | null;
  alsoInChannel: boolean;
  /** A message with replies: how many, and when the latest came. */
  replies: { count: number; lastAt: string | null } | null;
  /** The people it mentions (@Name). */
  mentions: { id: string; name: string }[];
  /** Emoji people put on it, in the order first used, with who. */
  reactions: Reaction[];
  /** Files and pictures sent with it. */
  files: MessageFileView[];
  /** What the first web address in it shows, once Stitch has fetched it (never in texts). */
  preview?: LinkPreview | null;
  /** A webhook's message (1.12.19): its text and cards (Stitch's Markdown), and its pictures. */
  app?: AppMessageView | null;
}

/** A webhook's message as Stitch keeps it: the shapes of apps/api/src/webhooks/card.ts. */
export interface AppMessageView {
  format: 'slack' | 'stitch';
  text: string;
  cards: AppCard[];
  /** By number: shown through Stitch once fetched ('ok'), else as a link. */
  images: { n: number; status: 'pending' | 'ok' | 'failed'; version: string | null; url: string }[];
}
export type AppCardStatus = 'ok' | 'info' | 'warning' | 'critical';
export interface AppCardImage {
  image: number | null;
  url: string;
  alt: string;
}
export type AppCardItem =
  | { t: 'header'; text: string }
  | { t: 'text'; text: string }
  | { t: 'fields'; fields: { name: string; value: string; short: boolean }[] }
  | ({ t: 'image'; small?: boolean; title?: string } & AppCardImage)
  | { t: 'context'; text: string }
  | { t: 'divider' }
  | { t: 'links'; links: { text: string; url: string }[] };
export interface AppCard {
  color: string | null;
  status: AppCardStatus | null;
  pretext: string;
  author: { name: string; url: string | null } | null;
  title: { text: string; url: string | null } | null;
  items: AppCardItem[];
  thumb: AppCardImage | null;
  footer: string;
  time: string | null;
}

/** A webhook in Channel settings → Webhooks (1.12.19). */
export interface WebhookView {
  id: string;
  name: string;
  /** Its address, when Stitch can show it again; null otherwise (it was shown once). */
  url: string | null;
  picture: string | null;
  state: 'active' | 'paused' | 'archived';
  pausedBy: string | null;
  createdAt: string;
  createdBy: string;
  replacedAt: string | null;
  lastUsedAt: string | null;
  today: number;
  total: number;
  lastError: { message: string; at: string } | null;
}
export interface WebhookList {
  webhooks: WebhookView[];
  base: string;
  canShowAgain: boolean;
  archived: boolean;
}
/** Stitch Admin → System → Webhooks. */
export interface WebhookSettingsView {
  url: string;
  source: 'stitch' | 'easypanel' | 'default';
  host: string;
  env: string | null;
  container: { startedAt: string | null; lastMessageAt: string | null; version: string | null; build: string | null } | null;
  thisContainer: string;
}
/**
 * A web address's preview under a message. Stitch fetched the page (the browser never does): its
 * picture comes from Stitch too (`previewImageUrl`).
 */
export interface LinkPreview {
  /** The address in the message, opened in a new tab. */
  url: string;
  kind: 'page' | 'youtube';
  siteName: string;
  title: string;
  description: string;
  /** YouTube: the channel. */
  author: string | null;
  /** The picture's version (part of its address), and whether the site wants it shown large. */
  image: { version: string; large: boolean } | null;
}
/** A file in a message; `image`: a picture that can be shown in the page. */
export interface MessageFileView {
  id: string;
  name: string;
  contentType: string;
  bytes: number;
  image: boolean;
  /**
   * The virus check (1.12.16): 'checking' (held until it's checked), 'ok', 'virus' (removed:
   * `virus` names it) or 'failed' (couldn't be checked: held). Missing from before: 'ok'.
   */
  check?: 'checking' | 'ok' | 'virus' | 'failed' | 'expired';
  virus?: string;
  /** An installer or disk image: downloaded with a warning. */
  program?: boolean;
  /** When it was posted: with the company's file retention (2936), how long it's kept. */
  postedAt?: string;
  /** Removed by file retention (check 'expired'): after how many days. */
  keptDays?: number;
}

/** What Messages takes: the endings, the biggest file, and the part size for big ones (1.12.16). */
export interface FileRules {
  endings: string[];
  maxBytes: number;
  partBytes: number;
  /** The virus check is on: files are held until they're checked. */
  checked: boolean;
}

/** Stitch Admin → System → Virus check. */
export interface VirusCheckView {
  /** 'found': Stitch found the clamav app in its own project by itself, and kept its address. */
  source: 'stitch' | 'found' | 'easypanel' | 'none';
  address: string | null;
  reachable: boolean | null;
  engine: string | null;
  signaturesAt: string | null;
  lastError: string | null;
  lastCheckedAt: string | null;
  pending: number;
  oldestPending: string | null;
  /** Where Stitch looks for it by itself while nothing names it. */
  looksAt: string[];
}
export interface Reaction {
  emoji: string;
  people: { id: string; name: string }[];
}
/** One person's emoji going on (or coming off) a message, as the live stream tells it. */
export interface ReactionChange {
  emoji: string;
  on: boolean;
  person: { id: string; name: string };
}
export type NotifyLevel = 'all' | 'mentions' | 'muted';
/** What someone chose to show as in Messages. */
export type StatusChoice = 'online' | 'idle' | 'dnd' | 'invisible';
export interface CustomStatus {
  emoji: string | null;
  text: string;
  clearAt: string | null;
}
/** How someone shows to the people in their company. */
export interface ChatPresence {
  status: 'online' | 'idle' | 'dnd' | 'offline';
  custom: CustomStatus | null;
  /** On the phone right now (never who with). */
  /** `in: 'patch'`: in a Patch (shown as "In a Patch" where Live sessions are on, 1.13.0). */
  phone: { state: 'talking' | 'ringing'; since: string | null; in?: 'patch' } | null;
}
export interface PresenceList {
  people: Record<string, ChatPresence>;
  me: { choice: StatusChoice; custom: CustomStatus | null };
  /** Each coworker's local time zone, for those who share it (1.12.13); never people at other companies. */
  zones?: Record<string, string>;
}
export interface TextingState {
  canSend: boolean;
  code: string | null;
  reason: string | null;
  optedOut: boolean;
  keyword: string | null;
  changedAt: string | null;
  /** People at the company may delete texts (1.12.30). Missing from an older server: no. */
  canDelete?: boolean;
}
export interface ConversationDetail {
  conversation: ConversationView;
  texting?: TextingState;
}

// --- shared texting (1.12.32) ------------------------------------------------------------------------

export type TeamKind = 'ringGroup' | 'callQueue';

/** A text conversation whose number's texts go to a ring group or queue, as you see it. */
export interface TeamTextView {
  extension: string;
  name: string;
  kind: TeamKind;
  state: 'waiting' | 'open' | 'resolved';
  owner: { id: string; name: string } | null;
  mine: boolean;
  /** Unanswered since ("Waiting 3m"). */
  waitingSince: string | null;
  /** Their latest text hasn't been answered yet (1.12.33: the team text sound's reminders). Missing from an older server: as waitingSince says. */
  owed?: boolean;
  urgent: boolean;
  /** Queues: you're signed in to it (signed out, its texts are read-only for you). */
  signedIn: boolean;
  supervisor: boolean;
  ownerOnly: boolean;
  /** In its open hours now. */
  open: boolean;
  canTake: boolean;
  canHand: boolean;
  canPutBack: boolean;
  canResolve: boolean;
  /** The Stitch AI summary beside it is on for this team. */
  summaries: boolean;
}

/** A ring group's or queue's texting rules. */
export interface TeamRules {
  takesTexts: boolean;
  handout: 'everyone' | 'one';
  limit: number;
  replyMinutes: number;
  supervisorMinutes: number;
  onCall: 'last' | 'skip';
  returningDays: number;
  awayMinutes: number;
  signedOutMinutes: number;
  ownerOnly: boolean;
  /** "team" (its own after-hours rule), "always", or a time of day rule's extension. */
  hours: string;
  place: { on: boolean; firstMinutes: number; updateMinutes: number; text: string };
  afterHours: { on: boolean; text: string };
  opening: { on: boolean; text: string };
  supervisors: string[];
  summaries: boolean;
  urgent: boolean;
}

export interface TeamRulesPage {
  team: { extension: string; name: string; kind: TeamKind };
  rules: TeamRules;
  defaults: TeamRules;
  /** Its people in Stitch. */
  members: { id: string; name: string; extension: string | null }[];
  /** Everyone at the company (to choose supervisors). */
  people: { id: string; name: string; role: string }[];
  timeRules: { extension: string; name: string }[];
  /** Its own after-hours rule on the phone system, if any. */
  /** Its own after-hours rule; `usual`: a queue (which has none) following the company's usual one (3137). */
  teamRule: { extension: string; name: string; usual?: boolean } | null;
  open: boolean;
  /** The numbers whose texts go to it now. */
  numbers: { number: string; label: string }[];
  company: string;
  maxText: number;
}

export type TextRouteKind = 'same' | 'person' | 'ringGroup' | 'callQueue' | 'none';

/** One company number on the Numbers page: where its calls and texts go. */
export interface TextRouteRow {
  number: string;
  label: string;
  calls: { to: string; kind: 'person' | 'ringGroup' | 'callQueue' | 'autoAttendant' | 'other' | 'none'; name: string };
  texts: { set: boolean; kind: TextRouteKind; target: string; name: string; reachKind: string };
  /** Who reads its texts: a person, a team, the admins (nobody in Stitch), or off. */
  reach: 'person' | 'team' | 'admins' | 'off';
  canText: boolean;
  changedBy: string | null;
  changedAt: string | null;
}
export interface TextRoutesPage {
  numbers: TextRouteRow[];
  people: { id: string; extension: string; name: string }[];
  teams: { extension: string; name: string; kind: TeamKind; takesTexts: boolean; people: number }[];
  /** Text-capable numbers whose texts go only to admins. */
  toAdmins: number;
}

export interface TextingSettingsView {
  canDelete: boolean;
  canChange: boolean;
  /** The answer to HELP: the company's own (null: Stitch's), and what goes out. Missing from an older server. */
  help?: { text: string | null; shown: string };
  /** People opted out now (admins only). */
  optedOut?: number | null;
  /** The texts a move to a team sends (1.13.0): the company's own (null: Stitch's), and Stitch's. */
  moveTexts?: { person: string | null; team: string | null; closed: string | null; defaults: { person: string; team: string; closed: string } };
}

export interface OptOutsPage {
  optedOut: { otherNumber: string; keyword: string; viaNumber: string; changedAt: string }[];
  events: { otherNumber: string; stitchNumber: string; optedOut: boolean; keyword: string; at: string }[];
}

/** The summary beside a shared conversation (Stitch AI). */
export interface TextSummary {
  /** What's going on, in a sentence or two. */
  now: string;
  /** What's still open. */
  open: string[];
  /** Earlier conversations with this number, briefly. */
  before: string | null;
  /** Who has had it. */
  people: string[];
  /** A reply to use or ignore. */
  suggestion: string | null;
}
export interface TextSummaryResult {
  /** Off, or not set up: why, in plain words. */
  off: string | null;
  summary: TextSummary | null;
  /** Up to which message it covers, and whether newer ones came since. */
  uptoSeq: number;
  stale: boolean;
  madeAt: string | null;
}
export interface CustomerTexting {
  registered: boolean;
  at: string | null;
  by: string | null;
}

// --- texts on the wallboard, TVs and Reports (1.12.32) -----------------------------------------------

/** A team's texts right now. */
export interface TextNumbers {
  /** Unanswered: in line, or given to someone who hasn't answered yet. */
  waiting: number;
  /** Nobody has them. */
  inLine: number;
  /** The longest anyone has waited for an answer, in seconds (null: nobody is waiting). */
  oldestSeconds: number | null;
  /** Someone has them (not resolved). */
  open: number;
  answeredToday: number;
  avgFirstReplySeconds: number | null;
  resolvedToday: number;
}
export interface TextAgent {
  extension: string;
  name: string;
  openTexts: number;
  /** Stitch open and not on do not disturb (TVs: not sent). */
  here?: boolean;
  signedIn?: boolean;
  onCall: boolean;
  /** On a call, and the team skips people on calls: no new texts until it ends. */
  paused: boolean;
}
export interface TextTeamBoard extends TextNumbers {
  extension: string;
  name: string;
  kind: TeamKind;
  handout: 'everyone' | 'one';
  inHours: boolean;
  replyMinutes: number;
  urgent: number;
  agents: TextAgent[];
}
/** A text waiting for an answer, for the team's supervisors. Never on a TV. */
export interface WaitingText {
  conversationId: string;
  team: string;
  teamName: string;
  number: string;
  name: string | null;
  waitingSeconds: number;
  urgent: boolean;
  owner: { id: string; name: string } | null;
  canOpen: boolean;
  canHand: boolean;
}
export interface TextBoard {
  at: string;
  teams: TextTeamBoard[];
  totals: TextNumbers | null;
  waiting: WaitingText[] | null;
}
/** What a TV gets: the numbers only. */
export interface TvTextBoard {
  teams: TextTeamBoard[];
  totals: TextNumbers | null;
}
export interface TextMetrics {
  conversations: number;
  answered: number;
  avgFirstReplySeconds: number | null;
  maxFirstReplySeconds: number | null;
  withinReply: number;
  withinReplyRate: number | null;
  resolved: number;
  handed: number;
  movedOn: number;
  escalated: number;
  autoTexts: number;
}
export interface TextReport {
  from: string;
  to: string;
  timeZone: string;
  by: 'hour' | 'day';
  teams: { extension: string; name: string; kind: TeamKind; takesTexts: boolean }[];
  team: string | null;
  replyMinutes: number | null;
  totals: TextMetrics;
  previous: { from: string; to: string; label: string; totals: TextMetrics };
  series: { key: string; conversations: number; answered: number; avgFirstReplySeconds: number | null; withinReplyRate: number | null; resolved: number }[];
  byTeam: { extension: string; name: string; kind: TeamKind; metrics: TextMetrics }[];
  people: { id: string; name: string; answered: number; avgFirstReplySeconds: number | null; withinReply: number; resolved: number }[];
  generatedAt: string;
}

/** After an apply or undo: every screen showing that company loads again (lib/changes). */
function changed<T>(tenantOf: (r: T) => string | undefined) {
  return (r: T): T => {
    announceChange({ tenant: tenantOf(r) });
    return r;
  };
}

export const api = {
  // Signing in
  authState: () => request<AuthState>('tenant', 'GET', '/api/auth/state'),
  /**
   * `account`: the Stitch team account or the company account with this email (they're separate,
   * 1.12.20). Only where one address serves both; elsewhere the address decides.
   */
  signIn: (email: string, password: string, account?: 'team' | 'company') =>
    request<Me & { notice?: string }>('tenant', 'POST', '/api/auth/sign-in', { email, password, ...(account ? { account } : {}) }),
  signOut: () => request<{ ok: true }>('tenant', 'POST', '/api/auth/sign-out', json({})),
  demoSignIn: (as: 'staff' | 'admin' | 'user') => request<Me>('tenant', 'POST', '/api/auth/demo', { as }),
  setup: (body: { token: string; email: string; name: string; password: string }) => request<Me>('tenant', 'POST', '/api/auth/setup', body),
  linkInfo: (token: string) => request<LinkInfo>('tenant', 'POST', '/api/auth/link', { token }),
  accept: (body: { token: string; name?: string; password?: string }) => request<Me>('tenant', 'POST', '/api/auth/accept', body),
  forgot: (email: string, account?: 'team' | 'company') => request<{ ok: true; message: string }>('tenant', 'POST', '/api/auth/forgot', { email, ...(account ? { account } : {}) }),
  updateMe: (set: {
    name?: string;
    timeZone?: string | null;
    zonePinned?: string | null;
    shareLocalTime?: boolean;
    teamClock?: boolean;
    teamClockStill?: boolean;
    patchDeskRing?: boolean;
    teamTextSound?: Partial<TeamTextSoundSettings>;
  }) =>
    request<Me>('tenant', 'PATCH', '/api/auth/me', set),
  overview: (d: string) => request<Overview>('tenant', 'GET', `/api/tenants/${enc(d)}/overview`),
  checklist: (d: string, set: { hide?: boolean; done?: 'flow' | 'phones' }) => request<{ ok: true }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/overview/checklist`, set),
  companySettings: (d: string) => request<CompanySettings>('tenant', 'GET', `/api/tenants/${enc(d)}/settings`),
  saveCompanySettings: (d: string, set: { timeZone?: string | null; transcripts?: boolean; transcriptsSelf?: boolean; transcriptsEvery?: boolean }) => request<CompanySettings>('tenant', 'PATCH', `/api/tenants/${enc(d)}/settings`, set),
  /** Voicemail transcripts on or off for mailboxes (company admins). */
  setTranscriptBoxes: (d: string, mailboxes: string[], on: boolean) =>
    request<{ on: boolean; every: boolean; mailboxes: string[]; off: string[] }>('tenant', 'PUT', `/api/tenants/${enc(d)}/voicemail-transcripts`, { mailboxes, on }),
  /** Voicemail transcripts on or off for my own mailbox. */
  setMyTranscripts: (d: string, on: boolean) => request<{ on: boolean; mailboxes: string[] }>('tenant', 'PUT', `/api/tenants/${enc(d)}/me/voicemail-transcripts`, { on }),
  changePassword: (current: string, next: string) => request<{ ok: true }>('tenant', 'POST', '/api/auth/password', { current, next }),

  // Who can sign in to a company
  access: (d: string) =>
    request<{ customer: { id: string; name: string }; members: MemberView[]; invites: InviteView[]; roles: RoleView[]; mail: boolean; seats?: MessagingSeats; seatRequest?: SeatRequestView | null }>(
      'tenant',
      'GET',
      `/api/tenants/${enc(d)}/access`
    ),
  /** Ask Stitch for more messaging-only people (company admins). */
  askForSeats: (d: string, more: number, note: string) =>
    request<{ request: SeatRequestView; emailed: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/access/seat-requests`, { more, note }),
  createRole: (d: string, body: { name: string; permissions: RolePermissions }) => request<{ role: RoleView }>('tenant', 'POST', `/api/tenants/${enc(d)}/access/roles`, body),
  updateRole: (d: string, id: string, body: { name?: string; permissions?: RolePermissions }) => request<{ role: RoleView }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/access/roles/${enc(id)}`, body),
  deleteRole: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/access/roles/${enc(id)}`),
  queueAgent: (d: string, queue: string, agent: string, loggedIn: boolean) =>
    request<{ status: string; message?: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/queues/${enc(queue)}/agents/${enc(agent)}`, { loggedIn }),
  // messagingOnly: an end user with no extension and no role, who uses Messages only (counted against the plan).
  invite: (d: string, body: { email: string; name: string; role: 'admin' | 'user'; extension?: string | null; roleId?: string | null; messagingOnly?: boolean }) =>
    request<InviteResult>('tenant', 'POST', `/api/tenants/${enc(d)}/access/invites`, body),
  revokeInvite: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/access/invites/${enc(id)}`),
  changeMember: (d: string, id: string, set: { role?: 'admin' | 'user'; extension?: string | null; roleId?: string | null; messagingOnly?: boolean }) =>
    request<{ ok: true }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/access/members/${enc(id)}`, set),
  // Messaging-only seats: how many a company has (Stitch staff set it; null follows Stitch's default).
  messagingSeats: (customerId: string) => request<{ seats: MessagingSeats; requests?: SeatRequestView[] }>('admin', 'GET', `/api/admin/customers/${enc(customerId)}/messaging-seats`),
  /** Raising it answers the company's waiting request for more (`request`, done). */
  setMessagingSeats: (customerId: string, allowance: number | null) =>
    request<{ seats: MessagingSeats; request?: SeatRequestView }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/messaging-seats`, { allowance }),
  /** Stitch staff: companies' requests for more messaging-only people (waiting ones, or all). */
  seatRequests: (status: 'waiting' | 'all' = 'waiting') => request<{ requests: SeatRequestListing[]; waiting: number }>('admin', 'GET', `/api/admin/seat-requests?status=${status}`),
  declineSeatRequest: (id: string, reason: string) => request<{ request: SeatRequestView }>('admin', 'POST', `/api/admin/seat-requests/${enc(id)}/decline`, { reason }),
  // Feature control (1.13.0): a customer's Features card, Requests, and the company's What's on.
  customerFeatures: (customerId: string) => request<{ features: FeatureView[]; requests: FeatureRequestView[] }>('admin', 'GET', `/api/admin/customers/${enc(customerId)}/features`),
  setCustomerFeature: (customerId: string, feature: string, on: boolean) =>
    request<{ feature: FeatureView; answered?: FeatureRequestView }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/features/${enc(feature)}`, { on }),
  featureRequests: (status: 'open' | 'all' = 'open') => request<{ requests: FeatureRequestListing[]; open: number }>('admin', 'GET', `/api/admin/feature-requests?status=${status}`),
  turnOnFeatureRequest: (id: string) => request<{ feature: FeatureView; request: FeatureRequestView; answered: boolean }>('admin', 'POST', `/api/admin/feature-requests/${enc(id)}/turn-on`, {}),
  closeFeatureRequest: (id: string, reason: string) => request<{ request: FeatureRequestView }>('admin', 'POST', `/api/admin/feature-requests/${enc(id)}/close`, { reason }),
  companyFeatures: (d: string) => request<{ admin: boolean; admins: string[]; features: CompanyFeature[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/features`),
  askForFeature: (d: string, feature: string) => request<{ request: FeatureRequestView; already: boolean; emailed: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/features/${enc(feature)}/ask`, {}),
  removeMember: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/access/members/${enc(id)}`),
  /** A password reset for one of the company's people (3046): emailed when Stitch can, else a link to pass on. */
  resetLink: (d: string, id: string) =>
    request<{ link: string | null; emailed: boolean; minutes: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/access/members/${enc(id)}/reset-link`, {}),

  // Stitch staff
  staff: () =>
    request<{ staff: { id: string; email: string; name: string; lastSignInAt: string | null; disabled: boolean }[]; invites: InviteView[]; mail: boolean }>('admin', 'GET', '/api/admin/staff'),
  inviteStaff: (email: string, name: string) => request<InviteResult>('admin', 'POST', '/api/admin/staff/invites', { email, name }),
  revokeStaffInvite: (id: string) => request<{ ok: true }>('admin', 'DELETE', `/api/admin/staff/invites/${enc(id)}`),
  setStaffDisabled: (id: string, disabled: boolean) => request<{ ok: true }>('admin', 'PATCH', `/api/admin/staff/${enc(id)}`, { disabled }),

  // Customers and onboarding (staff)
  customers: () =>
    request<{ customers: CustomerSummary[]; unlinked: { domain: string; name: string; extensionCount?: number }[] }>('admin', 'GET', '/api/admin/customers'),
  createCustomer: (body: { name: string; contactName?: string; contactEmail?: string; notes?: string; domain?: string; setUp?: boolean }) =>
    request<{ customer: CustomerSummary; checklist: ChecklistStep[] }>('admin', 'POST', '/api/admin/customers', body),
  importCustomers: (domains: string[], setUp = true) => request<{ added: string[] }>('admin', 'POST', '/api/admin/customers/import', { domains, setUp }),
  customer: (id: string) => request<CustomerDetail>('admin', 'GET', `/api/admin/customers/${enc(id)}`),
  /** Stitch Admin → a customer → Live sessions (3126): calls and Patches with their legs. */
  customerSessions: (id: string, q: { q?: string; from?: string; to?: string; kind?: 'call' | 'patch'; before?: string }) => {
    const p = new URLSearchParams(Object.entries(q).filter((x): x is [string, string] => Boolean(x[1])));
    return request<StaffSessions>('admin', 'GET', `/api/admin/customers/${enc(id)}/sessions${p.size ? `?${p}` : ''}`);
  },
  updateCustomer: (id: string, set: Partial<{ name: string; contactName: string; contactEmail: string; notes: string; domain: string | null; status: CustomerSummary['status'] }>) =>
    request<Omit<CustomerDetail, 'writable'>>('admin', 'PATCH', `/api/admin/customers/${enc(id)}`, set),
  createTenant: (id: string, domain: string) => request<CustomerDetail & { warning?: string }>('admin', 'POST', `/api/admin/customers/${enc(id)}/tenant`, { domain }),
  setStep: (id: string, step: StepId, done: boolean, note?: string) =>
    request<{ checklist: ChecklistStep[] }>('admin', 'POST', `/api/admin/customers/${enc(id)}/steps/${step}`, { done, ...(note ? { note } : {}) }),
  skipStep: (id: string, step: 'numbers' | 'people' | 'phones', skipped: boolean) =>
    request<{ checklist: ChecklistStep[] }>('admin', 'POST', `/api/admin/customers/${enc(id)}/skip`, { step, skipped }),

  // Phones, templates, numbers
  numbers: (d: string) => request<{ numbers: PhoneNumber[]; canChange?: boolean; inventory?: OpenInventory | null }>('tenant', 'GET', `/api/tenants/${enc(d)}/numbers`),
  setInventoryPressOne: (d: string, pressOne: boolean) => request<{ pressOne: boolean; note: string | null }>('tenant', 'PUT', `/api/tenants/${enc(d)}/numbers/inventory`, { pressOne }),
  putInInventory: (d: string, number: string) => request<{ moved: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/${enc(number)}/inventory`),
  carrierStatus: (d: string) => request<CarrierStatus>('tenant', 'GET', `/api/tenants/${enc(d)}/numbers/carrier`),
  searchNumbers: (d: string, q: { tollFree?: boolean; state?: string; areaCode?: string; exchange?: string; rateCenter?: string }) =>
    request<{ numbers: AvailableNumber[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/search`, q),
  rateCenters: (d: string, state: string) => request<{ rateCenters: RateCenter[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/numbers/rate-centers?state=${enc(state)}`),
  orderNumbers: (d: string, body: { numbers: string[]; destination: string; address: AddressChoice | null; confirm: true; confirmAddress?: boolean }) =>
    request<{ order: NumberOrder }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/orders`, body),
  numberOrders: (d: string) => request<{ orders: NumberOrder[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/numbers/orders`),
  numberOrder: (d: string, id: string) => request<{ order: NumberOrder }>('tenant', 'GET', `/api/tenants/${enc(d)}/numbers/orders/${enc(id)}`),
  e911: (d: string) => request<E911View>('tenant', 'GET', `/api/tenants/${enc(d)}/e911`),
  setE911: (d: string, number: string, address: AddressChoice) =>
    request<{ number: string; address: E911Address }>('tenant', 'PUT', `/api/tenants/${enc(d)}/e911/${enc(number)}`, { address, confirm: true }),
  removeE911: (d: string, number: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/e911/${enc(number)}?confirm=yes`),
  setCallerName: (d: string, number: string, name: string) =>
    request<{ number: string; callerName: string }>('tenant', 'PUT', `/api/tenants/${enc(d)}/numbers/${enc(number)}/caller-name`, { name }),
  saveAddress: (d: string, body: E911Address & { label: string }, id?: string) =>
    id
      ? request<{ address: SavedAddress }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/e911/addresses/${enc(id)}`, body)
      : request<{ address: SavedAddress }>('tenant', 'POST', `/api/tenants/${enc(d)}/e911/addresses`, body),
  removeAddress: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/e911/addresses/${enc(id)}`),
  portCheck: (d: string, numbers: string[]) =>
    request<{ portable: string[]; notPortable: { number: string; reason: string }[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/port-check`, { numbers }),
  portIn: (d: string, form: PortForm) => request<{ order: NumberOrder }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/ports`, form),
  cancelPort: (d: string, id: string) => request<{ order: NumberOrder }>('tenant', 'POST', `/api/tenants/${enc(d)}/numbers/ports/${enc(id)}/cancel`, {}),
  testCenter: () => request<TestCenterView>('admin', 'GET', '/api/admin/tests'),
  startTestRun: (body: { tenant: string | null; writes: boolean; confirm?: string }) => request<{ run: TestRunView }>('admin', 'POST', '/api/admin/tests/runs', body),
  testRun: (id: string) => request<{ run: TestRunView }>('admin', 'GET', `/api/admin/tests/runs/${enc(id)}`),
  markHandsOn: (id: string, caseId: string, mark: { status: HandsOnMark['status']; note: string } | null) =>
    mark
      ? request<{ run: TestRunView }>('admin', 'PUT', `/api/admin/tests/runs/${enc(id)}/hands-on/${enc(caseId)}`, mark)
      : request<{ run: TestRunView }>('admin', 'DELETE', `/api/admin/tests/runs/${enc(id)}/hands-on/${enc(caseId)}`),
  testReportUrl: (id: string) => `/api/admin/tests/runs/${enc(id)}/report`,
  // Stitch Admin → Field tests: real-world tests, each result kept, one JSON export.
  fieldTests: () => request<FieldTestsView>('admin', 'GET', '/api/admin/field-tests'),
  saveFieldTest: (id: string, result: FieldTestSave) => request<{ result: FieldTestResultView }>('admin', 'PUT', `/api/admin/field-tests/${enc(id)}/result`, result),
  fieldTestHistory: (id: string) => request<{ results: FieldTestResultView[] }>('admin', 'GET', `/api/admin/field-tests/${enc(id)}/history`),
  fieldTestsExportUrl: () => '/api/admin/field-tests/export',
  /** The JSON export and every screenshot in one zip (Joey, Nov 3 2026). */
  fieldTestsZipUrl: () => '/api/admin/field-tests/export.zip',
  /** A screenshot (shrunk first: lib/screenshots.ts) for a saved result, or waiting for the next save. */
  addFieldTestShot: (id: string, picture: Blob, opts: { result?: string; name?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.result) q.set('result', opts.result);
    if (opts.name) q.set('name', opts.name.slice(0, 200));
    return request<{ shot: FieldTestShotView }>('admin', 'POST', `/api/admin/field-tests/${enc(id)}/shots${q.size ? `?${q}` : ''}`, undefined, { data: picture, type: picture.type });
  },
  /** Takes back a screenshot still waiting for its result. */
  removeFieldTestShot: (shotId: string) => request<{ ok: true }>('admin', 'DELETE', `/api/admin/field-tests/shots/${enc(shotId)}`),
  /** The gateway's field test tools (3131): their state, the test company's lines, the log from `since`. */
  fieldTools: (company?: string, since?: number) => {
    const p = new URLSearchParams();
    if (company) p.set('company', company);
    if (since) p.set('since', String(since));
    return request<FieldToolsView>('admin', 'GET', `/api/admin/field-tests/gateway${p.size ? `?${p}` : ''}`);
  },
  setFieldTools: (body: { on: boolean; minutes?: number; company?: string }) => request<{ tools: FieldToolsView['tools'] }>('admin', 'PUT', '/api/admin/field-tests/gateway', body),
  fieldToolWatch: (person: string, ext: string) => request<{ person: string; ext: string }>('admin', 'POST', '/api/admin/field-tests/gateway/watches', { person, ext }),
  fieldToolUnwatch: (person: string, ext: string) => request<void>('admin', 'DELETE', `/api/admin/field-tests/gateway/watches/${enc(person)}/${enc(ext)}`),
  fieldToolPickup: (body: { person: string; dialog: number; swap?: boolean; target?: string }) =>
    request<{ call: { id: string; state: string }; replaces: string }>('admin', 'POST', '/api/admin/field-tests/gateway/pickup', body),
  /** "Copy summary" (3124): the newest result as text for its Teampace task. */
  fieldTestSummary: (id: string) => request<{ text: string; task: number | null }>('admin', 'GET', `/api/admin/field-tests/${enc(id)}/summary`),
  texting: () => request<TextingView>('admin', 'GET', '/api/admin/texting'),
  sendText: (body: { from: string; to: string; text: string; image?: string }) => request<{ text: TextView }>('admin', 'POST', '/api/admin/texting/send', body),
  textMediaUrl: (id: string, seq: number) => `/api/admin/texting/media/${enc(id)}/${seq}`,
  adminNumbers: (fresh = false) => request<AdminNumbersView>('admin', 'GET', `/api/admin/numbers${fresh ? '?fresh=1' : ''}`),
  adminE911: (number: string) => request<{ number: string; address: E911Address | null }>('admin', 'GET', `/api/admin/numbers/${enc(number)}/e911`),
  retryOrder: (id: string) => request<{ order: NumberOrder }>('admin', 'POST', `/api/admin/numbers/orders/${enc(id)}/retry`, {}),
  markConnected: (id: string, number: string) => request<{ order: NumberOrder }>('admin', 'POST', `/api/admin/numbers/orders/${enc(id)}/connected`, { number }),
  fixCallFlow: (number: string) => request<{ ok: true }>('admin', 'POST', `/api/admin/numbers/${enc(number)}/call-flow`, {}),
  releaseNumber: (number: string, confirm: string) => request<{ removedFromTenant: string | null }>('admin', 'POST', `/api/admin/numbers/${enc(number)}/release`, { confirm }),
  numbersPolicy: (customerId: string) => request<Omit<CarrierStatus, 'states'>>('admin', 'GET', `/api/admin/customers/${enc(customerId)}/numbers-policy`),
  setNumbersPolicy: (customerId: string, policy: NumbersPolicy) => request<{ policy: NumbersPolicy }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/numbers-policy`, policy),
  templates: (d: string) => request<{ templates: PhoneTemplate[]; uses: TemplateUse[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/phone-templates`),
  createTemplate: (d: string, body: { name: string; model: string; keys: string; shared?: boolean }) =>
    request<{ template: PhoneTemplate }>('tenant', 'POST', `/api/tenants/${enc(d)}/phone-templates`, body),
  updateTemplate: (d: string, id: string, set: { name?: string; keys?: string }) => request<{ template: PhoneTemplate }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/phone-templates/${enc(id)}`, set),
  removeTemplate: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/phone-templates/${enc(id)}`),
  planTemplate: (d: string, id: string, phones: string[]) =>
    request<{ changeSet: ChangeSetView | null; upToDate: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/phone-templates/${enc(id)}/plan`, { phones }),

  // A person's own view
  me: (d: string, extension?: string) => request<MyView>('tenant', 'GET', `/api/tenants/${enc(d)}/me${extension ? `?extension=${enc(extension)}` : ''}`),
  myCalls: (d: string, extension?: string) => request<{ calls: CallRecord[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/calls${extension ? `?extension=${enc(extension)}` : ''}`),
  myQueue: (d: string, queue: string, loggedIn: boolean, extension?: string) =>
    request<{ status: string; message?: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/queues/${enc(queue)}`, { loggedIn, ...(extension ? { extension } : {}) }),
  /** Their line right now (do not disturb, on a call, phones signed in), from the phone system's live connection when open. */
  myLine: (d: string, extension?: string) =>
    request<{ line: PersonPresence | null; live: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/line${extension ? `?extension=${enc(extension)}` : ''}`),
  myDnd: (d: string, on: boolean, extension?: string) =>
    request<{ status: string; message?: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/dnd`, { on, ...(extension ? { extension } : {}) }),
  /** A voicemail greeting: your own, or (admins) anyone's at the company, a voicemail box's too. */
  greeting: (d: string, extension?: string) => request<GreetingView>('tenant', 'GET', `/api/tenants/${enc(d)}/me/greeting${extension ? `?extension=${enc(extension)}` : ''}`),
  setGreeting: (d: string, change: GreetingChange, extension?: string) =>
    request<{ status: string; message?: string; changeSetId?: string; greeting?: GreetingView }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/greeting`, {
      ...change,
      ...(extension ? { extension } : {})
    }),
  voicemail: (d: string, extension?: string, page = 1) =>
    request<MailboxView>('tenant', 'GET', `/api/tenants/${enc(d)}/me/voicemail?${new URLSearchParams({ ...(extension ? { extension } : {}), page: String(page) }).toString()}`),
  voicemailAudioUrl: (d: string, id: string, extension?: string) => `/api/tenants/${enc(d)}/me/voicemail/${enc(id)}/audio${extension ? `?extension=${enc(extension)}` : ''}`,
  changeVoicemail: (d: string, id: string, change: { heard?: true; note?: string; moveTo?: string }, extension?: string) =>
    request<{ ok: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/voicemail/${enc(id)}`, { ...change, ...(extension ? { extension } : {}) }),
  deleteVoicemail: (d: string, id: string, extension?: string) =>
    request<{ ok: boolean }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/me/voicemail/${enc(id)}${extension ? `?extension=${enc(extension)}` : ''}`),
  myRecordings: (d: string, extension?: string, page = 1) =>
    request<{ recordings: RecordedCall[]; more?: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/recordings?${new URLSearchParams({ ...(extension ? { extension } : {}), page: String(page) }).toString()}`),
  myRecordingUrl: (d: string, id: string, extension?: string) => `/api/tenants/${enc(d)}/me/recordings/${enc(id)}/audio${extension ? `?extension=${enc(extension)}` : ''}`,
  callDetail: (d: string, record: string) => request<{ call: CallDetail }>(staffOr(), 'GET', `/api/tenants/${enc(d)}/calls/${enc(record)}`),
  callRecordingUrl: (d: string, record: string, recording: string) => `/api/tenants/${enc(d)}/calls/${enc(record)}/recordings/${enc(recording)}`,
  leland: () => request<{ enabled: boolean; model?: string }>('tenant', 'GET', '/api/leland'),
  lelandSend: (d: string, message: string, conversationId?: string, uploadId?: string) =>
    request<{ conversationId: string; items: LelandItem[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/leland`, { message, ...(conversationId ? { conversationId } : {}), ...(uploadId ? { uploadId } : {}) }),
  uploadSheet: async (d: string, file: File) =>
    request<{ id: string; upload: UploadInfo }>('tenant', 'POST', `/api/tenants/${enc(d)}/imports`, { name: file.name, data: await fileToBase64(file) }),
  planImport: (d: string, id: string, body: ImportPlanBody, viaLeland = false) =>
    request<{ import: ImportView }>('tenant', 'POST', `/api/tenants/${enc(d)}/imports/${enc(id)}/plan`, body, undefined, viaLeland ? { 'x-stitch-source': 'leland' } : undefined),
  lelandForget: (d: string, id: string) => request<{ ok: boolean }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/leland/${enc(id)}`),
  activity: (d: string, opts: { user?: string; before?: string } = {}) => {
    const q = new URLSearchParams({ ...(opts.user ? { user: opts.user } : {}), ...(opts.before ? { before: opts.before } : {}) }).toString();
    return request<ActivityView>('tenant', 'GET', `/api/tenants/${enc(d)}/activity${q ? `?${q}` : ''}`);
  },
  recordingMe: (d: string) => request<{ record: boolean; retentionDays?: number }>('tenant', 'GET', `/api/tenants/${enc(d)}/recording/me`),
  recordingBegin: (d: string) => request<{ id: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/recording/sessions`, {}),
  /** The last piece of a recording, sent as the tab closes (the browser finishes it after the page is gone). */
  recordingChunkOnExit: (d: string, id: string, seq: number, gz: Blob, page: string) => {
    void fetch(`/api/tenants/${enc(d)}/recording/sessions/${enc(id)}/chunks/${seq}`, {
      method: 'POST',
      keepalive: true,
      headers: { ...headersFor(), 'Content-Type': 'application/gzip', 'x-stitch-page': page },
      body: gz
    }).catch(() => undefined);
  },
  recordingChunk: (d: string, id: string, seq: number, gz: Blob, page: string) =>
    request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/recording/sessions/${enc(id)}/chunks/${seq}`, undefined, { data: gz, type: 'application/gzip' }, { 'x-stitch-page': page }),
  screenRecordings: (d: string, user?: string, before?: string) =>
    request<{ recordings: SessionRecordingView[]; more?: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/screen-recordings?${new URLSearchParams({ ...(user ? { user } : {}), ...(before ? { before } : {}) }).toString()}`),
  screenRecordingEvents: (d: string, id: string) => request<{ recording: SessionRecordingView; events: unknown[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/screen-recordings/${enc(id)}/events`),
  deleteScreenRecording: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/screen-recordings/${enc(id)}`),
  recordingSettings: (d: string) => request<RecordingSettingsView>('tenant', 'GET', `/api/tenants/${enc(d)}/recording/settings`),
  saveRecordingSettings: (
    d: string,
    set: { enabled?: boolean; retentionDays?: number; roles?: string[]; export?: { endpoint: string; region: string; bucket: string; prefix: string; accessKeyId: string; secret?: string } | null }
  ) => request<RecordingSettingsView>('tenant', 'PATCH', `/api/tenants/${enc(d)}/recording/settings`, set),
  testRecordingBucket: (d: string) => request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/recording/settings/test`, {}),
  /** Apply (or undo) a plan Leland made: recorded as Leland, for the person who pressed Apply. */
  applyForLeland: (id: string, putBack?: { at: string; summary: string }) =>
    request<{ changeSet: ChangeSetView; scheduled?: ScheduledChangeView }>(
      'tenant',
      'POST',
      `/api/change-sets/${enc(id)}/apply`,
      putBack ? { putBackAt: putBack.at, summary: putBack.summary } : undefined,
      undefined,
      { 'x-stitch-source': 'leland' }
    ).then(changed((r) => r.changeSet.tenant)),
  /** Approve a plan for later (1.12.15): applied at `at`, as Leland for the person. */
  scheduleForLeland: (id: string, at: string, summary: string) =>
    request<{ scheduled: ScheduledChangeView }>('tenant', 'POST', `/api/change-sets/${enc(id)}/schedule`, { at, summary }, undefined, { 'x-stitch-source': 'leland' }),
  /** A company's scheduled changes: waiting ones, and those done in the last 7 days. */
  scheduled: (d: string) => request<{ scheduled: ScheduledChangeView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/scheduled`),
  cancelScheduled: (d: string, id: string) => request<{ scheduled: ScheduledChangeView }>('tenant', 'POST', `/api/tenants/${enc(d)}/scheduled/${enc(id)}/cancel`, {}),
  runScheduled: (d: string, id: string) => request<{ scheduled: ScheduledChangeView }>('tenant', 'POST', `/api/tenants/${enc(d)}/scheduled/${enc(id)}/run`, {}),
  undoForLeland: (id: string) =>
    request<{ original: ChangeSetView; undo: ChangeSetView }>('tenant', 'POST', `/api/change-sets/${enc(id)}/undo`, undefined, undefined, { 'x-stitch-source': 'leland' }).then(changed((r) => r.original.tenant)),
  presence: (d: string) => request<PresenceView>('tenant', 'GET', `/api/tenants/${enc(d)}/presence`),
  emergencyLocations: (d: string) => request<{ locations: EmergencyLocation[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/emergency-locations`),

  // Messages
  inbox: (d: string) => request<MessagesInbox>('tenant', 'GET', `/api/tenants/${enc(d)}/messages`),
  messagesUnread: (d: string) => request<UnreadParts>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/unread`),
  /** The newest messages that mention you (the phone app's Activity). */
  mentions: (d: string) => request<{ mentions: MentionView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/mentions`),
  // phone: false: no extension (messaging only), so no phone status and no Call button for them.
  messagePeople: (d: string) =>
    request<{ people: { id: string; name: string; extension: string | null; phone?: boolean; me: boolean }[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/people`),
  conversation: (d: string, id: string) => request<ConversationDetail>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}`),
  conversationMessages: (d: string, id: string, page: { before?: number; after?: number; limit?: number; around?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(page).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString();
    return request<{ messages: MessageView[]; more: boolean; newer?: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages${q ? `?${q}` : ''}`);
  },
  searchMessages: (d: string, q: string, inConversation?: string) =>
    request<{ results: { message: MessageView; conversation: { id: string; kind: ConversationKind; title: string } }[] }>(
      'tenant',
      'GET',
      `/api/tenants/${enc(d)}/messages/search?${new URLSearchParams({ q, ...(inConversation ? { in: inConversation } : {}) }).toString()}`
    ),
  postMessage: (d: string, id: string, body: string, opts: { image?: string; threadRootId?: string; alsoInChannel?: boolean; mentions?: string[]; files?: string[] } = {}) =>
    request<{ message: MessageView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages`, {
      body,
      ...(opts.image ? { image: opts.image } : {}),
      ...(opts.threadRootId ? { threadRootId: opts.threadRootId, alsoInChannel: Boolean(opts.alsoInChannel) } : {}),
      ...(opts.mentions?.length ? { mentions: opts.mentions } : {}),
      ...(opts.files?.length ? { files: opts.files } : {})
    }),
  /** A file for a message, uploaded before it's sent (as it is, up to 25 MB). */
  uploadMessageFile: (d: string, id: string, file: File) =>
    request<{ file: MessageFileView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/files?name=${enc(file.name)}`, undefined, {
      data: file,
      type: 'application/octet-stream'
    }),
  /** What can be shared in Messages, and how big (1.12.16). */
  fileRules: (d: string) => request<FileRules>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/file-rules`),
  /** A big file (over 25 MB): started, sent in parts, then finished (1.12.16). */
  startUpload: (d: string, id: string, name: string, bytes: number) =>
    request<{ upload: { id: string; partBytes: number; parts: number } }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/uploads`, { name, bytes }),
  putUploadPart: (d: string, id: string, upload: string, part: number, data: Blob) =>
    request<{ received: number; parts: number }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/uploads/${enc(upload)}/parts/${part}`, undefined, {
      data,
      type: 'application/octet-stream'
    }),
  finishUpload: (d: string, id: string, upload: string) => request<{ file: MessageFileView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/uploads/${enc(upload)}/finish`, {}),
  cancelUpload: (d: string, id: string, upload: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/uploads/${enc(upload)}`),
  messageFileUrl: (d: string, id: string, file: string, inline = false) => `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/files/${enc(file)}${inline ? '?inline=1' : ''}`,
  editMessage: (d: string, id: string, message: string, body: string, mentions?: string[]) =>
    request<{ message: MessageView }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}`, { body, ...(mentions ? { mentions } : {}) }),
  thread: (d: string, id: string, root: string, before?: number) =>
    request<{ root: MessageView; replies: MessageView[]; more: boolean }>(
      'tenant',
      'GET',
      `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/threads/${enc(root)}${before ? `?before=${before}` : ''}`
    ),
  setNotify: (d: string, id: string, level: NotifyLevel | null) =>
    request<{ notify: NotifyLevel | null; notifyDefault: NotifyLevel }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/notify`, { level }),
  messagePresence: (d: string) => request<PresenceList>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/presence`),
  setStatus: (set: { choice?: StatusChoice; custom?: CustomStatus | null }) => request<{ choice: StatusChoice; custom: CustomStatus | null }>('tenant', 'PUT', '/api/messages/status', set),
  conversationMembers: (d: string, id: string) =>
    request<{ people: { id: string; name: string; extension: string | null }[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/members`),
  react: (d: string, id: string, message: string, emoji: string, on: boolean) =>
    request<{ message: MessageView }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/reactions`, { emoji, on }),
  /** This tab's person is at it (after real input, while it's in front), or has gone to the background. */
  messagesActivity: (visible: boolean, zone?: string) => request<{ ok: true }>('tenant', 'POST', '/api/messages/activity', { visible, ...(zone ? { zone } : {}) }),
  deleteMessage: (d: string, id: string, message: string) => request<{ message: MessageView }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}`),
  // Texting (1.12.30): start a text to a number, name a number, archive, bring back a deleted text, the company's switch.
  startText: (d: string, input: { from: string; to: string; name?: string | null }) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/texts`, input),
  nameContact: (d: string, id: string, name: string | null) => request<ConversationDetail>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/contact`, { name }),
  archiveText: (d: string, id: string, archived: boolean) => request<ConversationDetail>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/archive`, { archived }),
  restoreText: (d: string, id: string, message: string) =>
    request<{ message: MessageView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/restore`, {}),
  // A deleted chat message back, within its day, for whoever deleted it (1.13.0, 2932): the same address.
  restoreMessage: (d: string, id: string, message: string) =>
    request<{ message: MessageView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/restore`, {}),
  messageSettings: (d: string) => request<{ undo: boolean; canChange: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/message-settings`),
  setMessageSettings: (d: string, undo: boolean) => request<{ undo: boolean; canChange: boolean }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/message-settings`, { undo }),
  textingSettings: (d: string) => request<TextingSettingsView>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/texting-settings`),
  setTextingSettings: (d: string, canDelete: boolean) => request<TextingSettingsView>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/texting-settings`, { canDelete }),
  // Shared texting (1.12.32): Texts go to, teams' rules, who has a conversation, HELP and STOP.
  setTextHelp: (d: string, text: string | null) => request<TextingSettingsView>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/texting-settings/help`, { text }),
  optOuts: (d: string) => request<OptOutsPage>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/texting/opt-outs`),
  optOutsCsvUrl: (d: string) => `/api/tenants/${enc(d)}/messages/texting/opt-outs?format=csv`,
  textRoutes: (d: string) => request<TextRoutesPage>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/text-routes`),
  setTextRoute: (d: string, number: string, kind: TextRouteKind, target?: string) =>
    request<TextRoutesPage>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/text-routes/${enc(number)}`, { kind, ...(target ? { target } : {}) }),
  textTeams: (d: string) =>
    request<{ teams: { extension: string; name: string; kind: TeamKind; takesTexts: boolean; handout: 'everyone' | 'one'; numbers: string[]; supervisors: number }[] }>(
      'tenant',
      'GET',
      `/api/tenants/${enc(d)}/messages/text-teams`
    ),
  teamRules: (d: string, team: string) => request<TeamRulesPage>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/text-teams/${enc(team)}`),
  setTeamRules: (d: string, team: string, rules: TeamRules) => request<TeamRulesPage>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/text-teams/${enc(team)}`, rules),
  takeText: (d: string, id: string) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/take`, {}),
  handText: (d: string, id: string, to: string) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/hand`, { to }),
  handTargets: (d: string, id: string) =>
    request<{ people: { id: string; name: string; here: boolean; signedIn: boolean; supervisor: boolean }[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/hand`),
  putBackText: (d: string, id: string) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/put-back`, {}),
  resolveText: (d: string, id: string) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/resolve`, {}),
  textSummary: (d: string, id: string, refresh = false) =>
    request<TextSummaryResult>('tenant', refresh ? 'POST' : 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/summary`, refresh ? {} : undefined),
  /** Take a link preview off your own message (for everyone; it stays off). */
  removePreview: (d: string, id: string, message: string) =>
    request<{ message: MessageView }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/preview`),
  /** A link preview's picture, through Stitch. */
  previewImageUrl: (d: string, id: string, message: string, version: string) =>
    `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/preview/image?v=${enc(version)}`,
  markRead: (d: string, id: string, seq: number) => request<{ lastReadSeq: number; unread: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/read`, { seq }),
  messageMediaUrl: (d: string, id: string, message: string, seq: number) => `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/media/${seq}`,
  createChannel: (d: string, name: string, topic?: string, opts: { access?: ChannelAccess; people?: string[] } = {}) =>
    request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels`, {
      name,
      ...(topic ? { topic } : {}),
      ...(opts.access ? { access: opts.access } : {}),
      ...(opts.people?.length ? { people: opts.people } : {})
    }),
  updateChannel: (d: string, id: string, set: { name?: string; topic?: string; archived?: boolean; access?: ChannelAccess }) =>
    request<ConversationDetail>('tenant', 'PATCH', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}`, set),
  /** People in a channel (1.12.19): anyone in it adds; admins take out. */
  addChannelPeople: (d: string, id: string, people: string[]) => request<ConversationDetail & { added: string[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/members`, { people }),
  removeChannelPerson: (d: string, id: string, user: string) => request<ConversationDetail>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/members/${enc(user)}`),
  /** Channel settings → Webhooks (1.12.19). */
  webhooks: (d: string, id: string) => request<WebhookList>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks`),
  addWebhook: (d: string, id: string, name: string) => request<{ webhook: WebhookView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks`, { name }),
  changeWebhook: (d: string, id: string, hook: string, set: { name?: string; paused?: boolean }) =>
    request<{ webhook: WebhookView }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks/${enc(hook)}`, set),
  replaceWebhook: (d: string, id: string, hook: string) => request<{ webhook: WebhookView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks/${enc(hook)}/replace`, {}),
  deleteWebhook: (d: string, id: string, hook: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks/${enc(hook)}`),
  webhookPicture: (d: string, id: string, hook: string, small: string | null) =>
    small
      ? request<{ webhook: WebhookView }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks/${enc(hook)}/picture`, { small })
      : request<{ webhook: WebhookView }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/webhooks/${enc(hook)}/picture`),
  /** A webhook's picture, and a picture in a webhook's message: through Stitch (never the sender's site). */
  webhookPictureUrl: (d: string, id: string, hook: string, version: string) => `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/webhooks/${enc(hook)}/picture?v=${enc(version)}`,
  appImageUrl: (d: string, id: string, message: string, n: number, version: string) => `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/messages/${enc(message)}/images/${n}?v=${enc(version)}`,
  joinChannel: (d: string, id: string) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/join`, {}),
  leaveChannel: (d: string, id: string) => request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/channels/${enc(id)}/leave`, {}),
  startDm: (d: string, people: string[]) => request<ConversationDetail>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/dms`, { people }),
  setCustomerTexting: (customerId: string, registered: boolean) => request<{ texting: CustomerTexting }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/texting`, { registered }),

  health: () => request<Health>('admin', 'GET', '/api/health'),

  // Stitch Admin
  system: () => request<SystemInfo>('admin', 'GET', '/api/admin/system'),
  /** What keeps features off for customers right now (1.12.34). */
  blockers: () => request<{ customers: number; blockers: Blocker[] }>('admin', 'GET', '/api/admin/blockers'),
  /** What a deploy still needs (1.13.0, 3122). */
  deployChecklist: () => request<DeployChecklist>('admin', 'GET', '/api/admin/deploy-checklist'),
  changelog: () => request<{ version: string; markdown: string }>('admin', 'GET', '/api/admin/changelog'),
  askSupport: (body: { message: string; page?: string; tenant?: string }) =>
    request<{ request: { id: string; createdAt: string }; replyTo: string }>('tenant', 'POST', '/api/support/requests', body),
  supportRequests: (status: 'open' | 'closed' | 'all') => request<{ requests: SupportRequestView[]; open: number }>('admin', 'GET', `/api/admin/support?status=${status}`),
  setSupportStatus: (id: string, status: 'open' | 'closed') => request<{ ok: true }>('admin', 'PATCH', `/api/admin/support/${enc(id)}`, { status }),
  // Live help: the customer's tab
  livePresence: (body: { tabId: string; page: string; visible: boolean; tenant?: string }) => request<LivePresence>('tenant', 'POST', '/api/live/presence', body),
  liveLeave: (tabId: string) => request<{ ok: true }>('tenant', 'POST', '/api/live/leave', { tabId }),
  liveAnswer: (id: string, tabId: string, allow: boolean) => request<{ session: LiveSession }>('tenant', 'POST', `/api/live/sessions/${enc(id)}/answer`, { tabId, allow }),
  liveEvents: (id: string, tabId: string, events: unknown[]) => request<{ ok: true }>('tenant', 'POST', `/api/live/sessions/${enc(id)}/events`, { tabId, events }),
  liveControl: (id: string, tabId: string, allow: boolean) => request<{ session: LiveSession }>('tenant', 'POST', `/api/live/sessions/${enc(id)}/control`, { tabId, allow }),
  liveEnd: (id: string) => request<{ session: LiveSession }>('tenant', 'POST', `/api/live/sessions/${enc(id)}/end`, {}),
  // Live help: Stitch support
  liveOnline: () => request<{ people: OnlinePerson[] }>('admin', 'GET', '/api/admin/live'),
  liveAsk: (userId: string) => request<{ session: LiveSession }>('admin', 'POST', '/api/admin/live/sessions', { userId }),
  liveSession: (id: string) => request<{ session: LiveSession }>('admin', 'GET', `/api/admin/live/sessions/${enc(id)}`),
  liveAct: (id: string, action: LiveAction) => request<{ ok: true }>('admin', 'POST', `/api/admin/live/sessions/${enc(id)}/act`, action),
  liveWantControl: (id: string, want: boolean) => request<{ session: LiveSession }>('admin', 'POST', `/api/admin/live/sessions/${enc(id)}/control`, { want }),
  liveStop: (id: string) => request<{ session: LiveSession }>('admin', 'POST', `/api/admin/live/sessions/${enc(id)}/end`, {}),
  testEmail: () => request<{ ok: true; to: string }>('admin', 'POST', '/api/admin/email-test'),
  calling: () => request<CallingView>('admin', 'GET', '/api/admin/calling'),
  checkCalling: () => request<CallingView>('admin', 'POST', '/api/admin/calling/check'),
  tenants: () => request<{ tenants: TenantSummary[] }>('admin', 'GET', '/api/tenants'),
  allChangeSets: (status?: ChangeSetStatus[], limit = 200) =>
    request<{ changeSets: ChangeSetView[] }>(
      'admin',
      'GET',
      `/api/admin/change-sets?limit=${limit}${status && status.length ? `&status=${status.join(',')}` : ''}`
    ),
  adminChangeSet: (id: string) => request<{ changeSet: ChangeSetView }>('admin', 'GET', `/api/change-sets/${enc(id)}`),

  // Tenant portal
  tenant: (d: string) => request<{ tenant: TenantSummary }>('tenant', 'GET', `/api/tenants/${enc(d)}`),
  people: (d: string) =>
    request<{
      tenant: string;
      writable: boolean;
      fields: string[];
      writableFields: string[];
      /** Shared phones: extensions that are places (1.13.0). Missing from an older server. */
      sharedPhones?: SharedPhoneView[];
      canRemove: boolean;
      people: PersonRow[];
    }>(
      'tenant',
      'GET',
      `/api/tenants/${enc(d)}/people`
    ),
  directory: (d: string) => request<Directory & { tenant: string }>('tenant', 'GET', `/api/tenants/${enc(d)}/directory`),
  accounts: (d: string, kind: AccountKind, area: Area = 'tenant') => request<AccountsView>(area, 'GET', `/api/tenants/${enc(d)}/accounts/${enc(kind)}`),
  /** One account with the settings lists leave out (a recorded greeting, who is logged in). */
  account: (d: string, kind: AccountKind, ext: string) =>
    request<{ account: Account; recordings: Record<string, { seconds: number }> }>('tenant', 'GET', `/api/tenants/${enc(d)}/accounts/${enc(kind)}/${enc(ext)}`),
  routing: (d: string) => request<{ accounts: RoutingAccount[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/routing`),
  /** Saved copies of ring groups, queues and auto attendants (2718): the company's, newest first. */
  savedCopies: (d: string, kind?: SavedCopyKind) =>
    request<{ copies: SavedCopyView[]; where: 'archive' | 'database' }>('tenant', 'GET', `/api/tenants/${enc(d)}/saved-copies${kind ? `?kind=${enc(kind)}` : ''}`),
  saveCopy: (d: string, kind: SavedCopyKind, extension: string, beforeRemoval = false) =>
    request<{ copy: SavedCopyView }>('tenant', 'POST', `/api/tenants/${enc(d)}/saved-copies`, { kind, extension, ...(beforeRemoval ? { beforeRemoval: true } : {}) }),
  /** What the add form starts from to rebuild one (nothing is sent to the phone system). */
  rebuildCopy: (d: string, id: string) =>
    request<{ kind: SavedCopyKind; extension: string; free: boolean; name: string; values: Record<string, string>; left: string[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/saved-copies/${enc(id)}/rebuild`, {}),
  removeCopy: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/saved-copies/${enc(id)}`),
  savedCopyFileUrl: (d: string, id: string) => `/api/tenants/${enc(d)}/saved-copies/${enc(id)}/file`,
  options: (d: string, list: OptionList) => request<{ options: FieldOption[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/options/${enc(list)}`),
  /** Upload a recording already converted to 8 kHz mono 16-bit WAV. */
  uploadRecording: (d: string, wav: Blob) =>
    request<{ ref: string; seconds: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/recordings`, undefined, { data: wav, type: 'audio/wav' }),
  /** A recording to play: fetched with the session's credentials, handed to <audio> as a blob URL. */
  recordingUrl: async (d: string, ref: string): Promise<string> => {
    const res = await fetch(`/api/tenants/${enc(d)}/recordings/${enc(ref)}`, { headers: headersFor() });
    if (!res.ok) throw new ApiError(res.status, { message: 'The recording could not be loaded.' });
    return URL.createObjectURL(await res.blob());
  },
  /** Staff ask as staff: until call history is confirmed on the PBX, only staff may see it. */
  calls: (d: string, page = 1, size = 50) => request<CallPage & { tenant: string; more?: boolean; scoped?: boolean }>(staffOr(), 'GET', `/api/tenants/${enc(d)}/calls?page=${page}&size=${size}`),
  queueReport: (d: string, q: { from: string; to: string; queue?: string }) =>
    request<QueueReport>('tenant', 'GET', `/api/tenants/${enc(d)}/reports/queues?from=${q.from}&to=${q.to}${q.queue ? `&queue=${enc(q.queue)}` : ''}`),
  wallboardDisplays: (d: string) => request<{ displays: WallboardDisplay[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/wallboard/displays`),
  addWallboardDisplay: (d: string, name: string, options?: DisplayChoices) =>
    request<{ display: WallboardDisplay; secret: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/wallboard/displays`, { name, ...(options ? { options } : {}) }),
  /** A TV's name or choices; it picks them up within seconds. */
  changeWallboardDisplay: (d: string, id: string, set: { name?: string; options?: DisplayChoices }) =>
    request<{ display: WallboardDisplay }>('tenant', 'PATCH', `/api/tenants/${enc(d)}/wallboard/displays/${enc(id)}`, set),
  removeWallboardDisplay: (d: string, id: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/wallboard/displays/${enc(id)}`),
  /** What a TV shows, with its own pass (no sign-in). */
  displayBoard: (secret: string) =>
    request<Wallboard & { tenant: string; company: string; screen: string; options?: DisplayOptions; companyZone?: string; team?: { zone: string; count: number; initials: string[] }[] }>('tenant', 'GET', '/api/display/wallboard', undefined, undefined, {
      'x-stitch-display': secret
    }),
  wallboard: (d: string) => request<Wallboard & { tenant: string; canSignIn?: 'all' | string[] }>(staffOr(), 'GET', `/api/tenants/${enc(d)}/wallboard`),
  changeSets: (d: string, before?: string) =>
    request<{ changeSets: ChangeSetView[]; more?: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/change-sets${before ? `?before=${enc(before)}` : ''}`),
  changeSet: (id: string) => request<{ changeSet: ChangeSetView }>('tenant', 'GET', `/api/change-sets/${enc(id)}`),
  plan: (d: string, intents: Intent[], title?: string) =>
    request<{ changeSet: ChangeSetView }>('tenant', 'POST', `/api/tenants/${enc(d)}/change-sets`, { intents, ...(title ? { title } : {}) }),
  apply: (id: string) => request<{ changeSet: ChangeSetView }>('tenant', 'POST', `/api/change-sets/${enc(id)}/apply`).then(changed((r) => r.changeSet.tenant)),
  undo: (id: string) => request<{ original: ChangeSetView; undo: ChangeSetView }>('tenant', 'POST', `/api/change-sets/${enc(id)}/undo`).then(changed((r) => r.original.tenant)),
  discard: (id: string) => request<{ changeSet: ChangeSetView }>('tenant', 'POST', `/api/change-sets/${enc(id)}/discard`),
  // Texts on the wallboard, TVs and Reports (1.12.32). A TV's come with displayBoard, as `texts`.
  wallboardTexts: (d: string) => request<TextBoard>(staffOr(), 'GET', `/api/tenants/${enc(d)}/wallboard/texts`),
  textReport: (d: string, q: { from: string; to: string; team?: string }) =>
    request<TextReport>('tenant', 'GET', `/api/tenants/${enc(d)}/reports/texts?from=${q.from}&to=${q.to}${q.team ? `&team=${enc(q.team)}` : ''}`)
};

// --- queue reports -------------------------------------------------------------------------------

export interface ReportMetrics {
  offered: number;
  answered: number;
  missed: number;
  abandoned: number;
  answerRate: number | null;
  withinTarget: number | null;
  avgWaitSeconds: number | null;
  maxWaitSeconds: number | null;
  avgTalkSeconds: number | null;
  talkSeconds: number;
  avgHoldSeconds: number | null;
}
export interface QueueReport {
  from: string;
  to: string;
  /** The phone system's time zone: the dates and hours are in it (3136). */
  timeZone: string;
  /** The company's, when it differs. */
  companyTimeZone?: string;
  by: 'hour' | 'day';
  queues: { extension: string; name: string }[];
  queue: string | null;
  totals: ReportMetrics;
  previous: { from: string; to: string; label: string; totals: ReportMetrics } | null;
  series: (ReportMetrics & { key: string })[];
  byQueue: { extension: string; name: string; metrics: ReportMetrics; noAgents?: true }[];
  /** Queues with no agents: no figures to show (3140). */
  noAgents?: { extension: string; name: string }[];
  agents:
    | {
        extension: string;
        name: string;
        offered: number;
        answered: number;
        share: number;
        talkSeconds: number;
        avgTalkSeconds: number | null;
        avgHoldSeconds: number | null;
      }[]
    | null;
  partial: boolean;
  generatedAt: string;
}

export type LiveStatus = 'asking' | 'live' | 'ended';
export type LiveControl = 'none' | 'asked' | 'granted';
export type LiveEndReason = 'declined' | 'no-answer' | 'customer' | 'staff' | 'staff-left' | 'lost' | 'time' | 'busy';
export interface LivePresence {
  ask: { id: string; staffName: string } | null;
  session: { id: string; status: LiveStatus; control: LiveControl; staffName: string } | null;
}
export interface LiveSession {
  id: string;
  status: LiveStatus;
  control: LiveControl;
  staff: { id: string; name: string };
  user: { id: string; name: string; email: string };
  tenant: string | null;
  company: string | null;
  page: string;
  createdAt: string;
  acceptedAt: string | null;
  endedAt: string | null;
  reason: LiveEndReason | null;
}
export interface OnlinePerson {
  id: string;
  name: string;
  email: string;
  tabId: string;
  page: string;
  tenant: string | null;
  company: string | null;
  role: 'admin' | 'user' | null;
  device: string;
  visible: boolean;
  since: string;
  seenAt: string;
  session: { id: string; status: LiveStatus; staffName: string } | null;
}
/** What support does in the customer's tab. Positions are in the customer's window; nodes are page copy ids. */
export type LiveAction =
  | { type: 'pointer'; x: number; y: number }
  | { type: 'pointer-out' }
  | { type: 'ping'; x: number; y: number }
  | { type: 'click'; node: number; x: number; y: number }
  | { type: 'key'; key: LiveKey; shift?: boolean }
  | { type: 'text'; text: string }
  | { type: 'scroll'; node: number | null; dx: number; dy: number }
  | { type: 'choose'; node: number; value: string };
export const LIVE_KEYS = ['Enter', 'Escape', 'Tab', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '] as const;
export type LiveKey = (typeof LIVE_KEYS)[number];

export interface SupportRequestView {
  id: string;
  /** Who sent it; null if their account has since been removed. */
  userId: string | null;
  /** They have Stitch open right now, so support can join them. */
  online: boolean;
  createdAt: string;
  userName: string;
  userEmail: string;
  tenant: string | null;
  company: string | null;
  page: string;
  message: string;
  status: 'open' | 'closed';
  closedAt: string | null;
  closedBy: string | null;
}


// --- Patches: quick app-only calls from Messages, on LiveKit --------------------------------------

/** A live Patch as the people in its conversation see it. */
export interface PatchView {
  id: string;
  conversationId: string;
  startedAt: string;
  startedBy: { id: string | null; name: string };
  /** Who's in now, in the order they came in. */
  people: { id: string; name: string }[];
  /**
   * Desk phones on it (1.12.18): rung for it, or on it. text: "Ringing Dana’s desk phone",
   * "Dana · on desk phone", "Leaving a voicemail for Dana", "Connected to Dana’s phone".
   */
  phones?: PatchPhone[];
  /** A 1-on-1's other person who didn't come in (1.13.0): "Dana didn’t pick up", with Leave a voicemail. */
  voicemail?: VoicemailOffer[];
}
export interface VoicemailOffer {
  userId: string;
  name: string;
  why: 'no_answer' | 'declined' | 'busy' | 'dnd';
}
export interface PatchPhone {
  id: string;
  userId: string | null;
  name: string;
  kind: 'ring' | 'direct' | 'callback' | 'voicemail';
  state: 'ringing' | 'active';
  answer: 'desk' | 'voicemail' | 'phone' | null;
  text: string;
}
/** Your settings → Calls and audio: whether a Patch rings your desk phone here (and why not). */
export interface PatchRingSetting {
  on: boolean;
  available: boolean;
  reason?: string;
  code?: string;
}
/** Stitch Admin → System → Patch rings: one company's hidden Patch numbers. */
/** One change set Make Patch rings made: the Patch rings, or the presence room (1.12.21: separate). */
export interface PatchRingChangeSet {
  id: string;
  title: string;
  status: string;
  steps: number;
  notes: string[];
  error: string | null;
}

export interface PatchRingCompany {
  tenant: string;
  name: string;
  available: { ok: boolean; code?: string; reason?: string };
  people: number;
  rings: number;
  ringsThere: number | null;
  missing: number | null;
  clashes: { number: string; extension: string | null; what: string }[];
  presenceRoom: { number: string; there: boolean | null } | null;
  writable: boolean;
  lastCheck: { checkedAt: string; removed: number; error: string | null } | null;
  numbers: { number: string; kind: 'ring' | 'presence'; extension: string | null; name: string }[];
}
export interface PatchRingsStatus {
  companies: PatchRingCompany[];
  lastRun: { at: string; tenants: number; removed: number; error: string | null } | null;
  gateway: { features: string[] | null };
}
/** "Patch in": LiveKit's address and a short-lived token for this person and this conversation's room. */
export interface PatchJoin {
  patch: PatchView;
  started: boolean;
  url: string;
  token: string;
  identity: string;
  /** The server's time, so "· 4:12" isn't thrown off by this device's clock. */
  now: string;
}
export interface PatchHistoryItem {
  id: string;
  conversationId: string;
  startedAt: string;
  endedAt: string;
  seconds: number;
  with: { id: string; name: string }[];
  /** "Patch with Sam · 12 min". */
  text: string;
}
/** Stitch Admin → System: whether Patches are set up and LiveKit answers (never its secret). */
export type PatchesStatus =
  | { configured: false }
  | { configured: true; url: string; apiUrl: string; livekit: { ok: boolean; ms: number; error?: string }; webhooks: { lastAt: string | null; /** Stitch Admin's own container: they go to the portal's. */ inPortal?: boolean }; live: number; messages: boolean; push: boolean };

export const patchApi = {
  /** The live Patches in my conversations, and whether Patches are on at all. */
  live: (d: string) => request<{ enabled: boolean; patches: PatchView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/patches`),
  join: (d: string, conversationId: string) => request<PatchJoin>('tenant', 'POST', `/api/tenants/${enc(d)}/patches/join`, { conversationId }),
  leave: (d: string, id: string) => request<{ ended: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/patches/${enc(id)}/leave`, json({})),
  /** Only the person who started it: it ends for everyone. */
  end: (d: string, id: string) => request<{ ended: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/patches/${enc(id)}/end`, json({})),
  /** A Patch ringing me (from a direct message): no. My other devices stop ringing too. */
  decline: (d: string, id: string) => request<{ declined: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/patches/${enc(id)}/decline`, json({})),
  /** Leave a 1-on-1's other person a voicemail, into the Patch (1.13.0). */
  voicemail: (d: string, id: string, userId: string) => request<{ phone: PatchPhone }>('tenant', 'POST', `/api/tenants/${enc(d)}/patches/${enc(id)}/voicemail`, { userId }),
  history: (d: string) => request<{ patches: PatchHistoryItem[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/patches/history`),
  /** Where leaving goes when the tab closes (sent with keepalive, outside this client). */
  leaveUrl: (d: string, id: string) => `/api/tenants/${enc(d)}/patches/${enc(id)}/leave`,
  status: () => request<PatchesStatus>('admin', 'GET', '/api/admin/patches'),
  /** Your settings → Calls and audio. */
  deskRing: (d: string) => request<PatchRingSetting>('tenant', 'GET', `/api/tenants/${enc(d)}/me/patch-ring`),
  /** Stitch Admin → System → Patch rings. */
  rings: () => request<PatchRingsStatus>('admin', 'GET', '/api/admin/patch-rings'),
  checkRings: () => request<PatchRingsStatus>('admin', 'POST', '/api/admin/patch-rings/check', json({})),
  /** The catch-up: everyone's Patch ring at one company, in one change set. */
  makeRings: (tenant: string) =>
    request<{ made: boolean; message?: string; changeSet?: PatchRingChangeSet; presence?: PatchRingChangeSet; presenceProblem?: string; note?: string }>('admin', 'POST', `/api/admin/patch-rings/${enc(tenant)}`, json({}))
};

// --- meetings and webinars with guests (1.13.0, docs/meetings.md) ----------------------------------

export type MeetingKind = 'meeting' | 'webinar';

export interface MeetingView {
  id: string;
  code: string;
  title: string;
  kind: MeetingKind;
  hostId: string | null;
  hostName: string;
  startsAt: string | null;
  endsAt: string | null;
  lobby: boolean;
  live: boolean;
  liveSince: string | null;
  presenters: string[];
  /** The link, on this portal's address: /m/<code>. */
  path: string;
  /** The person may change or cancel it (its host, or an admin). */
  canChange: boolean;
  cancelled: boolean;
  /** Breakout rooms while they're open: each room, and who's assigned to it (identities). */
  breakouts: MeetingBreakouts | null;
  /** In its host's Google Calendar (2709). */
  calendar: { provider: 'google'; mine: boolean } | null;
  /** Being recorded now (2710). */
  recording: boolean;
}

/** Who a meeting invite reached (Nov 3 2026). */
export interface MeetingInviteResult {
  invited: { id: string; name: string }[];
  skipped: { id: string; name: string; why: string }[];
  /** It's going on now: they were rung. */
  rang: boolean;
}

/** A meeting's recording, on its page (2710). */
export interface MeetingRecordingView {
  id: string;
  state: 'recording' | 'stopping' | 'ready' | 'failed';
  startedAt: string;
  endedAt: string | null;
  seconds: number | null;
  startedByName: string;
  videoBytes: number | null;
  transcript: { status: 'pending' | 'done' | 'failed'; text: string; error: string | null } | null;
  canDelete: boolean;
}

/** Your own calendar (Meetings → Connect Google Calendar). */
export interface CalendarStatus {
  available: boolean;
  google: { email: string; connectedAt: string; problem: string | null } | null;
}

/** Stitch Admin → System → Google Calendar. Never the secret. */
export interface GoogleSettingsView {
  clientId: string;
  secretSet: boolean;
  redirectUri: string | null;
  ready: boolean;
  missing: string[];
  savedBy: string | null;
  savedAt: string | null;
  connected: number;
}

/** Stitch Admin → System → Archive (Storage A0, 1.13.0). */
export interface ArchiveSettingsView {
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  keyIdEnd: string | null;
  secretSet: boolean;
  ready: boolean;
  missing: string[];
  savedBy: string | null;
  savedAt: string | null;
  status: { waiting: number; oldestWaiting: string | null; parked: number; partsLastHour: number; rowsLastHour: number; lastPartAt: string | null };
  lastTest: { at: string; ok: boolean; said: string } | null;
  /** The stitch-archive container as the portal reaches it. */
  service: { url: string | null; savedUrl: string; ok: boolean; version: string | null; queryKey: boolean };
  /** Stitch Block Storage's key, which the archive can reuse (3158). */
  blockStorage: { available: boolean; bucketName: string | null; inUse: boolean; stale: boolean };
}

export interface ArchiveTestStep {
  step: string;
  ok: boolean;
  said: string;
  /** Passed, but something to fix. */
  warn?: boolean;
}

export interface ArchiveSettingsChange {
  endpoint?: string | null;
  region?: string | null;
  bucket?: string | null;
  prefix?: string | null;
  keyId?: string | null;
  secret?: string | null;
  serviceUrl?: string | null;
}

// --- Settings → Storage and retention (Storage A1, 1.13.0): the company's own copy ---------------

export type StorageProviderId = 'aws' | 'wasabi' | 'b2' | 'r2' | 'spaces' | 'linode' | 'minio' | 'other' | 'gcs' | 'azure';

export interface StorageConnectionView {
  id: string;
  provider: StorageProviderId;
  providerName: string;
  endpoint: string;
  region: string;
  bucket: string;
  folder: string;
  keyIdEnd: string;
  /** When the key stops working (an Azure SAS token's end, A3). */
  keyExpiresAt: string | null;
  /** Sealed mode (A3): files encrypted with the company's archive key (its id and fingerprint here). */
  mode: 'readable' | 'sealed';
  seal: { keyId: string; fingerprint: string } | null;
  deletesBy: 'company' | 'stitch';
  state: 'active' | 'removed';
  adopted: boolean;
  createdByName: string | null;
  createdAt: string;
  test: { at: string; ok: boolean; said: string } | null;
  lastWriteAt: string | null;
  lastProbeAt: string | null;
  lastError: { at: string; said: string } | null;
  failures: number;
  nextTryAt: string | null;
  backfillStartedAt: string | null;
  backfillDoneAt: string | null;
}

export interface StorageView {
  connection: StorageConnectionView | null;
  status: { waiting: number; behindSince: string | null; backfill: { total: number; done: number } | null; bytes: number; days: number } | null;
  stitchCopy: { ready: boolean; days: number; bytes: number; firstDay: string | null };
  providers: StorageProviderView[];
  canSeal: boolean;
  /** The company's admins may search every archived message in History (2716). */
  messageSearch: { on: boolean; since: string | null };
  /** Download everything (2716): the latest few, newest first. */
  exports: StorageExport[];
}

export interface StorageProviderView {
  id: StorageProviderId;
  name: string;
  regionHint: string;
  /** The address, with {region} where the region (Azure: the storage account) goes. */
  endpointTemplate: string | null;
  fixedRegion: string | null;
  /** Empty: no key ID (Azure's SAS token is the whole key). */
  keyIdLabel: string;
  secretLabel: string;
  regionLabel: string;
  bucketLabel: string;
  endpointLabel: string;
}

export interface StorageExport {
  id: string;
  status: 'waiting' | 'ready' | 'failed' | 'expired';
  files: number;
  bytes: number;
  requestedByName: string;
  createdAt: string;
  readyAt: string | null;
  expiresAt: string | null;
  downloads: number;
  error: string | null;
}

export interface StorageDraft {
  provider: StorageProviderId;
  endpoint: string;
  region: string;
  bucket: string;
  folder: string;
  keyId: string;
  secret: string;
  deletesBy: 'company' | 'stitch';
  /** Sealed mode (A3), with the key bundle the admin saved (or already had). */
  mode?: 'readable' | 'sealed';
  keyBundle?: StorageKeyBundle;
}

/** The file a company keeps to open its sealed archive (A3). */
export interface StorageKeyBundle {
  format: 'stitch-archive-key';
  version: 1;
  keyId: string;
  key: string;
  fingerprint?: string;
  company?: { id: string; name?: string };
  [more: string]: unknown;
}

export interface StorageTest {
  ok: boolean;
  adopted: boolean;
  /** `warn`: passed, with something to fix (shown in amber). */
  steps: { step: string; ok: boolean; said: string; warn?: boolean }[];
}

export const storageApi = {
  view: (d: string) => request<StorageView>('tenant', 'GET', `/api/tenants/${enc(d)}/storage`),
  policy: (d: string, body: { provider: StorageProviderId; bucket: string; folder: string; deletesBy: 'company' | 'stitch' }) =>
    request<{ steps: string[]; policy: string | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/policy`, body),
  test: (d: string, body: StorageDraft) => request<StorageTest>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/test`, body),
  connect: (d: string, body: StorageDraft & { backfill: boolean }) => request<{ view: StorageView; test: StorageTest; queued: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage`, body),
  testSaved: (d: string) => request<{ test: StorageTest; view: StorageView }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/test-saved`, {}),
  setDeletesBy: (d: string, deletesBy: 'company' | 'stitch') => request<StorageView>('tenant', 'PUT', `/api/tenants/${enc(d)}/storage`, { deletesBy }),
  backfill: (d: string) => request<{ queued: number; view: StorageView }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/backfill`, {}),
  remove: (d: string) => request<StorageView>('tenant', 'DELETE', `/api/tenants/${enc(d)}/storage`),
  setMessageSearch: (d: string, on: boolean) => request<StorageView>('tenant', 'PUT', `/api/tenants/${enc(d)}/storage/message-search`, { on }),
  /** Sealed mode (A3): a new archive key, as the key bundle to download and keep. */
  sealKey: (d: string) => request<{ bundle: StorageKeyBundle; fileName: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/seal-key`, {}),
  /** Download everything: ask for the whole archive (emailed when it's ready). */
  requestExport: (d: string) => request<{ view: StorageView; created: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/exports`, {}),
  exportUrl: (d: string, id: string) => `/api/tenants/${enc(d)}/storage/exports/${enc(id)}/download`
};

// --- Retention (Storage A2, 2717): counts (part 1) and deletion runs (part 2) ----------------------

export type RetentionKind = 'calls' | 'recordings' | 'voicemail' | 'messages' | 'transcripts' | 'patches' | 'meetings';
export type RetentionOutcome = 'nothing' | 'to_confirm' | 'confirmed' | 'held';

export interface RetentionRunView {
  id: string;
  at: string;
  manual: boolean;
  days: number;
  /** Items from before this (company-local) day are past the policy. */
  cutoff: string;
  outcome: RetentionOutcome;
  items: number;
  bytes: number;
  archivedDays: number;
  oldestDay: string | null;
  /** Narrower holds (a conversation, a person) keeping part of what's counted. */
  holds: number;
}

/** A confirmed count carried out: what went from Stitch's copy ('done'), or why nothing did. */
export interface RetentionDeletionView {
  runId: string;
  state: 'done' | 'skipped' | 'failed';
  note: string;
  at: string;
  days: number;
  cutoff: string;
  items: number;
  bytes: number;
}

export interface RetentionChoice {
  days: number;
  label: string;
  /** Longer than included: billed for what's older than 24 months. */
  extra: boolean;
}

export interface RetentionView {
  defaultDays: number;
  includedDays: number;
  extraCentsPer100Gb: number;
  choices: RetentionChoice[];
  holds: { company: boolean; narrow: number };
  deletionsOn: boolean;
  kinds: {
    kind: RetentionKind;
    label: string;
    noun: string;
    days: number;
    chosen: number | null;
    chosenBy: { name: string; at: string } | null;
    confirmed: { days: number; byName: string; at: string } | null;
    latest: RetentionRunView | null;
    /** The latest count is confirmed and waits to be carried out (with what it waits for, if anything). */
    pending: { note: string } | null;
    lastDeletion: RetentionDeletionView | null;
  }[];
}

/** Before confirming a count: what it would delete from Stitch's copy, by month and day (3123). */
export interface RetentionPreviewView {
  runId: string;
  kind: RetentionKind;
  label: string;
  noun: string;
  days: number;
  cutoff: string;
  outcome: RetentionOutcome;
  countedAt: string;
  counted: { items: number; bytes: number };
  now: { items: number; bytes: number; days: number };
  holds: number;
  months: { month: string; items: number; bytes: number; days: { day: string; items: number; bytes: number }[] }[];
}

export interface RetentionAdminView {
  defaultDays: number;
  includedDays: number;
  choices: RetentionChoice[];
  changed: { at: string; byName: string } | null;
  deletionsOn: boolean;
  deletionsChanged: { at: string; byName: string } | null;
  companiesKeepingLonger: number;
  /**
   * Hot data (2717): Postgres keeps `days` of calls (`archiveOnlyDays` after a company became archive
   * only), older ones from the archive. Off by default. `companies`: whose older calls come from the
   * archive now; `latestFrom`: the latest day any of them starts from.
   */
  hotData: { on: boolean; changed: { at: string; byName: string } | null; days: number; archiveOnlyDays: number; companies: number; latestFrom: string | null };
}

export const retentionApi = {
  view: (d: string) => request<RetentionView>('tenant', 'GET', `/api/tenants/${enc(d)}/storage/retention`),
  set: (d: string, kind: RetentionKind, days: number | null) => request<RetentionView>('tenant', 'PUT', `/api/tenants/${enc(d)}/storage/retention`, { kind, days }),
  count: (d: string, kind?: RetentionKind) => request<RetentionView>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/retention/count`, kind ? { kind } : {}),
  confirm: (d: string, runId: string) => request<RetentionView>('tenant', 'POST', `/api/tenants/${enc(d)}/storage/retention/confirm`, { runId }),
  /** What a count would delete, month by month and day by day (3123, proposal 26). */
  preview: (d: string, runId: string) => request<RetentionPreviewView>('tenant', 'GET', `/api/tenants/${enc(d)}/storage/retention/preview?runId=${enc(runId)}`),
  admin: () => request<RetentionAdminView>('admin', 'GET', '/api/admin/retention'),
  setDefault: (defaultDays: number) => request<RetentionAdminView>('admin', 'PUT', '/api/admin/retention', { defaultDays }),
  setDeletions: (deletionsOn: boolean) => request<RetentionAdminView>('admin', 'PUT', '/api/admin/retention', { deletionsOn }),
  setHotData: (hotDataOn: boolean) => request<RetentionAdminView>('admin', 'PUT', '/api/admin/retention', { hotDataOn })
};

// --- History over every date (Storage A1) ---------------------------------------------------------

export interface HistoryCall {
  id: string;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  direction: string;
  from: { number: string; name: string };
  to: { number: string; name: string };
  extensions: string[];
  answeredBy: string | null;
  result: 'answered' | 'missed' | 'voicemail';
  seconds: number;
  talkSeconds: number;
  hasRecording: boolean;
  /** Its recording has a written-out transcript. */
  hasTranscript: boolean;
  mailbox: string | null;
  /** One of Stitch's own Patch calls, shown as this line. */
  patch?: { kind: 'ring' | 'call'; text: string };
  from_: 'stitch' | 'archive' | 'your-storage';
}

export interface HistoryCallsView {
  calls: HistoryCall[];
  next: string | null;
  hotFrom: string | null;
  archive: 'not-needed' | 'ok' | 'unavailable' | 'your-storage-unreachable';
  /** Sorted by length: the first day sorted, when the dates reach further back than Stitch keeps calls. */
  sortedFrom: string | null;
  /** When the first call Stitch kept for the company started. */
  keptSince: string | null;
  /** Whose calls the person sees: every call, their role's queues' (and their own), or their own. */
  scope: 'all' | 'queues' | 'own';
}

export type HistorySort = 'newest' | 'oldest' | 'longest' | 'shortest';

export interface HistorySearch {
  from: string;
  to: string;
  q?: string;
  ext?: string;
  direction?: 'inbound' | 'outbound' | 'internal';
  result?: 'answered' | 'missed' | 'voicemail';
  recorded?: 'yes' | 'no';
  transcript?: 'yes' | 'no';
  /** Talked longer than this many seconds. */
  longer?: number;
  sort?: HistorySort;
  cursor?: string;
  limit?: number;
}

export const historyApi = {
  calls: (d: string, s: HistorySearch) => {
    const qs = new URLSearchParams(
      Object.entries(s)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)])
    ).toString();
    return request<HistoryCallsView>('tenant', 'GET', `/api/tenants/${enc(d)}/history/calls?${qs}`);
  },
  /** The phone system's record of a call Stitch keeps, to open it in full (null: Stitch's summary only). */
  record: (d: string, id: string) => request<{ recordId: string | null }>('tenant', 'GET', `/api/tenants/${enc(d)}/history/calls/${enc(id)}/record`),
  voicemail: (d: string, s: HistoryArchiveSearch & { mailbox?: string }) => request<HistoryVoicemailView>('tenant', 'GET', `/api/tenants/${enc(d)}/history/voicemail?${qsOf(s)}`),
  messages: (d: string, s: HistoryArchiveSearch & { kind?: 'channel' | 'dm' | 'text'; person?: string; files?: 'yes' }) =>
    request<HistoryMessagesView>('tenant', 'GET', `/api/tenants/${enc(d)}/history/messages?${qsOf(s)}`),
  patches: (d: string, s: HistoryArchiveSearch & { person?: string }) => request<HistoryPatchesView>('tenant', 'GET', `/api/tenants/${enc(d)}/history/patches?${qsOf(s)}`),
  /** What an archived item has to play or download (a call's recordings, a voicemail, a message's files). */
  files: (d: string, table: HistoryFileTable, id: string, at: string) =>
    request<{ files: HistoryFile[]; transcript: string | null }>('tenant', 'GET', `/api/tenants/${enc(d)}/history/files/${table}/${enc(id)}?at=${enc(at)}`),
  /** One of them, through Stitch (each listen and download is logged). */
  fileUrl: (d: string, table: HistoryFileTable, id: string, n: number, at: string, download = false) =>
    `/api/tenants/${enc(d)}/history/files/${table}/${enc(id)}/${n}?at=${enc(at)}${download ? '&download=1' : ''}`,
  // Meetings (Storage A3, 3086): the runs, one run in full, and its downloads (each logged).
  meetings: (d: string, s: HistoryArchiveSearch & { person?: string; kind?: 'meeting' | 'webinar'; recorded?: 'yes' }) =>
    request<HistoryMeetingsView>('tenant', 'GET', `/api/tenants/${enc(d)}/history/meetings?${qsOf(s)}`),
  meeting: (d: string, id: string, at: string) => request<HistoryMeetingRun>('tenant', 'GET', `/api/tenants/${enc(d)}/history/meetings/${enc(id)}?at=${enc(at)}`),
  meetingAttendanceUrl: (d: string, id: string, at: string) => `/api/tenants/${enc(d)}/history/meetings/${enc(id)}/attendance?at=${enc(at)}`,
  meetingChatUrl: (d: string, id: string, at: string) => `/api/tenants/${enc(d)}/history/meetings/${enc(id)}/chat?at=${enc(at)}`,
  meetingFileUrl: (d: string, id: string, n: number, at: string, download = false) => `/api/tenants/${enc(d)}/history/meetings/${enc(id)}/files/${n}?at=${enc(at)}${download ? '&download=1' : ''}`
};

const qsOf = (s: object) =>
  new URLSearchParams(
    Object.entries(s)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)])
  ).toString();

/** Where an archived item was read from: Stitch's archive, or the company's own storage. */
export type HistoryKept = 'archive' | 'your-storage';
export type HistoryArchiveState = 'ok' | 'unavailable' | 'your-storage-unreachable' | 'not-set-up';
export type HistoryFileTable = 'calls' | 'voicemails' | 'messages';
export interface HistoryFile {
  n: number;
  name: string;
  bytes: number | null;
  type: string;
}

export interface HistoryArchiveSearch {
  from: string;
  to: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface HistoryVoicemail {
  id: string;
  leftAt: string;
  mailbox: string;
  mailboxName: string;
  from: { number: string; name: string };
  seconds: number;
  transcript: string | null;
  from_: HistoryKept;
}
export interface HistoryVoicemailView {
  voicemails: HistoryVoicemail[];
  next: string | null;
  archive: HistoryArchiveState;
  /** The first day the archive has voicemail for the company (null: none yet). */
  keptSince: string | null;
  scope: 'all' | 'own';
}

export interface HistoryMessage {
  id: string;
  sentAt: string;
  conversation: { id: string; kind: string; name: string };
  author: { id: string | null; name: string };
  body: string;
  editedAt: string | null;
  text: { direction: string | null; status: string | null; stitchNumber: string | null; otherNumber: string | null } | null;
  files: { n: number; id: string; name: string | null }[];
  from_: HistoryKept;
}
export interface HistoryMessagesView {
  messages: HistoryMessage[];
  next: string | null;
  archive: HistoryArchiveState;
  keptSince: string | null;
  /** 'member': the conversations the person can read; 'all': every one (an admin, with the company's switch on). */
  scope: 'all' | 'member';
  adminsSearch: boolean;
  admin: boolean;
}

export interface HistoryPatch {
  id: string;
  startedAt: string;
  endedAt: string | null;
  conversation: { id: string; name: string | null } | null;
  startedBy: { id: string | null; name: string };
  people: { id: string; name: string }[];
  seconds: number;
  endReason: string | null;
  from_: HistoryKept;
}
export interface HistoryPatchesView {
  patches: HistoryPatch[];
  next: string | null;
  archive: HistoryArchiveState;
  keptSince: string | null;
  scope: 'all' | 'own';
}

/** History's Meetings tab (Storage A3, 3086): one run of a meeting or webinar from the archive. */
export interface HistoryMeeting {
  id: string;
  meetingId: string;
  run: number;
  title: string;
  kind: string;
  startedAt: string;
  endedAt: string | null;
  host: { id: string | null; name: string };
  people: number;
  guests: number;
  names: string[];
  recorded: boolean;
  from_: 'archive' | 'your-storage';
}

export interface HistoryMeetingsView {
  meetings: HistoryMeeting[];
  next: string | null;
  archive: HistoryArchiveState;
  keptSince: string | null;
  scope: 'all' | 'own';
}

export interface HistoryMeetingRun {
  meeting: { id: string; title: string; kind: string; startedAt: string | null; endedAt: string | null; scheduledStart: string | null; host: { id: string | null; name: string }; lobby: boolean; from_: 'archive' | 'your-storage' };
  people: { name: string; email: string | null; kind: 'member' | 'guest'; role: string; joinedAt: string | null; leftAt: string | null; seconds: number; outcome: string; askedAt: string | null; answeredByName: string | null }[];
  events: { at: string; kind: string; who: string; text: string | null }[];
  chatLines: number;
  files: HistoryFile[];
  transcript: string | null;
  /** One of the company's own people: recordings, the transcript and the chat are theirs only. */
  member: boolean;
}

export const archiveApi = {
  view: () => request<ArchiveSettingsView>('admin', 'GET', '/api/admin/archive'),
  save: (body: ArchiveSettingsChange) => request<ArchiveSettingsView>('admin', 'PUT', '/api/admin/archive', body),
  test: () => request<{ ok: boolean; steps: ArchiveTestStep[] }>('admin', 'POST', '/api/admin/archive/test', {}),
  useBlockStorageKey: (body: { bucket?: string; prefix?: string } = {}) =>
    request<{ view: ArchiveSettingsView; test: { ok: boolean; steps: ArchiveTestStep[] } | null; note: string | null }>('admin', 'POST', '/api/admin/archive/use-block-storage-key', body),
  backfill: () => request<{ queued: number; calls: number; messages: number; patches: number; transcripts: number; meetings?: number; view: ArchiveSettingsView }>('admin', 'POST', '/api/admin/archive/backfill', {}),
  retry: () => request<{ retried: number; view: ArchiveSettingsView }>('admin', 'POST', '/api/admin/archive/retry', {}),
  health: () => request<ArchiveHealthView>('admin', 'GET', '/api/admin/archive/health'),
  /** Stitch Admin → Archive (A3, 3086): every company's archive, deletion runs, holds, archive-only companies. */
  companies: () => request<ArchiveCompaniesView>('admin', 'GET', '/api/admin/archive/companies'),
  drills: (customerId?: string) =>
    request<{ drills: ArchiveDrillView[]; companies: DrillCompany[]; days: DrillDay[] | null }>('admin', 'GET', `/api/admin/archive/drills${customerId ? `?customerId=${enc(customerId)}` : ''}`),
  runDrill: (customerId: string, day: string) => request<{ drill: ArchiveDrillView; created: boolean }>('admin', 'POST', '/api/admin/archive/drills', { customerId, day }),
  drill: (id: string) => request<ArchiveDrillView>('admin', 'GET', `/api/admin/archive/drills/${enc(id)}`)
};

/** Stitch Admin → System → Restore drill (3123): a company's day read back from the archive and checked. */
export interface DrillTableResultView {
  table: string;
  ok: boolean;
  parts: number;
  rows: number;
  catalogRows: number;
  records: number;
  bytes: number;
  said: string;
}

export interface DrillCopyResultView {
  copy: 'stitch' | 'company';
  ok: boolean;
  tables: DrillTableResultView[];
  media: { files: number; bytes: number; ok: boolean; mismatched: number; missing: number; said: string };
  problems: string[];
  note: string;
  ms: number;
}

export interface ArchiveDrillView {
  id: string;
  customerId: string;
  customerName: string;
  day: string;
  requestedByName: string;
  status: 'waiting' | 'running' | 'passed' | 'failed' | 'error';
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  summary: string;
  copies: DrillCopyResultView[];
  tables: number;
  rows: number;
  files: number;
  bytes: number;
  problems: number;
}

export interface DrillCompany {
  id: string;
  name: string;
  domain: string | null;
  firstDay: string;
  lastDay: string;
}

/** A day a company has in the archive: rows in Stitch's copy and in its own (null: none there). */
export interface DrillDay {
  day: string;
  stitch: number | null;
  company: number | null;
}

/** Stitch Admin → System → Archive health (3123): each line's state, and what to do about it. */
export type ArchiveHealthState = 'ok' | 'warn' | 'problem' | 'off';

export interface ArchiveHealthLine {
  id: string;
  title: string;
  state: ArchiveHealthState;
  said: string;
  todo: string | null;
  /** A card on System (/admin/system#…) or a page. */
  to: string | null;
}

export interface ArchiveHealthKindView {
  kind: string;
  label: string;
  state: ArchiveHealthState;
  said: string;
  waiting: number;
  failing: number;
  parked: number;
  oldestQueuedAt: string | null;
  lastDoneAt: string | null;
  doneLast10Min: number;
  lastError: string | null;
}

export interface ArchiveHealthView {
  checkedAt: string;
  worst: ArchiveHealthState;
  lines: ArchiveHealthLine[];
  kinds: ArchiveHealthKindView[];
}

/** Stitch Admin → Archive (Storage A3, 3086): the archive company by company. */
export interface ArchiveCompanyView {
  customerId: string;
  name: string;
  domain: string | null;
  status: string;
  archiveOnlyAt: string | null;
  waiting: number;
  parked: number;
  oldestWaiting: string | null;
  lastError: string | null;
  stitch: { days: number; bytes: number; lastWrittenAt: string | null };
  own: {
    provider: string;
    bucket: string;
    folder: string;
    mode: 'readable' | 'sealed';
    waiting: number;
    oldestWaiting: string | null;
    failures: number;
    lastError: { at: string; said: string } | null;
    lastWriteAt: string | null;
    lastProbeAt: string | null;
    keyExpiresAt: string | null;
    backfill: 'running' | 'done' | null;
  } | null;
  health: 'ok' | 'waiting' | 'behind' | 'failing';
  said: string;
}

export interface ArchiveDeletionView {
  runId: string;
  customerId: string;
  company: string;
  kind: string;
  at: string;
  days: number;
  cutoff: string;
  outcome: string;
  items: number;
  bytes: number;
  deleteState: 'done' | 'skipped' | 'failed' | null;
  deleteNote: string | null;
  deletedAt: string | null;
  deleted: { items: number; bytes: number; files: number } | null;
  attempts: number;
}

export interface ArchiveCompaniesView {
  at: string;
  companies: ArchiveCompanyView[];
  deletions: ArchiveDeletionView[];
  holds: { id: string; customerId: string; company: string; scope: string; on: string; fromDay: string | null; toDay: string | null; placedByName: string; placedAt: string; byStaff: boolean }[];
  archiveOnly: { customerId: string; name: string; domain: string | null; since: string | null; lastSignInAt: string | null; people: number }[];
}

/** Stitch Admin → System → Rating (stitch-rating, 3097): is it answering, where, and how far behind. */
export interface RatingView {
  set: boolean;
  health?: { url: string | null; ok: boolean; key: boolean; body: { version?: string; build?: string; role?: string; queued?: number; behind?: number | null } | null };
  status?: { events: number; queued: number; priced: number; lastEventAt: string | null; behind: number } | null;
  address?: string | null;
}

export const ratingApi = {
  view: () => request<RatingView>('admin', 'GET', '/api/admin/billing/rating'),
  setAddress: (serviceUrl: string | null) => request<{ address: string | null }>('admin', 'PUT', '/api/admin/billing/rating/address', { serviceUrl })
};

/** Calls abroad and to premium-rate numbers (3186): a company's switch, limits, spend and what's paused. */
export interface CallLimitsView {
  limits: {
    customerId: string;
    international: boolean;
    dailyCents: number;
    monthlyCents: number;
    overrideUntil: string | null;
    overrideBy: string | null;
    overrideReason: string | null;
    paused: 'off' | 'daily' | 'monthly' | null;
    pausedAt: string | null;
    savedPlans: Record<string, string>;
    problem: string | null;
    checkedAt: string | null;
    saved: boolean;
  };
  /** Today and this month so far (null: the rating service didn't answer). */
  spend: { todayCents: number; monthCents: number; unratedMinutes: number } | null;
  settings: { pausePlan: string | null; pausePlanName: string | null };
}

export interface RateTableView {
  version: number;
  effectiveFrom: string;
  markupPercent: number;
  source: string;
  note: string;
  rates: number;
  createdBy: string;
  createdAt: string;
}

export const callLimitsApi = {
  view: (customerId: string) => request<CallLimitsView>('admin', 'GET', `/api/admin/billing/call-limits/${enc(customerId)}`),
  save: (customerId: string, body: { international?: boolean; dailyCents?: number; monthlyCents?: number }) => request<CallLimitsView>('admin', 'PUT', `/api/admin/billing/call-limits/${enc(customerId)}`, body),
  override: (customerId: string, until: string | null, reason = '') => request<CallLimitsView>('admin', 'POST', `/api/admin/billing/call-limits/${enc(customerId)}/override`, { until, reason }),
  overview: () =>
    request<{ settings: CallLimitsView['settings']; companies: (CallLimitsView['limits'] & { name: string })[] }>('admin', 'GET', '/api/admin/billing/call-limits'),
  setPausePlan: (plan: { id: string; name: string } | null) => request<CallLimitsView['settings']>('admin', 'PUT', '/api/admin/billing/call-limits/pause-plan', { plan }),
  dialPlans: (tenant: string) => request<{ plans: { id: string; name: string; global: boolean }[] }>('admin', 'GET', `/api/admin/billing/dial-plans?tenant=${enc(tenant)}`),
  /** available: false when the rating service isn't set up or answering (tables is then empty). */
  rateTables: () => request<{ tables: RateTableView[]; available: boolean }>('admin', 'GET', '/api/admin/billing/rating/rate-tables'),
  rates: (version: number, prefix = '') =>
    request<{ rates: { prefix: string; name: string; costCents: number | null; unitCents: number }[] }>('admin', 'GET', `/api/admin/billing/rating/rate-tables/${version}/rates?prefix=${enc(prefix)}`),
  addRateTable: (body: { effectiveFrom: string; markupPercent: number; source: string; note: string; csv: string }) =>
    request<{ table: RateTableView; nowRated: number }>('admin', 'POST', '/api/admin/billing/rating/rate-tables', body),
  unrated: () =>
    request<{ available: boolean; unrated: { account: string; name: string; prefix: string; calls: number; minutes: number; first: string; last: string }[] }>('admin', 'GET', '/api/admin/billing/rating/unrated'),
  companyRates: (customerId: string) => request<{ rates: { prefix: string; unitCents: number; note: string; createdBy: string; createdAt: string }[] }>('admin', 'GET', `/api/admin/billing/rating/company-rates/${enc(customerId)}`),
  setCompanyRate: (customerId: string, body: { prefix: string; unitCents: number | null; note?: string }) => request<{ nowRated: number }>('admin', 'PUT', `/api/admin/billing/rating/company-rates/${enc(customerId)}`, body),
  quote: (customerId: string, number: string) =>
    request<{ kind: string; number: string | null; rate: { unitCents: number; prefix: string; name: string; source: string } | null }>('admin', 'GET', `/api/admin/billing/rating/quote?customerId=${enc(customerId)}&number=${enc(number)}`)
};

/** Teampace (3089): each company's Teampace client. Stitch staff. */
export interface TeampaceCandidateView {
  id: number;
  name: string;
  why: string;
}
export interface TeampaceLinkView {
  configured: boolean;
  syncFrom: string | null;
  link: {
    customerId: string;
    clientId: number | null;
    state: 'waiting' | 'linked' | 'failed' | 'review';
    how: 'created' | 'matched' | 'chosen' | null;
    candidates: TeampaceCandidateView[];
    attempts: number;
    nextAt: string;
    lastError: string | null;
    differs: { field: string; stitch: string; teampace: string }[];
    contacts: number;
    linkedAt: string | null;
    checkedAt: string | null;
    updatedAt: string;
  } | null;
  client: { id: number; name: string; url: string | null } | null;
  /** Invoices, credit notes and voids sent to Teampace (3090), newest first. */
  documents: TeampaceDocumentView[];
  /** Payments (3091): the newest charges, and invoices unpaid on Stitch's clock. Missing on a retry or link answer. */
  payments?: {
    charges: (ChargeView & { number: string; source: string; key: string | null; teampaceId: number | null })[];
    unpaid: { invoiceId: string; number: string; openCents: number; why: 'failed' | 'overdue'; startedAt: string; failures: number; lastReason: string | null; blockedAt: string | null; warnedAt: string | null; suspendedAt: string | null }[];
  };
}
export interface TeampaceDocumentView {
  key: string;
  kind: 'invoice' | 'credit_note' | 'void';
  number: string;
  state: 'waiting' | 'sent' | 'failed';
  teampaceId: number | null;
  fileAttached: boolean;
  lastError: string | null;
  note: string | null;
  nextAt: string;
  sentAt: string | null;
  attempts: number;
}
/** Stitch Admin → System → Teampace, Take payments (3091). */
export interface TeampacePaymentSettings {
  on: boolean;
  from: string | null;
  retryDays: number[];
  retryFrom: 'teampace' | 'default';
}
/** The nightly reconciliation with Teampace (3093). */
export interface TeampaceDifference {
  key: string;
  customerId: string | null;
  company: string;
  kind: 'missing' | 'only_in_teampace' | 'total' | 'void' | 'credit_note' | 'payment' | 'credit_used';
  invoiceId: string | null;
  number: string;
  teampaceId: number | null;
  stitchCents: number | null;
  teampaceCents: number | null;
  detail: string;
  firstSeenAt: string;
  lastSeenAt: string;
}
export interface TeampaceReconRun {
  id: number;
  day: string;
  windowFrom: string;
  windowTo: string;
  status: 'running' | 'done' | 'failed';
  counts: Record<string, number>;
  error: string | null;
  byName: string;
  startedAt: string;
  finishedAt: string | null;
}
export interface TeampaceMonthReport {
  month: string;
  ended: boolean;
  invoices: { stitch: number; teampace: number };
  rows: { key: string; label: string; stitchCents: number; teampaceCents: number }[];
  matches: boolean;
  signoff: { month: string; matches: boolean; byName: string; at: string } | null;
}
export const reconApi = {
  status: () => request<{ configured: boolean; runs: TeampaceReconRun[]; open: TeampaceDifference[] }>('admin', 'GET', '/api/admin/teampace/reconciliation'),
  run: () => request<{ configured: boolean; runs: TeampaceReconRun[]; open: TeampaceDifference[] }>('admin', 'POST', '/api/admin/teampace/reconciliation/run', {}),
  month: (month: string) => request<TeampaceMonthReport>('admin', 'GET', `/api/admin/teampace/reconciliation/months/${enc(month)}`),
  signOff: (month: string) => request<TeampaceMonthReport>('admin', 'POST', `/api/admin/teampace/reconciliation/months/${enc(month)}/sign-off`, {})
};
export interface TeampaceReviewRow {
  customerId: string;
  company: string;
  proposal: 'link' | 'choose' | 'create';
  candidates: TeampaceCandidateView[];
}
export const teampaceApi = {
  status: () =>
    request<{
      configured: boolean;
      syncFrom: string | null;
      counts: Record<string, number>;
      companies: number;
      push: { push: boolean; pushFrom: string | null };
      documents: Record<string, number>;
      payments: TeampacePaymentSettings & { counts: Record<string, number> };
    }>('admin', 'GET', '/api/admin/teampace'),
  setPush: (on: boolean) => request<{ push: boolean; pushFrom: string | null }>('admin', 'PUT', '/api/admin/teampace/push', { on }),
  setPayments: (on: boolean) => request<TeampacePaymentSettings>('admin', 'PUT', '/api/admin/teampace/payments', { on }),
  retryDocument: (customerId: string, key: string) => request<{ document: TeampaceDocumentView | null }>('admin', 'POST', `/api/admin/customers/${enc(customerId)}/teampace/documents/retry`, { key }),
  start: () => request<{ syncFrom: string }>('admin', 'POST', '/api/admin/teampace/start', {}),
  review: () => request<{ rows: TeampaceReviewRow[] }>('admin', 'GET', '/api/admin/teampace/review'),
  applyReview: (choices: { customerId: string; action: 'link' | 'create' | 'skip'; clientId?: number }[]) =>
    request<{ linked: number; created: number; skipped: number; failed: { customerId: string; error: string }[] }>('admin', 'POST', '/api/admin/teampace/review', { choices }),
  view: (customerId: string) => request<TeampaceLinkView>('admin', 'GET', `/api/admin/customers/${enc(customerId)}/teampace`),
  retry: (customerId: string) => request<TeampaceLinkView>('admin', 'POST', `/api/admin/customers/${enc(customerId)}/teampace/retry`, {}),
  link: (customerId: string, clientId: number | null) => request<TeampaceLinkView>('admin', 'POST', `/api/admin/customers/${enc(customerId)}/teampace/link`, { clientId })
};

export const calendarApi = {
  status: () => request<CalendarStatus>('tenant', 'GET', '/api/me/calendar'),
  /** Google's page to say yes; back to `back` afterwards. */
  connectGoogle: (back: string) => request<{ url: string }>('tenant', 'POST', '/api/me/calendar/google', { back }),
  disconnectGoogle: () => request<{ ok: true }>('tenant', 'POST', '/api/me/calendar/google/disconnect', json({})),
  google: () => request<GoogleSettingsView>('admin', 'GET', '/api/admin/google'),
  saveGoogle: (body: { clientId?: string | null; clientSecret?: string | null }) => request<GoogleSettingsView>('admin', 'PUT', '/api/admin/google', body)
};

export interface MeetingBreakouts {
  rooms: { n: number; name: string; people: string[] }[];
  openedAt: string;
}

export interface MeetingRun {
  startedAt: string;
  endedAt: string | null;
  seconds: number;
  people: { name: string; guest: boolean; seconds: number }[];
}

/** What to connect with: the room's address, a token for this person, and whether they may talk and share. */
export interface MeetingConnect {
  url: string;
  token: string;
  identity: string;
  publish: boolean;
  /** Which room: 0 the meeting's own, else a breakout room (with its name). */
  room: number;
  roomName: string;
  /** Being recorded right now (everyone is told). */
  recording: boolean;
  /** This person may start recording. */
  recordable: boolean;
}

/** What anyone with the link sees first (and, signed in to the company, where to join it). */
export interface MeetingDoor {
  title: string;
  company: string;
  hostName: string;
  kind: MeetingKind;
  startsAt: string | null;
  endsAt: string | null;
  live: boolean;
  lobby: boolean;
  member?: { id: string; tenant: string };
  /** Being recorded right now: said before anyone comes in. */
  recording: boolean;
}

export type GuestState = 'waiting' | 'in' | 'denied' | 'left';

export const meetingsApi = {
  list: (d: string) => request<{ enabled: boolean; meetings: MeetingView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/meetings`),
  /** Coworkers (Joey, Nov 3 2026): a message from you with the details; a ring while it's going on now. */
  invite: (d: string, id: string, people: string[]) =>
    request<MeetingInviteResult>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/invite`, { people, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
  get: (d: string, id: string) => request<{ meeting: MeetingView; runs: MeetingRun[]; recordings: MeetingRecordingView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/meetings/${enc(id)}`),
  record: (d: string, id: string, on: boolean) => request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/recording`, { on }),
  recordingUrl: (d: string, id: string, rec: string, kind: 'video' | 'audio', download = false) =>
    `/api/tenants/${enc(d)}/meetings/${enc(id)}/recordings/${enc(rec)}/${kind}${download ? '?download=1' : ''}`,
  deleteRecording: (d: string, id: string, rec: string) => request<{ ok: true }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/meetings/${enc(id)}/recordings/${enc(rec)}`),
  create: (d: string, body: { title: string; kind?: MeetingKind; startsAt?: string | null; endsAt?: string | null; lobby?: boolean }) =>
    request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings`, body),
  update: (d: string, id: string, body: { title?: string; kind?: MeetingKind; startsAt?: string | null; endsAt?: string | null; lobby?: boolean }) =>
    request<MeetingView>('tenant', 'PATCH', `/api/tenants/${enc(d)}/meetings/${enc(id)}`, body),
  cancel: (d: string, id: string) => request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/cancel`, json({})),
  join: (d: string, id: string, room?: number) =>
    request<MeetingConnect & { meeting: MeetingView }>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/join`, room === undefined ? json({}) : { room }),
  openBreakouts: (d: string, id: string, body: { rooms: number; names?: string[]; assign: Record<string, number> }) =>
    request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/breakouts`, body),
  moveTo: (d: string, id: string, identity: string, room: number) => request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/breakouts/move`, { identity, room }),
  closeBreakouts: (d: string, id: string) => request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/breakouts/close`, json({})),
  lobby: (d: string, id: string) => request<{ waiting: { id: string; name: string; askedAt: string }[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/meetings/${enc(id)}/lobby`),
  answer: (d: string, id: string, guestId: string, letIn: boolean) =>
    request<{ answered: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/lobby/${enc(guestId)}`, { in: letIn }),
  admitAll: (d: string, id: string) => request<{ admitted: number }>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/lobby/all`, json({})),
  remove: (d: string, id: string, identity: string) => request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/remove`, { identity }),
  mute: (d: string, id: string, identity: string) => request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/mute`, { identity }),
  presenter: (d: string, id: string, identity: string, on: boolean) => request<MeetingView>('tenant', 'POST', `/api/tenants/${enc(d)}/meetings/${enc(id)}/presenters`, { identity, on }),
  // The link (anyone): the guest's key goes in a header, never in the address.
  door: (code: string) => request<MeetingDoor>('tenant', 'GET', `/api/m/${enc(code)}`),
  ask: (code: string, name: string) => request<{ guestId: string; key: string; state: GuestState }>('tenant', 'POST', `/api/m/${enc(code)}/guest`, { name }),
  status: (code: string, guestId: string, key: string) =>
    request<{ state: GuestState; join?: MeetingConnect & { title: string } }>('tenant', 'GET', `/api/m/${enc(code)}/guest/${enc(guestId)}`, undefined, undefined, { 'x-meeting-key': key }),
  leave: (code: string, guestId: string, key: string) => request<{ ok: true }>('tenant', 'POST', `/api/m/${enc(code)}/guest/${enc(guestId)}/leave`, json({}), undefined, { 'x-meeting-key': key }),
  /** The meeting as a calendar file (Outlook, Apple): a download, from the company's page or the link's. */
  icsUrl: (d: string, id: string) => `/api/tenants/${enc(d)}/meetings/${enc(id)}/ics`,
  doorIcsUrl: (code: string) => `/api/m/${enc(code)}/ics`
};

// --- calls in the app: placed and answered here, through the call gateway, with sound on LiveKit ---

/** A call on the person's own line, as the app shows it (calling/calls.ts on the server). */
export interface AppCall {
  id: string;
  tenant: string;
  extension: string;
  direction: 'inbound' | 'outbound';
  state: 'ringing' | 'dialing' | 'active' | 'ended';
  /** The other end: who's calling, or what was dialed. */
  number: string | null;
  name: string | null;
  forwardedFrom: { number: string | null; name: string | null } | null;
  waiting: boolean;
  emergency: boolean;
  held: boolean;
  remoteHeld: boolean;
  /** "<user id>:<device>": which open Stitch answered it. */
  answeredBy: string | null;
  /** A warm transfer in progress: this call is the consult for that one. */
  consultFor?: string | null;
  /** The conference the call is in (its first call's id): calls that share one are one call with several people. */
  conference?: string | null;
  /** The person added this one to the conference (so they can remove them). */
  added?: boolean;
  /** The person left this conference: it's going on without them. */
  left?: boolean;
  /** The latest change, with the phone system's code when it refused (486 busy, 404 no such number…). */
  event: { type: string; cause?: string; code?: number; transferState?: string };
  /** Newer changes have bigger numbers: an older one heard late is ignored. */
  seq: number;
  /** Someone a Patch rang, calling back while it's going: "Bring Dana into the Patch?" (1.12.18). */
  patchOffer?: { patchId: string; conversationId: string; userId: string; name: string } | null;
}
/** What this app needs to hear and be heard on a call: LiveKit's address, the room and its own token. */
export interface CallJoin {
  url: string;
  room: string;
  token: string;
}
export type CallAction = 'decline' | 'hangup' | 'hold' | 'resume' | 'leave' | 'end-all';

export const callsApi = {
  /** Whether calls in the app work here (and why not), and the calls on my line now. */
  line: (d: string) => request<{ on: boolean; available: boolean; reason?: string; extension: string; calls: AppCall[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/line/calls`),
  dial: (d: string, to: string, device: string) => request<{ call: AppCall; join: CallJoin | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls`, { to, device }),
  answer: (d: string, id: string, device: string) => request<{ call: AppCall; join: CallJoin | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/answer`, { device }),
  act: (d: string, id: string, action: CallAction) => request<{ call: AppCall }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/${action}`, {}),
  dtmf: (d: string, id: string, digits: string) => request<{ call: AppCall }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/dtmf`, { digits }),
  transfer: (d: string, id: string, to: string) => request<{ call: AppCall }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/transfer`, { to }),
  /** A warm transfer: the call is held and this device calls `to` (with the consult's sound). */
  consult: (d: string, id: string, to: string, device: string) =>
    request<{ call: AppCall; join: CallJoin | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/consult`, { to, device }),
  complete: (d: string, id: string, consult: string) => request<{ call: AppCall; join: null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/complete`, { consult }),
  cancelConsult: (d: string, id: string, consult: string, device: string) =>
    request<{ call: AppCall; join: CallJoin | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/cancel-consult`, { consult, device }),
  /** This device takes up a connected call's sound again. */
  rejoin: (d: string, id: string, device: string) => request<{ call: AppCall; join: CallJoin | null }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/rejoin`, { device }),
  /** A call back from someone a Patch rang: answered into the Patch (the app joins the Patch to hear it). */
  bringIntoPatch: (d: string, id: string, device: string) => request<{ patchId: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/bring-into-patch`, { device }),
  /** Adds someone to the call (a conference): they're called from my line, and join everyone when they answer. */
  add: (d: string, id: string, to: string) => request<{ call: AppCall }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/add`, { to }),
  /** Where hanging up (or leaving a call with several people) goes when the tab closes (sent with keepalive, outside this client). */
  hangupUrl: (d: string, id: string, leave = false) => `/api/tenants/${enc(d)}/me/line/calls/${enc(id)}/${leave ? 'leave' : 'hangup'}`,
  /** The calls on my own phones (a desk phone, the phone system's app), and whether Stitch may hold or hang them up. */
  phoneCalls: (d: string) => request<{ calls: PhoneCallView[]; control: boolean; patches?: DeskInPatch[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/line/phone-calls`),
  /** Patches you were in or missed, for one history list with your calls (Live sessions, 1.13.0). */
  sessions: (d: string) => request<{ sessions: SessionHistoryItem[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/me/sessions`),
  phoneCall: (d: string, id: string, action: 'hold' | 'resume' | 'hangup') =>
    request<{ ok: true; calls: PhoneCallView[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/phone-calls/${enc(id)}/${action}`, {}),
  /** A blind transfer of a call on my desk phone (1.13.0, 2758): to an extension or a phone number. */
  transferPhoneCall: (d: string, id: string, to: string) =>
    request<{ ok: true; calls: PhoneCallView[] }>('tenant', 'POST', `/api/tenants/${enc(d)}/me/line/phone-calls/${enc(id)}/transfer`, { to })
};

/** A call on one of my own phones right now (not one in the app). */
/** A Patch in someone's history (Live sessions, 1.13.0): in it, or missed. */
export interface SessionHistoryItem {
  id: string;
  kind: 'patch' | 'meeting';
  /** A Patch's conversation. */
  conversationId: string | null;
  /** A meeting's (1.13.0). */
  meetingId?: string;
  title?: string;
  startedAt: string;
  endedAt: string;
  seconds: number;
  with: string[];
  missed: boolean;
  text: string;
}
/** The person's desk phone in a Patch (it answered the Patch's ring; Live sessions, 1.13.0). */
export interface DeskInPatch {
  patchId: string;
  conversationId: string;
  since: string | null;
  with: string[];
}
export interface PhoneCallView {
  id: string;
  state: 'ringing' | 'talking' | 'held' | 'other';
  inbound: boolean;
  name: string | null;
  number: string | null;
  /** How long it had been going when read. */
  seconds: number | null;
  /** How many of my phones it's on. */
  phones: number;
}

// --- the call records service (apps/records): voicemail emails, call recording transcripts, its status ---

/** A company's settings for its calls in Stitch (its admins). */
export interface CallSettingsView {
  /** False for a tenant that isn't a Stitch customer yet. */
  editable: boolean;
  voicemailEmails: boolean;
  /** What each person gets unless they choose for themselves. */
  defaultSend: boolean;
  defaultAttach: boolean;
  /** Every recorded call written out automatically. */
  recordingTranscripts: boolean;
  /** Hosts can record meetings (1.13.0). */
  meetingRecording: boolean;
  /** Recordings can be written out (the transcription service is set up). */
  transcription: boolean;
  /** The call records service is running (it sends the emails). */
  service: boolean;
}

/** A person's own voicemail emails at a company. */
export interface VoicemailEmailChoices {
  /** They have a mailbox there (their own extension). */
  available: boolean;
  /** The company has voicemail emails on. */
  on: boolean;
  extension: string | null;
  /** Where they go: the email address on the extension (null: none set, or not known). */
  address?: string | null;
  /** The mailbox's choice, shared by everyone signed in as the extension. */
  own: { send: boolean | null; attach: boolean | null };
  effective: { send: boolean; attach: boolean };
  defaults: { send: boolean; attach: boolean };
}

export interface RecordsStatus {
  service: {
    running: { instance: string; version: string; startedAt: string; seenAt: string }[];
    lastSeenAt: string | null;
    counts: { records: number; duplicates: number; uploads: number; refusedAuth: number; refusedAddress: number; invalid: number; openToAnyone?: number };
  };
  lastRecordAt: string | null;
  recordsToday: number;
  voicemailEmailsToday: { sent: number; failed: number; withoutAudio?: number; withoutAudioWhy?: string | null };
  /** The shared transcription queues (with Redis): main (asked-for recordings, voicemail) and bulk. */
  transcription: { main: Record<string, number>; bulk: Record<string, number> } | null;
  /** Automatic recording transcripts (when the transcription service is set up). */
  automatic: AutomaticTranscriptsView | null;
}

/** When automatic recording transcripts catch up: Stitch staff set it in Stitch Admin → System. */
export interface TranscriptionNightView {
  /** "20:00" and "07:00" (24-hour), in `zone`. */
  nightStart: string;
  nightEnd: string;
  zone: string;
  /** Whether it's night now; until when (the night's end, or the next one's start). */
  now: { inWindow: boolean; night: string; until: string };
  /** Saved by Stitch staff (null: the defaults). */
  saved: { at: string; byName: string } | null;
}

/** Automatic recording transcripts across the platform (Stitch Admin → System). */
export interface AutomaticTranscriptsView {
  /** Waiting to be written out, and how many of those are in the line now. */
  owed: number;
  inLine: number;
  /** Being written out now (null: not known, no Redis). */
  active: number | null;
  doneLastHour: number;
  doneLastHalfHour: number;
  /** Failed: to be tried again tonight, and for good (gone, too long, or three nights tried). */
  failedTonight: number;
  failedForGood: number;
  oldestOwedAt: string | null;
  /** Rough minutes to clear what's waiting at the last hour's pace (null: nothing waiting, or nothing done lately). */
  clearMinutes: number | null;
  /** Work is waiting but nothing finished in the last half hour. */
  stalled: boolean;
  night: TranscriptionNightView;
  lastNight: { night: string; doneAt: string | null; retried: number; found: number; lookups: number } | null;
}

/** One turn of a call recording's transcript: who spoke, when (seconds in), and what. */
export interface TranscriptTurnView {
  speaker: 'caller' | 'agent' | null;
  name: string;
  start: number;
  end: number;
  text: string;
}

export interface RecordingTranscriptView {
  recordingId: string;
  status: 'none' | 'pending' | 'done' | 'failed';
  turns?: TranscriptTurnView[];
  error?: string | null;
  /** While it waits: how many asked-for recordings go before it, or being written out now. */
  progress?: { state: 'waiting' | 'active'; ahead: number };
  /** Waiting to be written out automatically (its turn may be tonight): it can be asked for now. */
  automatic?: boolean;
}

export interface CdrSender {
  address: string;
  note: string;
  addedBy: string;
  addedAt: string;
}

/** Stitch Admin → System → Call recordings storage (1.12.27): the phone system's cloud-stored recordings. */
export interface RecordingsStorageView {
  endpoint: string;
  bucket: string;
  region: string;
  regionFromAddress: string | null;
  accessKeyId: { set: boolean };
  secretAccessKey: { set: boolean };
  /** Meeting recordings (1.13.0): the key LiveKit's recorder writes with. */
  uploadKey: { set: boolean };
  /** Everything needed is saved, and this container can unlock the key. */
  configured: boolean;
  /** Meeting recordings can be written (the upload key is there and unlocks). */
  uploads: boolean;
  problem: string | null;
  canSeal: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}
export interface RecordingsStorageCheck {
  ok: boolean;
  listed: number;
  publicBucket: boolean | null;
  message: string;
}
export const recordingsStorageApi = {
  get: () => request<RecordingsStorageView>('admin', 'GET', '/api/admin/settings/recordings'),
  save: (change: Partial<{ endpoint: string; bucket: string; region: string; accessKeyId: string; secretAccessKey: string; uploadKeyId: string; uploadSecret: string }>) =>
    request<RecordingsStorageView>('admin', 'PUT', '/api/admin/settings/recordings', change),
  remove: () => request<RecordingsStorageView>('admin', 'DELETE', '/api/admin/settings/recordings'),
  test: () => request<RecordingsStorageCheck>('admin', 'POST', '/api/admin/settings/recordings/test', {})
};

/** Stitch Admin → System → Call gateway (1.13.0, Teampace 2845): its address and token, kept in Stitch. */
export interface GatewaySettingsView {
  url: { value: string | null; source: 'stitch' | 'easypanel' | 'none'; changed: { at: string; byName: string } | null };
  /** Whether a token is set (never the token). */
  token: { set: boolean; source: 'stitch' | 'easypanel' | 'none'; changed: { at: string; byName: string } | null; unreadable: boolean };
  ready: boolean;
  canSeal: boolean;
  easypanel: { name: 'GATEWAY_URL' | 'GATEWAY_TOKEN'; inStitch: boolean }[];
  updatedAt: string | null;
  updatedByName: string | null;
  /** App calling started with a gateway (a change is used at once). */
  active: boolean;
  /** One was saved while app calling was off: it starts when Stitch restarts. */
  restartNeeded: boolean;
}

export const gatewaySettingsApi = {
  get: () => request<GatewaySettingsView>('admin', 'GET', '/api/admin/settings/gateway'),
  save: (change: { url?: string | null; token?: string | null }) => request<GatewaySettingsView>('admin', 'PUT', '/api/admin/settings/gateway', change),
  move: () => request<{ moved: string[]; kept: string[]; settings: GatewaySettingsView }>('admin', 'POST', '/api/admin/settings/gateway/move', {}),
  test: (typed: { url?: string; token?: string } = {}) => request<{ ok: boolean; message: string; version: string | null }>('admin', 'POST', '/api/admin/settings/gateway/test', typed)
};

/**
 * Stitch Admin → System: a group of settings kept in Stitch instead of EasyPanel (1.13.0, Teampace 2845):
 * LiveKit, email, Block Storage, transcripts and AI. Secrets only say whether they're set.
 */
export interface SettingsGroupView {
  fields: {
    key: string;
    env: string;
    secret: boolean;
    value: string | null;
    set: boolean;
    source: 'stitch' | 'easypanel' | 'none';
    changed: { at: string; byName: string } | null;
    unreadable: boolean;
  }[];
  canSeal: boolean;
  easypanel: { name: string; inStitch: boolean }[];
  updatedAt: string | null;
  updatedByName: string | null;
  /** In use now (a change is used at once). */
  active?: boolean;
  /** Saved while it was off: it starts when Stitch restarts. */
  restartNeeded?: boolean;
}

export const settingsGroupApi = (path: string) => ({
  get: () => request<SettingsGroupView>('admin', 'GET', `/api/admin/settings/${path}`),
  save: (change: Record<string, string | null>) => request<SettingsGroupView>('admin', 'PUT', `/api/admin/settings/${path}`, change),
  move: () => request<{ moved: string[]; kept: string[]; settings: SettingsGroupView }>('admin', 'POST', `/api/admin/settings/${path}/move`, {}),
  test: (typed: Record<string, string> = {}) => request<{ ok: boolean; message: string }>('admin', 'POST', `/api/admin/settings/${path}/test`, typed)
});

/** Stitch Admin → System → Call records → Check a mailbox. */
export interface VoicemailCheck {
  tenant: string;
  mailbox: string;
  /** Nobody signs in as it (a shared box). */
  shared: boolean;
  emails: { on: boolean; send: boolean; attach: boolean; attachFrom: 'mailbox' | 'company'; companyAttach: boolean };
  /** Email addresses on the extension (how many); null when it couldn't be read. */
  addresses: number | null;
  latest: { id: string; at: string; seconds: number; listedWithAudio: boolean } | null;
  listProblem: string | null;
  audio: { ok: boolean; type?: string; bytes?: number; file?: string; tooLarge?: boolean; why?: string } | null;
  emailed: { status: string; recipients: number; note: string | null } | null;
  verdict: { ok: boolean; text: string };
}

export const recordsApi = {
  callSettings: (d: string) => request<CallSettingsView>('tenant', 'GET', `/api/tenants/${enc(d)}/call-settings`),
  saveCallSettings: (d: string, set: Partial<Pick<CallSettingsView, 'voicemailEmails' | 'defaultSend' | 'defaultAttach' | 'recordingTranscripts' | 'meetingRecording'>>) =>
    request<CallSettingsView>('tenant', 'PATCH', `/api/tenants/${enc(d)}/call-settings`, set),
  voicemailEmails: (d: string) => request<VoicemailEmailChoices>('tenant', 'GET', `/api/tenants/${enc(d)}/me/voicemail-emails`),
  saveVoicemailEmails: (d: string, own: { send: boolean | null; attach: boolean | null }) => request<VoicemailEmailChoices>('tenant', 'PUT', `/api/tenants/${enc(d)}/me/voicemail-emails`, own),
  status: () => request<RecordsStatus>('admin', 'GET', '/api/admin/records'),
  /** Stitch staff: why a mailbox's voicemail emails go with or without the audio (1.12.26). */
  checkVoicemail: (tenant: string, mailbox: string) =>
    request<VoicemailCheck>('admin', 'GET', `/api/admin/records/voicemail-check?tenant=${enc(tenant)}&mailbox=${enc(mailbox)}`),
  /** Who may send call records (beside CDR_ALLOW_IPS): Stitch staff keep the list. */
  senders: () => request<{ senders: CdrSender[] }>('admin', 'GET', '/api/admin/records/senders'),
  addSender: (address: string, note: string) => request<{ senders: CdrSender[] }>('admin', 'POST', '/api/admin/records/senders', { address, note }),
  removeSender: (address: string) => request<{ senders: CdrSender[] }>('admin', 'DELETE', `/api/admin/records/senders/${enc(address)}`),
  /** The night window for automatic recording transcripts (Stitch staff). */
  night: () => request<TranscriptionNightView>('admin', 'GET', '/api/admin/settings/transcription'),
  saveNight: (w: { nightStart: string; nightEnd: string; zone: string }) => request<TranscriptionNightView>('admin', 'PUT', '/api/admin/settings/transcription', w),
  callTranscripts: (d: string, record: string) =>
    request<{ transcripts: RecordingTranscriptView[]; canTranscribe: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/calls/${enc(record)}/transcripts`),
  transcribe: (d: string, record: string, recording: string) =>
    request<{ transcript: RecordingTranscriptView }>('tenant', 'POST', `/api/tenants/${enc(d)}/calls/${enc(record)}/recordings/${enc(recording)}/transcribe`)
};

// --- Stitch Admin → Voicemail emails (moving companies to Stitch's, and back) ---------------------------

/** A company with a phone system tenant, and where its move stands (Stitch's data only). */
export interface VoicemailMoveCompany {
  customerId: string;
  name: string;
  domain: string;
  status: CustomerStatus;
  /** Inside the write fence. */
  writable: boolean;
  /** Stitch's voicemail emails are on. */
  stitchEmails: boolean;
  /** Mailboxes moved, with what they had saved (until put back). */
  saved: number;
  movedAt: string | null;
  movedBy: string | null;
}
export interface VoicemailMoveOverview {
  /** The phone system's own emails can be changed through Stitch. */
  available: boolean;
  companies: VoicemailMoveCompany[];
  writes: { readOnly: boolean; everywhere: boolean };
}
/** A mailbox as the phone system has it now, with what Stitch saved for it. */
export interface VoicemailMoveMailbox {
  extension: string;
  name: string;
  box: boolean;
  state: 'on' | 'moved' | 'off' | 'changed' | 'unknown';
  /** The phone system's own emails on now, in plain words. */
  on: string[];
  saved: boolean;
  savedAt?: string;
  /** What it had before the move. */
  had?: string[];
  detail?: string;
}
export type VoicemailMoveOutcome = 'moved' | 'already' | 'put_back' | 'nothing' | 'gone' | 'failed';
export interface VoicemailMoveResult {
  extension: string;
  name: string;
  box: boolean;
  outcome: VoicemailMoveOutcome;
  detail?: string;
  /** Moved: the emails it had on. Put back: the ones on again. */
  emails?: string[];
}
export interface VoicemailMoveCompanyResult {
  customerId: string;
  name: string;
  domain: string;
  outcome: 'done' | 'partial' | 'failed' | 'skipped' | 'nothing';
  detail?: string;
  stitchEmails: { on: boolean; changed: boolean; note: string };
  mailboxes: VoicemailMoveResult[];
}
export interface VoicemailMoveRun {
  action: 'move' | 'putBack';
  scope: 'all' | 'company' | 'mailbox';
  at: string;
  by: string;
  companies: VoicemailMoveCompanyResult[];
  totals: Record<VoicemailMoveOutcome, number> & { skippedCompanies: number };
}
/** Every company, one company, or one mailbox of it. */
export type VoicemailMoveScope = { all: true } | { customer: string; mailbox?: string };

export const voicemailMoveApi = {
  overview: () => request<VoicemailMoveOverview>('admin', 'GET', '/api/admin/voicemail-emails'),
  mailboxes: (customerId: string) => request<{ company: VoicemailMoveCompany; mailboxes: VoicemailMoveMailbox[] }>('admin', 'GET', `/api/admin/voicemail-emails/${enc(customerId)}`),
  move: (scope: VoicemailMoveScope) => request<{ run: VoicemailMoveRun }>('admin', 'POST', '/api/admin/voicemail-emails/move', scope),
  putBack: (scope: VoicemailMoveScope) => request<{ run: VoicemailMoveRun }>('admin', 'POST', '/api/admin/voicemail-emails/put-back', scope)
};

// --- Stitch Admin → Updates (1.12.33) --------------------------------------------------------------
// Each customer's state of each update (App calling, voicemail emails, old voicemail, Patch rings),
// one customer from its page or many from the Updates page. See apps/api/src/updates.

export type UpdateId = 'app-calling' | 'voicemail-emails' | 'mailbox-policy' | 'patch-rings' | 'live-sessions' | 'number-inventory';
export type UpdateStateName = 'unknown' | 'done' | 'needed' | 'partial' | 'problem' | 'unavailable';
export type UpdateAction = 'check' | 'apply' | 'put_back';

export interface UpdateInfo {
  id: UpdateId;
  title: string;
  about: string;
  applyLabel: string;
  putBackLabel: string | null;
  needs: UpdateId[];
}

export interface UpdateSummary extends UpdateInfo {
  counts: Record<UpdateStateName, number> & { leftOut: number; running: number; customers: number };
  lastCheckedAt: string | null;
}

export interface UpdateRow {
  customerId: string;
  name: string;
  domain: string | null;
  status: CustomerStatus;
  state: UpdateStateName;
  summary: string;
  counts: Record<string, number>;
  leftOut: boolean;
  leftOutNote: string;
  running: UpdateAction | null;
  checkedAt: string | null;
  changedAt: string | null;
  changedBy: string | null;
  lastAction: 'apply' | 'put_back' | null;
  lastResult: string;
  blocked: string | null;
}

export interface MailboxPolicyRow {
  extension: string;
  name: string;
  box: boolean;
  policy: string | null;
  label: string;
  state: 'standard' | 'different' | 'unset' | 'company' | 'other' | 'unknown';
  saved: { previous: string; label: string; at: string; by: string } | null;
  detail?: string;
}

export type UpdateDetail =
  | { kind: 'none' }
  | { kind: 'mailbox-policy'; standard: string; rows: MailboxPolicyRow[] }
  | ({ kind: 'voicemail-emails' } & { company: VoicemailMoveCompany; mailboxes: VoicemailMoveMailbox[] })
  | {
      kind: 'patch-rings';
      people: number;
      rings: number;
      ringsThere: number | null;
      missing: number | null;
      clashes: { number: string; extension: string | null; what: string }[];
      presenceRoom: { number: string; there: boolean | null } | null;
      available: { ok: boolean; reason?: string };
    };

export interface UpdateStarted {
  started: number;
  skipped: { customerId: string; name: string; why: string }[];
}

/** Whether a mailbox's voicemail is written out, from the company's transcript settings. */
export function transcribed(t: { on: boolean; every?: boolean; mailboxes: string[]; off?: string[] } | null | undefined, mailbox: string): boolean {
  if (!t?.on) return false;
  if (t.mailboxes.includes(mailbox)) return true;
  return Boolean(t.every) && !(t.off ?? []).includes(mailbox);
}

/** A field test named beside an update's button on Stitch Admin → Updates, with its newest result (3124). */
export interface FieldTestBrief {
  id: string;
  title: string;
  task: number | null;
  needs: string | null;
  ready: boolean;
  status: FieldTestStatus;
  testedAt: string | null;
  testedBy: string | null;
  build: string | null;
}
/** By update id, and 'bring-up-to-date' for Bring every customer up to date. */
export type FieldTestGuards = Partial<Record<UpdateId | 'bring-up-to-date', FieldTestBrief[]>>;

export const updatesApi = {
  list: () =>
    request<{ updates: UpdateSummary[]; mailboxPolicy: { standard: string; choices: { value: string; label: string; description?: string }[] } | null; fieldTests?: FieldTestGuards }>(
      'admin',
      'GET',
      '/api/admin/updates'
    ),
  one: (id: string) => request<{ update: UpdateSummary; rows: UpdateRow[]; fieldTests?: FieldTestBrief[] }>('admin', 'GET', `/api/admin/updates/${enc(id)}`),
  run: (id: string, action: UpdateAction, customers: string[] | 'needed' | 'all') => request<UpdateStarted>('admin', 'POST', `/api/admin/updates/${enc(id)}/run`, { action, customers }),
  leaveOut: (id: string, customer: string, on: boolean, note = '') =>
    request<{ updates: (UpdateInfo & { row: UpdateRow })[] }>('admin', 'PUT', `/api/admin/updates/${enc(id)}/customers/${enc(customer)}/left-out`, { on, note }),
  detail: (id: string, customer: string) => request<UpdateDetail>('admin', 'GET', `/api/admin/updates/${enc(id)}/customers/${enc(customer)}`),
  setStandardPolicy: (policy: string) => request<{ standard: string }>('admin', 'PUT', '/api/admin/updates/mailbox-policy/standard', { policy }),
  forCustomer: (customer: string) => request<{ updates: (UpdateInfo & { row: UpdateRow })[] }>('admin', 'GET', `/api/admin/customers/${enc(customer)}/updates`),
  setUp: (customer: string) => request<{ started: number }>('admin', 'POST', `/api/admin/customers/${enc(customer)}/updates/set-up`),
  /** Every customer (linked, not suspended): each update it needs, in order (1.12.34). */
  bringUpToDate: () => request<{ customers: number; updates: number; skipped: { customerId: string; name: string; why: string }[] }>('admin', 'POST', '/api/admin/updates/bring-up-to-date')
};

// --- smart messaging -------------------------------------------------------------------------------
// Company Settings → Messages, legal holds, what the smart service found in Messages (Spam, closed
// text conversations, archived channels), and its status on Stitch Admin → System. See
// docs/smart-messaging.md.

export interface SmartSettings {
  enabled: boolean;
  /** Days kept; null: forever. */
  chatDays: number | null;
  textDays: number | null;
  /** Nothing is deleted before this (a day after the rules last got stricter). */
  retentionFrom: string | null;
  /** Quiet channels archived after this many days; null: off. */
  archiveDays: number | null;
  /** Quiet text conversations closed after this many days; null: off. */
  closeDays: number | null;
  spamFilter: boolean;
  /** Catch me up: AI summaries by Anthropic's Claude API (on by default since 1.12.34). */
  aiCatchup: boolean;
  /** Days files in channels and direct messages are kept (2936): 30, 60 or 90. */
  fileDays: 30 | 60 | 90;
  updatedAt: string | null;
  updatedBy: string;
}
export type SmartSettingsChange = Partial<Pick<SmartSettings, 'enabled' | 'chatDays' | 'textDays' | 'archiveDays' | 'closeDays' | 'spamFilter' | 'aiCatchup' | 'fileDays'>>;
export interface SmartSettingsView {
  editable: boolean;
  settings: SmartSettings;
  /** Legal holds on now. */
  holds: number;
  service: { running: boolean; containers?: number; heartbeatAt: string | null };
  /** Catch-ups can be switched on (Stitch has a Claude API key). */
  ai?: { available: boolean };
  /** File retention (2936): when removals start (a week's notice before the first), and how many files are older than kept now. */
  files?: { startsAt: string | null; older: number };
}
/** What a legal hold is on (1.13.0, 2717 widened it: a number, a ring group or queue, one item). */
export type HoldScope = 'company' | 'conversation' | 'person' | 'number' | 'queue' | 'item';
export interface LegalHoldView {
  id: string;
  scope: HoldScope;
  conversationId: string | null;
  userId: string | null;
  number: string | null;
  extension: string | null;
  item: { kind: 'call' | 'recording' | 'voicemail'; id: string } | null;
  /** Only these company-local days (either end open). */
  fromDay: string | null;
  toDay: string | null;
  reason: string;
  placedByName: string;
  placedAt: string;
  liftedAt: string | null;
  liftedByName: string;
  target: { name: string; email?: string; kind?: 'channel' | 'dm' | 'text'; number?: string | null } | null;
}
export interface HoldTargets {
  people: { id: string; name: string; email: string }[];
  conversations: { id: string; kind: 'channel' | 'text'; name: string | null; number: string | null; stitchNumber: string | null }[];
  /** Its ring groups and call queues (2717). */
  teams: { extension: string; name: string; kind: 'ringGroup' | 'callQueue' }[];
}
type HoldDays = { fromDay?: string; toDay?: string };
export type NewHold = (
  | { scope: 'company'; reason: string }
  | { scope: 'person'; userId: string; reason: string }
  | { scope: 'conversation'; conversationId: string; reason: string }
  | { scope: 'number'; number: string; reason: string }
  | { scope: 'queue'; extension: string; reason: string }
  | { scope: 'item'; item: { kind: 'call' | 'recording' | 'voicemail'; id: string }; reason: string }
) &
  HoldDays;
export interface SmartMarks {
  /** Smart messaging is on for the company. */
  on: boolean;
  spam: { conversationId: string; flaggedAt: string; reasons: string[] }[];
  closed: { conversationId: string; closedAt: string; state: 'closed' | 'reopened' }[];
  /** Text conversations retention emptied (shown again when a new text comes). */
  emptied: string[];
  archived: { id: string; name: string; archivedAt: string; archivedBy: string | null; auto: boolean }[];
  /** Heads-up posted: archived on this date unless someone posts. */
  archiving: { id: string; dueAt: string }[];
  /** This person's own Done, Snooze and Later (never anyone else's). */
  mine?: TriageView[];
  /** Catch me up is on for the company. */
  catchUp?: boolean;
  /** File retention (2936): how long files are kept, and when removals start (null: not set yet). */
  files?: { keepDays: number; startsAt: string | null };
}
export interface TriageView {
  conversationId: string;
  /** Done: hidden until a message newer than this. */
  doneSeq: number | null;
  doneAt: string | null;
  snoozedUntil: string | null;
  laterAt: string | null;
}
export interface CatchUpView {
  /** Written by AI (Claude), for this person only; null when there was nothing new. */
  summary: string | null;
  messages: number;
  fromSeq: number;
  toSeq: number;
  model: string;
  at: string;
  cached: boolean;
}
export interface SmartServiceView {
  running: boolean;
  heartbeatAt: string | null;
  backlog: { lag: number | null; pending: number | null } | null;
  instances: { id: string; host: string; version: string; startedAt: string; heartbeatAt: string; ageSeconds: number; running: boolean; status: SmartInstanceStatus }[];
}
export interface SmartInstanceStatus {
  events?: { handled?: number; failed?: number; dropped?: number; claimed?: number; lastEventAt?: string | null; lag?: number | null; pending?: number | null; length?: number | null };
  timers?: { name: string; running: boolean; runs: number; lastStartedAt: string | null; lastFinishedAt: string | null; lastResult: Record<string, number | string | boolean | null> | null; lastError: string | null }[];
  trash?: number | null;
  storage?: boolean;
}

export const smartApi = {
  settings: (d: string) => request<SmartSettingsView>('tenant', 'GET', `/api/tenants/${enc(d)}/settings/messages`),
  saveSettings: (d: string, set: SmartSettingsChange) => request<SmartSettingsView>('tenant', 'PATCH', `/api/tenants/${enc(d)}/settings/messages`, set),
  holds: (d: string) => request<{ holds: LegalHoldView[]; canPlace: boolean; canLift: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/legal-holds`),
  holdTargets: (d: string) => request<HoldTargets>('tenant', 'GET', `/api/tenants/${enc(d)}/legal-holds/targets`),
  placeHold: (d: string, body: NewHold) => request<{ hold: LegalHoldView }>('tenant', 'POST', `/api/tenants/${enc(d)}/legal-holds`, body),
  liftHold: (d: string, id: string) => request<{ hold: LegalHoldView }>('tenant', 'POST', `/api/tenants/${enc(d)}/legal-holds/${enc(id)}/lift`, {}),
  marks: (d: string) => request<SmartMarks>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/smart`),
  notSpam: (d: string, id: string) => request<{ ok: true; cleared: boolean }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/smart/spam/${enc(id)}/not-spam`, {}),
  triage: (d: string, id: string, set: { done?: boolean; snoozeUntil?: string | null; later?: boolean }) =>
    request<{ triage: TriageView }>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/smart/triage/${enc(id)}`, set),
  catchUp: (d: string, id: string, after?: number, thread?: string) =>
    request<{ catchUp: CatchUpView }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/smart/catch-up/${enc(id)}`, thread ? { thread } : after !== undefined ? { after } : {}),
  service: () => request<SmartServiceView>('admin', 'GET', '/api/admin/smart'),
  /** eDiscovery: the export as a zip to save (company admins only). */
  exportMessages: async (d: string, body: { people: string[]; conversations: string[]; from: string; to: string }): Promise<{ blob: Blob; name: string }> => {
    const res = await fetch(`/api/tenants/${enc(d)}/messages/export`, { method: 'POST', headers: { ...headersFor(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) {
      let json: Record<string, unknown> = {};
      try {
        json = (await res.json()) as Record<string, unknown>;
      } catch {
        // Not JSON.
      }
      throw new ApiError(res.status, json);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'stitch-messages.zip';
    return { blob: await res.blob(), name };
  }
};

// --- Push notifications (the installable Stitch app) ---------------------------------------------
// Kept apart from `api` above so other work on the client merges cleanly.

/** A device that gets someone's notifications (never its push address). */
export interface PushDeviceView {
  id: string;
  /** "Chrome on Android", "Stitch app on iPhone". */
  label: string;
  createdAt: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  /** Signed up from this browser. */
  current: boolean;
}
export interface QuietHoursView {
  from: string;
  to: string;
  zone: string;
}
export interface PushSettingsView {
  /** Notifications at all, on every device. */
  enabled: boolean;
  /** Notifications say who and what; off, just "New message". */
  previews: boolean;
  quiet: QuietHoursView | null;
  devices: PushDeviceView[];
}
export interface AdminPushView {
  ready: boolean;
  keys: 'env' | 'generated' | null;
  sealed: boolean;
  keyFingerprint: string | null;
  subject: string;
  devices: number;
  people: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  problem?: string;
}

export const pushApi = {
  key: () => request<{ publicKey: string }>('tenant', 'GET', '/api/push/key'),
  subscribe: (sub: PushSubscriptionJSON) => request<{ device: PushDeviceView }>('tenant', 'POST', '/api/push/subscriptions', sub),
  unsubscribe: (by: { endpoint: string } | { id: string }) => request<{ ok: true }>('tenant', 'DELETE', '/api/push/subscriptions', by),
  test: () => request<{ devices: number; sent: number; failed: number; gone: number }>('tenant', 'POST', '/api/push/test'),
  settings: () => request<PushSettingsView>('tenant', 'GET', '/api/push/settings'),
  saveSettings: (set: { enabled?: boolean; previews?: boolean; quiet?: QuietHoursView | null }) => request<PushSettingsView>('tenant', 'PATCH', '/api/push/settings', set),
  admin: () => request<AdminPushView>('admin', 'GET', '/api/admin/push')
};

/** Where one of the carrier's settings comes from (1.12.12): Stitch Admin, EasyPanel's variable, Ahoi's own default, or nowhere. */
export type SettingSource = 'stitch' | 'easypanel' | 'default' | 'none';
export interface SettingChanged {
  at: string;
  byName: string;
}
/** Stitch Admin → System → Carrier: Ahoi's settings, never a secret's value. */
/** The carrier's API token: its age, its last Test, and when to rotate it (1.13.0, 3125). */
export interface CarrierKeyHealth {
  source: SettingSource;
  /** When it was saved in Stitch (unknown for one in EasyPanel). */
  setAt: string | null;
  ageDays: number | null;
  lastTest: { at: string; ok: boolean } | null;
  lastOkAt: string | null;
  rotateEveryDays: number;
  rotateDue: boolean;
}
export interface CarrierSettingsView {
  /** Which carrier account these are (1.12.25). */
  account?: { id: string; name: string; main: boolean; isDefault: boolean };
  keyHealth?: CarrierKeyHealth;
  apiKey: { set: boolean; source: SettingSource; changed: SettingChanged | null; unreadable: boolean };
  apiUrl: { value: string; source: SettingSource; changed: SettingChanged | null };
  callFlow: { value: string | null; source: SettingSource; changed: SettingChanged | null };
  trunk: { value: string | null; source: SettingSource; changed: SettingChanged | null };
  writes: { value: boolean; source: SettingSource; changed: SettingChanged | null };
  webhookSecret: { set: boolean; source: SettingSource; changed: SettingChanged | null; unreadable: boolean };
  webhookIps: { value: string[]; source: SettingSource; changed: SettingChanged | null };
  /** DATA_ENCRYPTION_KEY is set, so secrets can be saved. */
  canSeal: boolean;
  /** EasyPanel variables still set, and whether Stitch has its own (so the variable can go). */
  easypanel: { name: string; setting: string; inStitch: boolean }[];
  updatedAt: string | null;
  updatedByName: string | null;
  webhooks: { inboundUrl: string | null; receiptUrl: string | null; publicUrl: boolean; seen: string[] };
}
export interface CarrierChange {
  apiKey?: string | null;
  apiUrl?: string | null;
  callFlow?: string | null;
  trunk?: string | null;
  writes?: boolean | null;
  webhookIps?: string[] | null;
  confirmSpending?: true;
}

/** `?account=` for one of several carrier accounts (1.12.25); the first account when left out. */
const acct = (account?: string) => (account && account !== 'main' ? `?account=${enc(account)}` : '');

export const carrierApi = {
  get: (account?: string) => request<CarrierSettingsView>('admin', 'GET', `/api/admin/settings/carrier${acct(account)}`),
  save: (change: CarrierChange, account?: string) => request<CarrierSettingsView>('admin', 'PUT', `/api/admin/settings/carrier${acct(account)}`, change),
  newWebhookSecret: (account?: string) => request<CarrierSettingsView>('admin', 'POST', `/api/admin/settings/carrier/webhook-secret${acct(account)}`, {}),
  move: () => request<{ moved: string[]; kept: string[]; settings: CarrierSettingsView }>('admin', 'POST', '/api/admin/settings/carrier/move', {}),
  test: (body: { apiKey?: string; apiUrl?: string }, account?: string) =>
    request<{ ok: true; ms: number; callFlows: number; numbers: number } | { ok: false; ms: number; message: string }>('admin', 'POST', `/api/admin/settings/carrier/test${acct(account)}`, body),
  callFlows: (account?: string) => request<{ callFlows: { id: string; name: string; routes: string[] }[] }>('admin', 'GET', `/api/admin/settings/carrier/call-flows${acct(account)}`),
  trunks: () => request<{ trunks: { id: string; name: string; companies: number }[] }>('admin', 'GET', '/api/admin/settings/carrier/trunks'),
  // Several carrier accounts (1.12.25).
  accounts: () => request<{ accounts: CarrierAccountRow[] }>('admin', 'GET', '/api/admin/settings/carrier/accounts'),
  addAccount: (name: string) => request<{ accounts: CarrierAccountRow[]; added: string }>('admin', 'POST', '/api/admin/settings/carrier/accounts', { name }),
  editAccount: (id: string, change: { name?: string; default?: true }) => request<{ accounts: CarrierAccountRow[] }>('admin', 'PATCH', `/api/admin/settings/carrier/accounts/${enc(id)}`, change),
  removeAccount: (id: string) => request<{ accounts: CarrierAccountRow[] }>('admin', 'DELETE', `/api/admin/settings/carrier/accounts/${enc(id)}`)
};

/** One of Stitch's carrier accounts, on System → Carrier (1.12.25). */
export interface CarrierAccountRow {
  id: string;
  name: string;
  main: boolean;
  /** Where new numbers and port-ins go. */
  isDefault: boolean;
  token: boolean;
  writes: boolean;
  callFlow: string | null;
  webhooks: boolean;
  /** Its numbers couldn't be read lately: when, and why. */
  problem: { at: string; message: string } | null;
}

/** Stitch Admin → System → Webhooks (1.12.19): where webhook addresses point. */
export const webhookSettingsApi = {
  get: () => request<WebhookSettingsView>('admin', 'GET', '/api/admin/settings/webhooks'),
  save: (baseUrl: string | null) => request<WebhookSettingsView>('admin', 'PUT', '/api/admin/settings/webhooks', { baseUrl })
};

/** Stitch Admin → System → Virus check (1.12.16). */
export const virusCheckApi = {
  get: () => request<VirusCheckView>('admin', 'GET', '/api/admin/settings/virus-check'),
  save: (address: string | null) => request<VirusCheckView>('admin', 'PUT', '/api/admin/settings/virus-check', { address }),
  test: () => request<{ ok: boolean; message: string; engine?: string; signaturesAt?: string | null }>('admin', 'POST', '/api/admin/settings/virus-check/test', {})
};

// --- the receptionist seat and console (1.13.0) --------------------------------------------------

export interface ReceptionSettings {
  holdAmberSeconds: number;
  holdRedSeconds: number;
  askOn: boolean;
  askSeconds: number;
  quickNotes: string[];
  outsideNumbers: { name: string; number: string }[];
  /** The mailbox prefix (8 + 204 is 204's mailbox) for To voicemail; '' is off. */
  voicemailPrefix: string;
  showCoworkerDetail: boolean;
  operatorsRingSeconds: number;
  operatorsNoAnswer: string;
  /** Blind transfers that come back (2714): seconds the person's phone rings before the caller comes back; 0 is off. */
  recallSeconds: number;
}
export interface OperatorsView {
  extension: string | null;
  name: string;
  members: string[] | null;
  wanted: string[];
  handAdded: string[];
  drift: string | null;
  syncedAt: string | null;
}
/** A call queue and whether the receptionists back it up (2714). */
/** Saved copies (2718). */
export type SavedCopyKind = 'ringGroup' | 'callQueue' | 'autoAttendant';
export interface SavedCopyView {
  id: string;
  kind: SavedCopyKind;
  extension: string;
  name: string;
  reason: 'saved' | 'before-removal';
  savedAt: string;
  savedByName: string;
  location: 'archive' | 'database';
  bytes: number;
}

export interface QueueBackupView {
  extension: string;
  name: string;
  on: boolean;
  seconds: number | null;
  /** Where callers who wait too long go now when that isn't the receptionists ('' for nowhere). */
  elsewhere: string;
  /** The same, as people know it ("Dana Ruiz’s voicemail"). */
  elsewhereName: string;
}
/** An auto attendant and whether its 0 key rings the receptionists (2714). */
export interface MenuZeroView {
  extension: string;
  name: string;
  on: boolean;
  /** Where 0 goes now when that isn't the receptionists ('' when the menu has no 0). */
  elsewhere: string;
  elsewhereName: string;
  /** Why 0 can't be pointed at the receptionists: it starts dial-by-name, or all ten keys are used. */
  blocked: 'byName' | 'full' | null;
}
export interface ReceptionView {
  settings: ReceptionSettings;
  operators: OperatorsView;
  receptionists: { userId: string; name: string; extension: string | null; inOperators: boolean }[];
  /** People who sign in with their own extension and aren't receptionists: who Add a receptionist offers. */
  candidates?: { userId: string; name: string; extension: string }[];
}
export type OfferState = 'sent' | 'seen' | 'replied' | 'accepted' | 'declined' | 'voicemail' | 'expired' | 'withdrawn' | 'caller_left' | 'failed';
export interface OfferView {
  id: string;
  state: OfferState;
  from: { id: string; name: string; extension: string };
  to: { id: string; name: string; extension: string };
  callId: string;
  caller: { name: string; number: string };
  note: string;
  conversationId: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}
export type TransferKind = 'blind' | 'warm' | 'voicemail' | 'message' | 'park' | 'queue' | 'group' | 'outside' | 'pickup';
export interface TransferView {
  id: string;
  callId: string;
  callerName: string;
  callerNumber: string;
  kind: TransferKind;
  target: string;
  targetName: string;
  outcome: string;
  at: string;
}
export interface ConsolePerson {
  extension: string;
  name: string;
  userId: string | null;
  /** A shared phone: a place (lobby, kitchen), not a person (1.13.0, 3067). */
  place?: boolean;
  receptionist: boolean;
  state: 'idle' | 'ringing' | 'talking' | 'offline' | 'unknown';
  dnd: boolean;
  stitchOpen: boolean;
  since?: string;
  with?: { name: string; number: string };
}
export interface ConsoleView {
  me: { extension: string; onBreak: { since: string; queues: string[] } | null; transferredToday: number };
  settings: Pick<ReceptionSettings, 'holdAmberSeconds' | 'holdRedSeconds' | 'askOn' | 'askSeconds' | 'quickNotes' | 'voicemailPrefix' | 'outsideNumbers' | 'recallSeconds'>;
  live: boolean;
  people: ConsolePerson[];
  groups: {
    extension: string;
    name: string;
    kind: 'ringGroup' | 'callQueue';
    operators?: true;
    waiting?: number;
    agents?: number;
    /** Callers waiting in a queue, longest first (2714): `canAnswer` when they can be taken out of turn. */
    callers?: { id: string; waitingSeconds: number; from?: string; canAnswer: boolean }[];
  }[];
  parks: { extension: string; name: string; calls: number }[];
  mailboxes: { extension: string; name: string }[];
  transfers: TransferView[];
  asks: OfferView[];
}
export type OfferAction = 'take' | 'here' | 'voicemail' | 'not-now' | 'later' | 'seen' | 'withdraw';

export const receptionApi = {
  get: (d: string) => request<ReceptionView>('tenant', 'GET', `/api/tenants/${enc(d)}/reception`),
  save: (d: string, set: Partial<ReceptionSettings>) => request<{ settings: ReceptionSettings }>('tenant', 'PUT', `/api/tenants/${enc(d)}/reception`, set),
  syncOperators: (d: string, handAdded?: string[]) =>
    request<{ changeSet: ChangeSetView | null; operators: OperatorsView }>('tenant', 'POST', `/api/tenants/${enc(d)}/reception/operators`, handAdded ? { handAdded } : {}),
  seat: (d: string, ext: string) => request<{ inStitch: boolean; seat: 'standard' | 'receptionist'; inOperators: boolean }>('tenant', 'GET', `/api/tenants/${enc(d)}/people/${enc(ext)}/seat`),
  /** Queue backup (2714): each queue, and where its backup rings (Operators, or the only receptionist). */
  queueBackups: (d: string) => request<{ target: { extension: string; name: string } | null; queues: QueueBackupView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/reception/queues`),
  setQueueBackup: (d: string, queue: string, on: boolean, seconds: number) =>
    request<{ changeSet: ChangeSetView | null }>('tenant', 'PUT', `/api/tenants/${enc(d)}/reception/queues/${enc(queue)}`, { on, seconds }),
  /** Press 0 (2714): each auto attendant, and whether 0 rings the receptionists. */
  menuZeros: (d: string) => request<{ target: { extension: string; name: string } | null; menus: MenuZeroView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/reception/menus`),
  setMenuZero: (d: string, menu: string, on: boolean) => request<{ changeSet: ChangeSetView | null }>('tenant', 'PUT', `/api/tenants/${enc(d)}/reception/menus/${enc(menu)}`, { on }),
  setSeat: (d: string, ext: string, seat: 'standard' | 'receptionist', inOperators?: boolean) =>
    request<{ changeSet: ChangeSetView | null; seat: 'standard' | 'receptionist'; inOperators: boolean }>('tenant', 'PUT', `/api/tenants/${enc(d)}/people/${enc(ext)}/seat`, {
      seat,
      ...(inOperators !== undefined ? { inOperators } : {})
    }),
  console: (d: string) => request<ConsoleView>('tenant', 'GET', `/api/tenants/${enc(d)}/console`),
  transfer: (d: string, id: string, kind: Exclude<TransferKind, 'warm' | 'message' | 'pickup'>, to: string, name?: string) =>
    request<{ transfer: TransferView }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/calls/${enc(id)}/transfer`, { kind, to, ...(name ? { name } : {}) }),
  warm: (d: string, id: string, to: string, name?: string, recall?: boolean) =>
    request<{ transfer: TransferView }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/calls/${enc(id)}/warm`, { to, ...(name ? { name } : {}), ...(recall ? { recall: true } : {}) }),
  /** How a blind transfer that comes back ended (2714). */
  transferOutcome: (d: string, id: string, outcome: 'answered' | 'came back') => request<{ ok: true }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/transfers/${enc(id)}/outcome`, { outcome }),
  ask: (d: string, id: string, userId: string, note: string) => request<{ offer: OfferView }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/calls/${enc(id)}/ask`, { userId, note }),
  setBreak: (d: string, on: boolean) => request<{ onBreak: ConsoleView['me']['onBreak'] }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/break`, { on }),
  /** A queue caller out of turn (2714): what the app dials to take them. */
  answerQueueCaller: (d: string, id: string) => request<{ dial: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/console/queue-calls/${enc(id)}/answer`),
  offer: (d: string, id: string) => request<{ offer: OfferView }>('tenant', 'GET', `/api/tenants/${enc(d)}/offers/${enc(id)}`),
  answer: (d: string, id: string, action: OfferAction) => request<{ offer: OfferView }>('tenant', 'POST', `/api/tenants/${enc(d)}/offers/${enc(id)}/${action}`, {})
};


// --- Texting registration and moves to a team (1.13.0, Teampace 3073) -----------------------------------

export type RegistrationState = 'draft' | 'brand_pending' | 'in_review' | 'approved' | 'rejected' | 'retired' | 'failed';
export type NumberTextState = 'approved' | 'allowed' | 'p2p' | 'waiting' | 'not_registered' | 'rejected' | 'personal';

export interface BrandFacts {
  legalName: string;
  dba: string;
  companyType: 'PRIVATE_PROFIT' | 'PUBLIC_PROFIT' | 'NON_PROFIT' | 'GOVERNMENT' | 'SOLE_PROPRIETOR' | '';
  vertical: string;
  website: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  stockSymbol: string;
  stockExchange: string;
}

export interface CampaignFields {
  useCase: 'CUSTOMER_CARE' | 'LOW_VOLUME' | 'MIXED' | 'ACCOUNT_NOTIFICATION' | '';
  subUseCases: string[];
  description: string;
  callToAction: string;
  samples: string[];
  startReply: string;
  stopReply: string;
  helpReply: string;
  embeddedLink: boolean;
  embeddedPhone: boolean;
  numberPool: boolean;
  ageGated: boolean;
  loans: boolean;
}

export interface WebsiteCheckView {
  id: string;
  label: string;
  ok: boolean;
  detail: string;
  paste: string | null;
}

export interface RegistrationView {
  registration: {
    customerId: string;
    brand: string;
    facts: BrandFacts;
    hasEin: boolean;
    einLast4: string | null;
    website: { at: string; url: string; reached: boolean; privacyUrl: string | null; termsUrl: string | null; checks: WebsiteCheckView[] } | null;
    campaign: CampaignFields;
    notes: Record<string, string>;
    covers: 'team' | 'all';
    state: RegistrationState;
    brandId: string | null;
    campaignId: string | null;
    crid: string | null;
    carrierStatus: string | null;
    rejectReason: string | null;
    dailyLimit: number | null;
    fix: { at: string; reason: string; explanation: string; campaign: Partial<CampaignFields>; siteChanges: string[]; by: 'leland' | 'rules' } | null;
    submissions: number;
    imported: boolean;
    held: boolean;
    heldReason: string | null;
    error: string | null;
    submittedAt: string | null;
    decidedAt: string | null;
    checkedAt: string | null;
  };
  numbers: TextingNumberView[];
  preflight: { id: string; label: string; ok: boolean; level: 'fail' | 'warn'; fix: string }[];
  ready: boolean;
  editable: boolean;
  canSubmit: boolean;
  canResubmit: boolean;
  carrier: { name: string; spending: boolean } | null;
  ai: boolean;
  websiteCheck: boolean;
  fees: { brand: number; campaignReview: number; monthlyStandard: number; monthlyLowVolume: number; note: string };
  events: { id: string; kind: string; detail: Record<string, unknown>; byName: string; at: string }[];
}

export type FieldTestStatus = 'passed' | 'failed' | 'blocked' | 'skipped' | 'not_run';

/** A screenshot kept with a field test result (Joey, Nov 3 2026). */
export interface FieldTestShotView {
  id: string;
  name: string;
  size: number;
  contentType: string;
  /** The picture (staff only). */
  url: string;
  uploadedAt: string;
}

export interface FieldTestResultView {
  id: string;
  status: FieldTestStatus;
  notes: string;
  /** One per expected result, in the test's order. */
  expected: boolean[];
  answers: Record<string, string>;
  build: string;
  testedBy: string;
  testedAt: string;
  shots?: FieldTestShotView[];
}

export interface FieldTestInfo {
  id: string;
  title: string;
  area: string;
  /** The first Stitch version it runs on (null: now). */
  needs: string | null;
  decides?: string;
  task: number | null;
  minutes: number;
  userStory: string;
  why: string;
  before: string[];
  steps: string[];
  expected: string[];
  answers?: { key: string; label: string; hint?: string; options?: string[] }[];
  careful?: string;
  ifFails?: string;
  /** It runs from Stitch Admin (3131): `gateway` opens the gateway's field test tools beside its steps. */
  run?: 'gateway';
  /** The running Stitch is new enough for it. */
  ready: boolean;
  result: FieldTestResultView | null;
  /** How many results have been recorded for it. */
  results: number;
  /** Screenshots you added for a result you haven't saved yet. */
  waitingShots?: FieldTestShotView[];
}

export interface FieldTestsView {
  version: number;
  running: { version: string; build: string };
  areas: string[];
  tests: FieldTestInfo[];
}

export interface FieldTestSave {
  status: FieldTestStatus;
  notes: string;
  expected: boolean[];
  answers: Record<string, string>;
  /** A gateway test run here (3131): the gateway's log from this line on is kept with the result. */
  gatewayLogSince?: number;
  /** Screenshots added while it was written (their ids): kept with the result. */
  shots?: string[];
}

/** One call a watched extension reported (d1, d2…), the newest state of it (3131). */
export interface FieldToolDialog {
  n: number;
  person: string;
  ext: string;
  id: string;
  callId: string;
  localTag: string;
  remoteTag: string;
  direction: string;
  state: string;
  local: string;
  remote: string;
  remoteName: string;
  updated: string;
}

export interface FieldToolEvent {
  seq: number;
  at: string;
  kind: string;
  data: Record<string, unknown>;
}

/** Stitch Admin → Field tests: the gateway's field test tools (3131). */
export interface FieldToolsView {
  gateway: { set: boolean; supported: boolean; version: string; error?: string };
  /** Companies inside the write fence: the ones a test company can be. */
  companies: { id: string; name: string; tenant: string }[];
  company: { id: string; name: string; tenant: string } | null;
  /** The company's people signed in to Stitch, registered through the gateway. */
  lines: { person: string; extension: string; name: string; state: string }[];
  tools: {
    on: boolean;
    until: string | null;
    watches: { person: string; ext: string }[];
    dialogs: FieldToolDialog[];
    log: FieldToolEvent[];
    next: number;
    /** The test company they're on for. */
    company: string | null;
  } | null;
}

export interface TextingNumberView {
  number: string;
  label: string;
  where: string;
  team: string | null;
  teamName: string | null;
  state: NumberTextState;
  canSend: boolean;
  reason: string | null;
  allowedReason: string | null;
  assignError: string | null;
  /** Person-to-person texting (P2P) is on for it at the carrier. Missing from an older server. */
  p2p?: boolean;
}

export interface MoveOptions {
  contact: string;
  optedOut: boolean;
  personCanText: boolean;
  followDays: number;
  teams: { extension: string; name: string; number: string | null; label: string | null; canText: boolean; reason: string | null; personText: string; teamText: string; closedText: string }[];
}

export interface RegistrationListing {
  customerId: string;
  name: string;
  domain: string;
  allowedAnyway: boolean;
  state: RegistrationState | null;
  brand: string | null;
  campaignId: string | null;
  crid: string | null;
  carrierStatus: string | null;
  rejectReason: string | null;
  submissions: number;
  submittedAt: string | null;
  decidedAt: string | null;
  checkedAt: string | null;
  held: boolean;
  heldReason: string | null;
  imported: boolean;
  error: string | null;
}

export type RegistrationSave = { facts?: Partial<BrandFacts>; ein?: string | null; campaign?: Partial<CampaignFields>; covers?: 'team' | 'all' };

export const textingApi = {
  registration: (d: string) => request<RegistrationView>('tenant', 'GET', `/api/tenants/${enc(d)}/texting/registration`),
  save: (d: string, body: RegistrationSave) => request<RegistrationView>('tenant', 'PUT', `/api/tenants/${enc(d)}/texting/registration`, body),
  website: (d: string) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/website`, {}),
  draft: (d: string) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/draft`, {}),
  submit: (d: string) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/submit`, {}),
  check: (d: string) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/check`, {}),
  fix: (d: string) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/fix`, {}),
  resubmit: (d: string, useFix: boolean) => request<RegistrationView>('tenant', 'POST', `/api/tenants/${enc(d)}/texting/registration/resubmit`, { useFix }),
  verticals: (d: string) => request<{ verticals: { id: string; name: string }[]; companyTypes: { id: string; name: string }[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/texting/verticals`),
  numbers: (d: string) => request<{ numbers: TextingNumberView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/texting/numbers`),
  moveOptions: (d: string, id: string) => request<MoveOptions>('tenant', 'GET', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/move`),
  move: (d: string, id: string, body: { team: string; note?: string; text?: string | null }) =>
    request<{ moved: { id: string; to: string; team: string; number: string; teamText: 'sent' | 'quiet' | 'no' }; conversation: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/messages/conversations/${enc(id)}/move`, body),
  setMoveTexts: (d: string, t: { person: string | null; team: string | null; closed: string | null }) =>
    request<TextingSettingsView>('tenant', 'PUT', `/api/tenants/${enc(d)}/messages/texting-settings/move-texts`, t),
  // Stitch staff.
  all: () => request<{ registrations: RegistrationListing[] }>('admin', 'GET', '/api/admin/texting/registrations'),
  staffView: (customerId: string) => request<RegistrationView>('admin', 'GET', `/api/admin/texting/registrations/${enc(customerId)}`),
  staffAction: (customerId: string, action: 'check' | 'submit' | 'hold' | 'release' | 'retire' | 'import', body: { reason?: string; brandId?: string; campaignId?: string } = {}) =>
    request<RegistrationView>('admin', 'POST', `/api/admin/texting/registrations/${enc(customerId)}/${action}`, body),
  allowNumber: (customerId: string, number: string, reason: string | null) =>
    request<{ numbers: TextingNumberView[] }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/texting/numbers/${enc(number)}`, { reason }),
  /** Staff: P2P on or off at the carrier (Stitch calls it), or marked only (`markOnly`: done in the carrier's portal). */
  setP2p: (customerId: string, number: string, on: boolean, markOnly = false) =>
    request<{ numbers: TextingNumberView[] }>('admin', 'PUT', `/api/admin/customers/${enc(customerId)}/texting/numbers/${enc(number)}/p2p`, { on, ...(markOnly ? { markOnly } : {}) }),
  /** Admins: turn on person-to-person texting (P2P) for a person's own number, agreeing to its rules. */
  turnOnP2p: (d: string, number: string) => request<RegistrationView>('tenant', 'PUT', `/api/tenants/${enc(d)}/texting/numbers/${enc(number)}/p2p`, { promise: true }),
  /** Admins: turn P2P off on a person's own number; texting on it stops at the carrier at once. */
  turnOffP2p: (d: string, number: string) => request<RegistrationView>('tenant', 'DELETE', `/api/tenants/${enc(d)}/texting/numbers/${enc(number)}/p2p`)
};


// --- Shared phones (1.13.0, Teampace 3067) -----------------------------------------------------------

export interface SharedPhoneView {
  extension: string;
  name: string;
  kind: SharedPhoneKind;
}

export const placesApi = {
  list: (d: string) => request<{ sharedPhones: SharedPhoneView[] }>('tenant', 'GET', `/api/tenants/${enc(d)}/shared-phones`),
  set: (d: string, extension: string, place: { name: string; kind: SharedPhoneKind }) =>
    request<{ sharedPhone: SharedPhoneView }>('tenant', 'PUT', `/api/tenants/${enc(d)}/shared-phones/${enc(extension)}`, place),
  remove: (d: string, extension: string) => request<{ removed: boolean }>('tenant', 'DELETE', `/api/tenants/${enc(d)}/shared-phones/${enc(extension)}`)
};

// --- Billing in the portal (1.13.0, 3099): admins, staff, and roles with Billing -------------------

export type InvoiceState = 'paid' | 'open' | 'overdue' | 'void';

export interface BillingInvoiceRow {
  id: string;
  number: string | null;
  kind: 'first' | 'monthly' | 'final';
  period: string;
  issuedOn: string | null;
  dueOn: string | null;
  totalCents: number;
  dueCents: number;
  paidCents: number;
  state: InvoiceState;
}

export interface BillingOverview {
  account: {
    number: string | null;
    term: 'monthly' | 'annual';
    net30: boolean;
    billTo: Record<string, string>;
    status: 'trial' | 'active' | 'suspended' | 'cancelled';
    cancelAt: string | null;
  } | null;
  balanceCents?: number;
  invoices?: BillingInvoiceRow[];
  next?: { period: string; totalCents: number; sections: { id: string; title: string; sub: string; totalCents: number }[] } | null;
  plans?: { item: string; name: string; kind: string; count: number; who: string[] }[];
  contacts?: { email: string; name: string }[];
  recipients?: { email: string; name: string }[];
}

export interface BillingInvoiceLine {
  kind: string;
  description: string;
  note: string;
  qty: number | null;
  rateCents: number | null;
  rateLabel?: string;
  amountCents: number;
}

export interface BillingInvoiceView extends BillingInvoiceRow {
  subtotalCents: number;
  discountCents: number;
  feeCents: number;
  taxCents: number;
  creditCents: number;
  carryCents: number;
  voided: { reason: string; at: string } | null;
  account: { number: string | null; billTo: Record<string, string>; net30: boolean };
  service: { from: string; to: string } | null;
  usage: { from: string; to: string } | null;
  sections: { id: string; title: string; sub: string; totalCents: number; lines: BillingInvoiceLine[] }[];
  creditNotes: { number: string; totalCents: number; reason: string; at: string }[];
}

/** Billing → Payment methods (3092): cards and bank accounts in Teampace's card plugin, and autopay. */
export interface PaymentMethodView {
  id: number;
  kind: 'card' | 'ach';
  label: string;
  brand: string;
  last4: string;
  expires: string | null;
  expiring: boolean;
  expired: boolean;
  default: boolean;
}
export interface PaymentMethodsView {
  available: boolean;
  reason: string | null;
  autopay: boolean;
  net30: boolean;
  methods: PaymentMethodView[];
}
export const paymentMethodsApi = {
  list: (d: string) => request<PaymentMethodsView>('tenant', 'GET', `/api/tenants/${enc(d)}/billing/payment-methods`),
  page: (d: string) => request<{ url: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/billing/payment-methods/page`, {}),
  makeDefault: (d: string, id: number) => request<PaymentMethodsView>('tenant', 'POST', `/api/tenants/${enc(d)}/billing/payment-methods/${id}/default`, {}),
  remove: (d: string, id: number) => request<PaymentMethodsView>('tenant', 'DELETE', `/api/tenants/${enc(d)}/billing/payment-methods/${id}`),
  autopay: (d: string, on: boolean) => request<PaymentMethodsView>('tenant', 'PUT', `/api/tenants/${enc(d)}/billing/autopay`, { on })
};

/** Paying an invoice (3091): through Ceexo's payment processor (Teampace's card plugin), once Stitch takes payments. */
export interface ChargeView {
  id: string;
  at: string;
  amountCents: number;
  card: string;
  state: 'paid' | 'failed' | 'processing' | 'not_charged' | 'refunded';
  refundedCents: number;
  reason: string | null;
  how: string;
}
export interface InvoicePaymentsView {
  on: boolean;
  canPay: boolean;
  reason: string | null;
  openCents: number;
  attempts: ChargeView[];
}
export interface PaymentBannerView {
  on: boolean;
  blocked: boolean;
  suspended: boolean;
  unpaid: { invoiceId: string; number: string; openCents: number; why: 'failed' | 'overdue'; reason: string | null; nextTry: string | null; blocked: boolean }[];
}
export const invoicePayApi = {
  status: (d: string) => request<PaymentBannerView>('tenant', 'GET', `/api/tenants/${enc(d)}/billing/payments/status`),
  view: (d: string, id: string) => request<InvoicePaymentsView>('tenant', 'GET', `/api/tenants/${enc(d)}/billing/invoices/${enc(id)}/payments`),
  pay: (d: string, id: string, methodId: number | null) => request<{ charge: ChargeView; payments: InvoicePaymentsView }>('tenant', 'POST', `/api/tenants/${enc(d)}/billing/invoices/${enc(id)}/pay`, { methodId }),
  page: (d: string, id: string) => request<{ url: string }>('tenant', 'POST', `/api/tenants/${enc(d)}/billing/invoices/${enc(id)}/pay-page`, {})
};

export const billingApi = {
  overview: (d: string) => request<BillingOverview>('tenant', 'GET', `/api/tenants/${enc(d)}/billing`),
  invoice: (d: string, id: string) => request<{ invoice: BillingInvoiceView }>('tenant', 'GET', `/api/tenants/${enc(d)}/billing/invoices/${enc(id)}`),
  /** The PDF or usage file, as a link (downloads with the session cookie). */
  fileUrl: (d: string, id: string, kind: 'pdf' | 'csv') => `/api/tenants/${enc(d)}/billing/invoices/${enc(id)}/${kind}`,
  setContacts: (d: string, contacts: { email: string; name: string }[]) =>
    request<{ contacts: { email: string; name: string }[]; recipients: { email: string; name: string }[] }>('tenant', 'PUT', `/api/tenants/${enc(d)}/billing/contacts`, { contacts }),
  setAddress: (d: string, body: { attn: string; line1: string; line2: string; city: string; state: string; zip: string; email: string }) =>
    request<{ billTo: Record<string, string> }>('tenant', 'PUT', `/api/tenants/${enc(d)}/billing/address`, body)
};

// --- Stitch Admin → Billing (1.13.0, 3099): the month's run, every invoice, its files and emails ------

export interface AdminInvoice {
  id: string;
  customerId: string;
  company?: string;
  kind: 'first' | 'monthly' | 'final';
  period: string;
  status: 'draft' | 'held' | 'issued';
  number: string | null;
  issuedOn: string | null;
  dueOn: string | null;
  totalCents: number;
  dueCents: number;
  paidCents: number;
  creditCents: number;
  heldByName: string;
  voided: { reason: string; byName: string; at: string } | null;
  emails?: { sent: number; failed: number; skipped: number; bounced?: number };
  lastTotalCents?: number | null;
  usageReady?: boolean | null;
  snapshot?: { sections?: { id: string; title: string; sub: string; totalCents: number }[] };
}

export interface AdminInvoiceEmail {
  id: string;
  to: string;
  status: 'sent' | 'failed' | 'skipped';
  detail: string;
  byName: string;
  at: string;
  /** After it was sent: it bounced, or was marked as spam (Resend's webhook). */
  problem?: 'bounced' | 'complained' | null;
  problemDetail?: string;
  problemAt?: string | null;
}

/** An invoice email that bounced or was marked as spam, with its invoice and company. */
export interface AdminEmailProblem extends AdminInvoiceEmail {
  invoiceId: string;
  problem: 'bounced' | 'complained';
  customerId: string;
  company: string;
  number: string | null;
}

export interface BillingSettingsView {
  /** "Customers see Billing" (1.13.0): the company's own people see Billing in their portal; off until billing goes live. */
  customersSeeBilling: boolean;
  sendInvoiceEmails: boolean;
  issuer: { name: string; address: string; email: string; phone: string; website: string };
}

export const billingAdminApi = {
  invoices: (period?: string, drafts = false) =>
    request<{ invoices: AdminInvoice[] }>('admin', 'GET', `/api/admin/billing/invoices?${new URLSearchParams({ ...(period ? { period } : {}), ...(drafts ? { drafts: '1' } : {}) }).toString()}`),
  invoice: (id: string) => request<{ invoice: AdminInvoice }>('admin', 'GET', `/api/admin/billing/invoices/${enc(id)}`),
  emails: (id: string) =>
    request<{ emails: AdminInvoiceEmail[]; documents: { kind: 'pdf' | 'csv'; fileName: string; sha256: string; bytes: number; storage: 'b2' | 'db'; createdAt: string }[] }>(
      'admin',
      'GET',
      `/api/admin/billing/invoices/${enc(id)}/emails`
    ),
  fileUrl: (id: string, kind: 'pdf' | 'csv') => `/api/admin/billing/invoices/${enc(id)}/${kind}`,
  send: (id: string) => request<{ emails: AdminInvoiceEmail[] }>('admin', 'POST', `/api/admin/billing/invoices/${enc(id)}/email`, {}),
  hold: (id: string, held: boolean) => request<{ invoice: AdminInvoice }>('admin', 'POST', `/api/admin/billing/invoices/${enc(id)}/${held ? 'hold' : 'release'}`, {}),
  void: (id: string, reason: string) => request<{ invoice: AdminInvoice }>('admin', 'POST', `/api/admin/billing/invoices/${enc(id)}/void`, { reason }),
  creditNote: (id: string, chargesCents: number, reason: string) =>
    request<{ creditNote: { number: string; totalCents: number } }>('admin', 'POST', `/api/admin/billing/invoices/${enc(id)}/credit-notes`, { chargesCents, reason }),
  /** month: "2026-12". */
  run: (month: string) => request<{ invoices: AdminInvoice[]; runs: { id: string; action: string; status: string; counts: Record<string, unknown>; error: string | null; byName: string; startedAt: string }[] }>('admin', 'GET', `/api/admin/billing/runs/${enc(month)}`),
  preview: (month: string) => request<{ invoices: AdminInvoice[] }>('admin', 'POST', `/api/admin/billing/runs/${enc(month)}/preview`, {}),
  issue: (month: string) => request<{ issued: number; held: number; already: number; waiting: number }>('admin', 'POST', `/api/admin/billing/runs/${enc(month)}/issue`, {}),
  settings: () => request<{ settings: BillingSettingsView; bounces?: { on: boolean; url: string } }>('admin', 'GET', '/api/admin/billing/settings'),
  emailProblems: () => request<{ problems: AdminEmailProblem[] }>('admin', 'GET', '/api/admin/billing/email-problems'),
  saveSettings: (body: { customersSeeBilling?: boolean; sendInvoiceEmails?: boolean; taxesOn?: boolean; taxResearchOn?: boolean; issuerAddress?: string; issuerEmail?: string; issuerPhone?: string }) =>
    request<{ settings: BillingSettingsView }>('admin', 'PUT', '/api/admin/billing/settings', body)
};

// --- Stitch Admin → Billing → Taxes (1.13.0, 3098) ----------------------------------------------------

export type TaxKind = 'usf' | 'e911' | 'telecom' | 'sales' | 'other';

export interface TaxRateView {
  id: string;
  jurisdiction: 'federal' | 'state' | 'county' | 'city' | 'district';
  state: string | null;
  countyFips: string | null;
  placeFips: string | null;
  district: string | null;
  name: string;
  kind: TaxKind;
  method: 'percent' | 'per_line' | 'per_number';
  rate: number;
  share: number;
  basis: 'voice' | 'nonvoice' | 'both';
  effectiveFrom: string;
  effectiveTo: string | null;
  sourceUrl: string;
  notes: string;
  status: 'draft' | 'approved' | 'retired';
  createdBy: string;
  approvedBy: string | null;
}

export interface TaxCompanyView {
  customerId: string;
  company: string;
  place: { address: Record<string, string>; state: string | null; county: string | null; countyFips: string | null; city: string | null; placeFips: string | null; matched: boolean; said: string } | null;
  exemptions: { id: string; kinds: TaxKind[]; expiresOn: string | null; fileName: string }[];
  ready: boolean;
  reason: string;
}

export const taxAdminApi = {
  overview: () =>
    request<{
      on: boolean;
      research: { on: boolean; configured: boolean; recent: { key: string; place: string; quarter: string; status: 'done' | 'failed'; drafts: number; said: string; at: string }[] };
      period: string;
      rates: TaxRateView[];
      companies: TaxCompanyView[];
    }>('admin', 'GET', '/api/admin/billing/taxes'),
  runResearch: () => request<{ researched: number; drafts: number; skipped?: string }>('admin', 'POST', '/api/admin/billing/taxes/research/run', {}),
  addRate: (body: Omit<TaxRateView, 'id' | 'status' | 'createdBy' | 'approvedBy' | 'effectiveTo'>) => request<{ rate: TaxRateView }>('admin', 'POST', '/api/admin/billing/taxes/rates', body),
  approve: (id: string) => request<{ rate: TaxRateView }>('admin', 'POST', `/api/admin/billing/taxes/rates/${enc(id)}/approve`, {}),
  retire: (id: string, lastDay: string) => request<{ rate: TaxRateView | null }>('admin', 'POST', `/api/admin/billing/taxes/rates/${enc(id)}/retire`, { lastDay }),
  serviceAddress: (customerId: string, a: { line1: string; city: string; state: string; zip: string }) =>
    request<{ ready: boolean; reason: string }>('admin', 'PUT', `/api/admin/billing/accounts/${enc(customerId)}/service-address`, a),
  addExemption: (customerId: string, body: { kinds: TaxKind[]; expiresOn: string | null; fileName: string; contentType: string; content: string }) =>
    request<{ id: string }>('admin', 'POST', `/api/admin/billing/accounts/${enc(customerId)}/tax-exemptions`, body),
  revokeExemption: (id: string) => request<{ revoked: boolean }>('admin', 'POST', `/api/admin/billing/tax-exemptions/${enc(id)}/revoke`, {}),
  exemptionUrl: (id: string) => `/api/admin/billing/tax-exemptions/${enc(id)}/file`,
  /** month: "2026-12". */
  reportCsvUrl: (month: string) => `/api/admin/billing/taxes/report/${enc(month)}?format=csv`,
  reportPdfUrl: (month: string) => `/api/admin/billing/taxes/report/${enc(month)}?format=pdf`
};
